import { type DragEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FactoryRoundedIcon from '@mui/icons-material/FactoryRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ViewTimelineRoundedIcon from '@mui/icons-material/ViewTimelineRounded';
import { useSnackbar } from 'notistack';
import {
  createIndustrialCostProductionPlanItem,
  deleteIndustrialCostProductionPlanItem,
  getIndustrialCostProductionPlanningContext,
  getIndustrialCostProductionPlanningLineQueue,
  getIndustrialCostProductionPlanningWeekContext,
  moveIndustrialCostProductionPlanGroupToLockedStart,
  resolveIndustrialCostProductionPlanGroupLockedStartConflict,
  unplanIndustrialCostProductionPlanGroup,
  updateIndustrialCostProductionPlanGroupScheduleAdjustment,
  updateIndustrialCostProductionPlanItem,
  upsertIndustrialCostProductionPlanLineDaySettings,
} from '../services/industrialCostService';
import type {
  ImportedManufacturingOrder,
  ProductionLineConfig,
  ProductionPlanItem,
  ProductionPlanningContext,
} from '../types/industrialCost';

import { formatEsNumber } from '../utils/numberFormat';
type PendingOrderGroup = {
  key: string;
  referenceLabel: string;
  isReferenceGroup: boolean;
  orders: ImportedManufacturingOrder[];
  totalQuantity: number;
  quantityUnit: string | null;
  customers: string[];
  customerOrders: string[];
};

type LineProcessGroup = {
  key: string;
  label: string;
  lines: ProductionLineConfig[];
  firstDisplayOrder: number;
};

type PlannedOrderGroup = {
  key: string;
  referenceLabel: string;
  isReferenceGroup: boolean;
  items: ProductionPlanItem[];
  totalQuantity: number;
  quantityUnit: string | null;
  totalDurationMinutes: number;
  startTime: string | null;
  endTime: string | null;
  status: ProductionPlanItem['status'];
  customers: string[];
  customerOrders: string[];
};

type WeeklyContinuousSegment = {
  lineId: string;
  dateIso: string;
  originalDateIso: string;
  groupKey: string;
  referenceLabel: string;
  segmentMinutes: number;
  totalGroupMinutes: number;
  itemsCount: number;
  isContinuationFromPreviousDay: boolean;
  continuesNextDay: boolean;
  isOverflowBeyondWeek: boolean;
};

type WeeklyPendingDropRequest = {
  groupKey: string;
  line: ProductionLineConfig;
  dateIso: string;
  lineHasQueue: boolean;
  insertBeforeSequenceOrder: number | null;
};

type WeeklyPlannedGroupDropRequest = {
  groupKey: string | null;
  line: ProductionLineConfig;
  insertBeforeSequenceOrder: number | null;
};

type WeeklyPlannedGroupDropPreview = {
  lineId: string;
  groupKey: string;
  insertBeforeSequenceOrder: number | null;
};

type PendingDurationAssignmentRequest = {
  group: PendingOrderGroup;
  line: ProductionLineConfig;
  targetDate: string;
  startMode: 'append_to_queue' | 'locked_start';
  lockedStartTime?: string;
  insertBeforeSequenceOrder?: number | null;
};

type PlannedGroupScheduleAdjustmentRequest = {
  line: ProductionLineConfig;
  group: PlannedOrderGroup;
};

type PlannedGroupDropPreview = {
  lineId: string;
  targetGroupKey: string;
  position: 'before' | 'after';
};

type TrackingConfirmDialogRequest = {
  title: string;
  description: string;
  confirmLabel: string;
  confirmColor?: 'primary' | 'info' | 'warning' | 'error';
  onConfirm: () => void;
};

type ProjectedPlanSegmentType = 'production' | 'gap';

type ProjectedPlanConflictType =
  | 'previous_item_overlaps_locked_start'
  | 'locked_start_moved_after_previous';

type ProjectedPlanSegment = {
  type: ProjectedPlanSegmentType;
  key: string;
  item: ProductionPlanItem | null;
  groupKey: string | null;
  referenceLabel: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  durationMinutes: number;
  isStartLocked: boolean;
  lockedStartDate: string | null;
  lockedStartTime: string | null;
  hasConflict: boolean;
  conflictType: ProjectedPlanConflictType | null;
  conflictMessage: string | null;
  segmentIndex: number;
  totalSegments: number;
  isSplitSegment: boolean;
};

type ProjectedLinePlan = {
  lineId: string;
  lineLabel: string;
  segments: ProjectedPlanSegment[];
  totalProductionMinutes: number;
  totalGapMinutes: number;
  projectedEndDate: string | null;
  projectedEndTime: string | null;
  conflictsCount: number;
};

function getTodayLocalIsoDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function formatQuantity(value: number | null | undefined, unit?: string | null) {
  const safeValue = Number(value ?? 0);

  return `${formatEsNumber(safeValue, {
    maximumFractionDigits: 2,
  })}${unit ? ` ${unit}` : ''}`;
}

function formatTime(value: string | null | undefined) {
  if (!value) return '—';
  return value.slice(0, 5);
}

function formatDurationMinutes(value: number | null | undefined) {
  const safeValue = Number(value || 0);

  if (safeValue <= 0) return 'Sin duración';

  const hours = Math.floor(safeValue / 60);
  const minutes = safeValue % 60;

  if (hours > 0 && minutes > 0) return `${hours} h ${minutes} min`;
  if (hours > 0) return `${hours} h`;

  return `${minutes} min`;
}

function resolvePlanItemDurationMinutes(item: ProductionPlanItem) {
  const itemDuration = Number(item.estimated_duration_minutes || 0);
  if (itemDuration > 0) return itemDuration;

  return Number(item.imported_order_planning_duration_minutes || 0);
}

function resolvePlanItemOrderValue(item: ProductionPlanItem) {
  const sequenceOrder = Number(item.line_sequence_order ?? 0);
  if (sequenceOrder > 0) return sequenceOrder;

  const priorityOrder = Number(item.priority || 0);
  if (priorityOrder > 0) return priorityOrder;

  return Number.MAX_SAFE_INTEGER;
}

function resolvePlanItemPersistedOrderValue(item: ProductionPlanItem) {
  const sequenceOrder = Number(item.line_sequence_order ?? 0);
  if (sequenceOrder > 0) return sequenceOrder;

  const priorityOrder = Number(item.priority || 0);
  if (priorityOrder > 0) return priorityOrder;

  return 0;
}

function resolveNextLineSequenceOrder(items: ProductionPlanItem[]) {
  return items.reduce(
    (maxOrder, item) => Math.max(maxOrder, resolvePlanItemPersistedOrderValue(item)),
    0,
  );
}

function normalizePlanningTime(value: string | null | undefined, fallback = '06:00') {
  if (!value) return fallback;

  const normalized = value.slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(normalized)) return fallback;

  return normalized;
}

function timeToMinutes(value: string) {
  const [hoursRaw, minutesRaw] = value.split(':');
  const hours = Number(hoursRaw || 0);
  const minutes = Number(minutesRaw || 0);

  return hours * 60 + minutes;
}

function minutesToTime(value: number) {
  const normalized = ((value % 1440) + 1440) % 1440;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function parseIsoDateParts(dateIso: string) {
  const [yearRaw, monthRaw, dayRaw] = dateIso.slice(0, 10).split('-');

  return {
    year: Number(yearRaw),
    month: Number(monthRaw),
    day: Number(dayRaw),
  };
}

function isoDateToEpochDay(dateIso: string) {
  const { year, month, day } = parseIsoDateParts(dateIso);

  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

function epochDayToIsoDate(epochDay: number) {
  const date = new Date(epochDay * 86_400_000);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function addMinutesToTime(startTime: string, minutesToAdd: number) {
  return minutesToTime(timeToMinutes(startTime) + minutesToAdd);
}

function dateTimeToAbsoluteMinutes(dateIso: string, timeValue: string) {
  const epochDay = isoDateToEpochDay(dateIso);

  return epochDay * 1_440 + timeToMinutes(normalizePlanningTime(timeValue));
}

function absoluteMinutesToDateTime(value: number) {
  const epochDay = Math.floor(value / 1_440);
  const minutesInDay = value - epochDay * 1_440;

  return {
    dateIso: epochDayToIsoDate(epochDay),
    time: minutesToTime(minutesInDay),
  };
}

function toLocalIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getWeekStartIsoDate(dateIso: string) {
  const date = new Date(`${dateIso}T00:00:00`);
  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diffToMonday);

  return toLocalIsoDate(date);
}

function addDaysToIsoDate(dateIso: string, daysToAdd: number) {
  const date = new Date(`${dateIso}T00:00:00`);
  date.setDate(date.getDate() + daysToAdd);

  return toLocalIsoDate(date);
}

function normalizeIsoDateValue(value: string | null | undefined) {
  if (!value) return '';
  return value.slice(0, 10);
}

function getCurrentTrackingReference() {
  const now = new Date();

  now.setSeconds(0, 0);
  now.setMinutes(Math.floor(now.getMinutes() / 15) * 15);

  return {
    dateIso: toLocalIsoDate(now),
    time: minutesToTime(now.getHours() * 60 + now.getMinutes()),
  };
}

function getWeekTrackingReference(weekStartDate: string) {
  return {
    dateIso: weekStartDate,
    time: '06:00',
  };
}

function isPlanItemLockedStartBeforeReference(
  item: ProductionPlanItem,
  referenceDateIso: string,
  referenceTime: string,
) {
  if (
    !item.is_start_locked ||
    !item.locked_start_date ||
    !item.locked_start_time
  ) {
    return false;
  }

  return (
    dateTimeToAbsoluteMinutes(
      normalizeIsoDateValue(item.locked_start_date),
      normalizePlanningTime(item.locked_start_time),
    ) <
    dateTimeToAbsoluteMinutes(
      normalizeIsoDateValue(referenceDateIso),
      normalizePlanningTime(referenceTime),
    )
  );
}

function sanitizePlanItemForLiveProjection(
  item: ProductionPlanItem,
  referenceDateIso: string,
  referenceTime: string,
): ProductionPlanItem {
  if (
    !isPlanItemLockedStartBeforeReference(
      item,
      referenceDateIso,
      referenceTime,
    )
  ) {
    return item;
  }

  return {
    ...item,
    is_start_locked: false,
    locked_start_date: null,
    locked_start_time: null,
    locked_start_conflict_policy: null,
    locked_start_note: null,
  };
}

function formatShortWeekDay(dateIso: string) {
  const date = new Date(`${dateIso}T00:00:00`);

  return new Intl.DateTimeFormat('es-ES', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(date);
}

function formatTrackingDayLabel(dateIso: string) {
  const date = new Date(`${dateIso}T00:00:00`);
  const label = new Intl.DateTimeFormat('es-ES', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
  }).format(date);

  return label.charAt(0).toLocaleUpperCase('es-ES') + label.slice(1);
}

function calculateTimeRangeDurationMinutes(startTime: string, endTime: string) {
  const startMinutes = timeToMinutes(normalizePlanningTime(startTime));
  const endMinutes = timeToMinutes(normalizePlanningTime(endTime));

  if (endMinutes > startMinutes) {
    return endMinutes - startMinutes;
  }

  if (endMinutes < startMinutes) {
    return (24 * 60 - startMinutes) + endMinutes;
  }

  return 24 * 60;
}

function buildLineLabel(line: ProductionLineConfig) {
  return line.name || line.code || 'Línea sin nombre';
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'No se pudo completar la operación.';
}

function buildProcessGroupKey(value: string | null | undefined) {
  const normalized = value?.trim();

  return normalized || '__ungrouped__';
}

function buildProcessGroupLabel(value: string | null | undefined) {
  const normalized = value?.trim();

  return normalized || 'Sin grupo';
}

function uniqueNonEmpty(values: Array<string | null | undefined>) {
  const normalizedValues = values
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));

  return Array.from(new Set(normalizedValues));
}

function resolvePendingOrderGroupDurationMinutes(group: PendingOrderGroup) {
  return group.orders.reduce(
    (sum, order) => sum + Math.max(Number(order.planning_duration_minutes || 0), 0),
    0,
  );
}

function getMinimumPendingGroupManualDurationMinutes(
  group: PendingOrderGroup | null | undefined,
) {
  return Math.max(group?.orders.length ?? 1, 1);
}

function distributePendingGroupDurationMinutesAcrossOrders(
  group: PendingOrderGroup,
  totalDurationMinutes: number,
) {
  const orders = group.orders;
  const durationsByOrderId = new Map<string, number>();

  if (orders.length === 0) {
    return durationsByOrderId;
  }

  const totalDuration = Math.max(
    Math.round(Number(totalDurationMinutes || 0)),
    getMinimumPendingGroupManualDurationMinutes(group),
  );

  orders.forEach((order) => {
    durationsByOrderId.set(order.id, 1);
  });

  let remainingMinutes = totalDuration - orders.length;

  if (remainingMinutes <= 0) {
    return durationsByOrderId;
  }

  const quantityWeights = orders.map((order) =>
    Math.max(Number(order.planned_quantity_value || 0), 0),
  );
  const totalQuantityWeight = quantityWeights.reduce((sum, value) => sum + value, 0);
  const effectiveWeights =
    totalQuantityWeight > 0 ? quantityWeights : orders.map(() => 1);
  const totalEffectiveWeight = effectiveWeights.reduce((sum, value) => sum + value, 0);

  const allocations = orders.map((order, index) => {
    const rawExtraMinutes =
      totalEffectiveWeight > 0
        ? (remainingMinutes * effectiveWeights[index]) / totalEffectiveWeight
        : 0;
    const baseExtraMinutes = Math.floor(rawExtraMinutes);

    return {
      orderId: order.id,
      baseExtraMinutes,
      remainder: rawExtraMinutes - baseExtraMinutes,
    };
  });

  allocations.forEach((allocation) => {
    durationsByOrderId.set(
      allocation.orderId,
      (durationsByOrderId.get(allocation.orderId) ?? 1) +
        allocation.baseExtraMinutes,
    );
  });

  remainingMinutes -= allocations.reduce(
    (sum, allocation) => sum + allocation.baseExtraMinutes,
    0,
  );

  allocations
    .sort((a, b) => b.remainder - a.remainder)
    .slice(0, remainingMinutes)
    .forEach((allocation) => {
      durationsByOrderId.set(
        allocation.orderId,
        (durationsByOrderId.get(allocation.orderId) ?? 1) + 1,
      );
    });

  return durationsByOrderId;
}

function getInitialPendingPanelOpen() {
  return false;
}

const PLANNING_CONTEXT_STALE_TIME_MS = 15_000;

function PlannedGroupDropPlaceholder({
  isDragging,
  isActive,
  onDragOver,
  onDrop,
}: {
  isDragging: boolean;
  isActive: boolean;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
}) {
  if (!isDragging) return null;

  return (
    <Box
      onDragOver={onDragOver}
      onDrop={onDrop}
      sx={{
        borderRadius: 2.5,
        border: isActive
          ? '1px dashed rgba(56,189,248,0.72)'
          : '1px dashed transparent',
        bgcolor: isActive ? 'rgba(14,165,233,0.16)' : 'transparent',
        minHeight: isActive ? 58 : 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'min-height 0.12s ease, background 0.12s ease, border-color 0.12s ease',
      }}
    >
      {isActive && (
        <Typography
          sx={{
            color: '#bae6fd',
            fontSize: '0.78rem',
            fontWeight: 900,
            pointerEvents: 'none',
          }}
        >
          Soltar aquí
        </Typography>
      )}
    </Box>
  );
}
function PlannedOrderGroupCard({
  group,
  visualStartTime,
  visualEndTime,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  isBeingDragged = false,
}: {
  group: PlannedOrderGroup;
  visualStartTime: string;
  visualEndTime: string;
  onDragStart: (groupKey: string) => void;
  onDragEnd: () => void;
  onDragOver?: (event: DragEvent<HTMLElement>) => void;
  onDrop?: (event: DragEvent<HTMLElement>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  isBeingDragged?: boolean;
}) {
  return (
    <Paper
      elevation={0}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', group.key);
        onDragStart(group.key);
      }}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      sx={{
        borderRadius: 2.5,
        border: '1px solid rgba(56,189,248,0.22)',
        bgcolor: 'rgba(8,47,73,0.34)',
        px: 1,
        py: 0.8,
        cursor: 'grab',
        opacity: isBeingDragged ? 0.38 : 1,
        transition: 'border-color 0.18s ease, background 0.18s ease, transform 0.18s ease, opacity 0.12s ease',
        '&:hover': {
          borderColor: 'rgba(56,189,248,0.42)',
          bgcolor: 'rgba(8,47,73,0.44)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Stack spacing={0.65} sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: '#f8fafc',
              fontWeight: 950,
              fontSize: '0.92rem',
              wordBreak: 'break-word',
              lineHeight: 1.2,
            }}
          >
            {group.referenceLabel}
          </Typography>

          <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`${group.items.length} ${group.items.length === 1 ? 'OF' : 'OFs'}`}
              variant="outlined"
              sx={{
                height: 21,
                color: '#bae6fd',
                borderColor: 'rgba(56,189,248,0.24)',
                bgcolor: 'rgba(14,165,233,0.08)',
                fontSize: '0.68rem',
                fontWeight: 850,
              }}
            />

            <Chip
              size="small"
              label={formatQuantity(group.totalQuantity, group.quantityUnit)}
              variant="outlined"
              sx={{
                height: 21,
                color: '#cbd5e1',
                borderColor: 'rgba(148,163,184,0.18)',
                fontSize: '0.68rem',
              }}
            />

            <Chip
              size="small"
              label={formatDurationMinutes(group.totalDurationMinutes)}
              variant="outlined"
              sx={{
                height: 21,
                color: group.totalDurationMinutes > 0 ? '#cbd5e1' : '#64748b',
                borderColor: 'rgba(148,163,184,0.18)',
                fontSize: '0.68rem',
              }}
            />

            <Chip
              size="small"
              label={`${visualStartTime}–${visualEndTime}`}
              variant="outlined"
              sx={{
                height: 21,
                color: '#bae6fd',
                borderColor: 'rgba(56,189,248,0.22)',
                bgcolor: 'rgba(14,165,233,0.08)',
                fontSize: '0.68rem',
                fontWeight: 800,
              }}
            />
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!canMoveUp}
            onClick={(event) => {
              event.stopPropagation();
              onMoveUp();
            }}
            sx={{
              minWidth: 42,
              height: 34,
              px: 0.75,
              py: 0,
              lineHeight: 1,
              fontSize: '1.05rem',
              fontWeight: 950,
            }}
          >
            ↑
          </Button>

          <Button
            size="small"
            variant="outlined"
            disabled={!canMoveDown}
            onClick={(event) => {
              event.stopPropagation();
              onMoveDown();
            }}
            sx={{
              minWidth: 42,
              height: 34,
              px: 0.75,
              py: 0,
              lineHeight: 1,
              fontSize: '1.05rem',
              fontWeight: 950,
            }}
          >
            ↓
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ProjectedContinuationCard({
  segment,
  visualStartTime,
  visualEndTime,
  onOpenOriginalDay,
}: {
  segment: WeeklyContinuousSegment;
  visualStartTime: string;
  visualEndTime: string;
  onOpenOriginalDay: () => void;
}) {
  return (
    <Paper
      elevation={0}
      onClick={(event) => {
        event.stopPropagation();
        onOpenOriginalDay();
      }}
      sx={{
        borderRadius: 2.5,
        border: '1px dashed rgba(56,189,248,0.38)',
        bgcolor: 'rgba(8,47,73,0.28)',
        px: 1,
        py: 0.8,
        cursor: 'pointer',
      }}
    >
      <Stack spacing={0.65}>
        <Stack direction="row" spacing={0.65} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: '#38bdf8',
              fontWeight: 950,
              fontSize: '0.82rem',
              flexShrink: 0,
            }}
          >
            ↳
          </Typography>

          <Typography
            noWrap
            sx={{
              color: '#f8fafc',
              fontWeight: 950,
              fontSize: '0.9rem',
              lineHeight: 1.2,
              minWidth: 0,
            }}
          >
            {segment.referenceLabel}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label="Continuación"
            variant="outlined"
            sx={{
              height: 21,
              color: '#bae6fd',
              borderColor: 'rgba(56,189,248,0.24)',
              bgcolor: 'rgba(14,165,233,0.08)',
              fontSize: '0.66rem',
              fontWeight: 850,
            }}
          />

          <Chip
            size="small"
            label={formatDurationMinutes(segment.segmentMinutes)}
            variant="outlined"
            sx={{
              height: 21,
              color: '#cbd5e1',
              borderColor: 'rgba(148,163,184,0.18)',
              fontSize: '0.66rem',
            }}
          />

          <Chip
            size="small"
            label={`${visualStartTime}–${visualEndTime}`}
            variant="outlined"
            sx={{
              height: 21,
              color: '#bae6fd',
              borderColor: 'rgba(56,189,248,0.22)',
              bgcolor: 'rgba(14,165,233,0.08)',
              fontSize: '0.66rem',
              fontWeight: 800,
            }}
          />
        </Stack>

        <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
          Viene de {formatShortWeekDay(segment.originalDateIso)}. Click para abrir el día original.
        </Typography>
      </Stack>
    </Paper>
  );
}

function buildProjectedLinePlan({
  line,
  items,
  baseStartDate,
  baseStartTime,
}: {
  line: ProductionLineConfig;
  items: ProductionPlanItem[];
  baseStartDate: string;
  baseStartTime: string;
}): ProjectedLinePlan {
  const orderedItems = [...items].sort((a, b) => {
    const orderDiff = resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b);

    if (orderDiff !== 0) return orderDiff;

    return normalizeIsoDateValue(a.planned_date).localeCompare(
      normalizeIsoDateValue(b.planned_date),
    );
  });

  const groups = buildPlannedOrderGroups(orderedItems);
  const segments: ProjectedPlanSegment[] = [];

  let cursorMinutes = dateTimeToAbsoluteMinutes(
    baseStartDate,
    normalizePlanningTime(baseStartTime),
  );

  groups.forEach((group) => {
    const firstItem = group.items[0];
    const durationMinutes = Math.max(Number(group.totalDurationMinutes || 0), 0);
    const lockedItem = group.items.find((item) => item.is_start_locked);
    const lockedStartDate = normalizeIsoDateValue(lockedItem?.locked_start_date);
    const lockedStartTime = lockedItem?.locked_start_time
      ? normalizePlanningTime(lockedItem.locked_start_time)
      : null;
    const hasLockedStart = Boolean(lockedItem && lockedStartDate && lockedStartTime);

    if (!firstItem) return;

    if (durationMinutes <= 0) {
      const start = absoluteMinutesToDateTime(cursorMinutes);

      segments.push({
        type: 'production',
        key: `production:${group.key}:0`,
        item: firstItem,
        groupKey: group.key,
        referenceLabel: group.referenceLabel,
        startDate: start.dateIso,
        startTime: start.time,
        endDate: start.dateIso,
        endTime: start.time,
        durationMinutes: 0,
        isStartLocked: hasLockedStart,
        lockedStartDate: hasLockedStart ? lockedStartDate : null,
        lockedStartTime: hasLockedStart ? lockedStartTime : null,
        hasConflict: false,
        conflictType: null,
        conflictMessage: null,
        segmentIndex: 1,
        totalSegments: 1,
        isSplitSegment: false,
      });

      return;
    }

    if (!hasLockedStart) {
      const start = absoluteMinutesToDateTime(cursorMinutes);
      const end = absoluteMinutesToDateTime(cursorMinutes + durationMinutes);

      segments.push({
        type: 'production',
        key: `production:${group.key}:0`,
        item: firstItem,
        groupKey: group.key,
        referenceLabel: group.referenceLabel,
        startDate: start.dateIso,
        startTime: start.time,
        endDate: end.dateIso,
        endTime: end.time,
        durationMinutes,
        isStartLocked: false,
        lockedStartDate: null,
        lockedStartTime: null,
        hasConflict: false,
        conflictType: null,
        conflictMessage: null,
        segmentIndex: 1,
        totalSegments: 1,
        isSplitSegment: false,
      });

      cursorMinutes += durationMinutes;
      return;
    }

    if (!lockedStartDate || !lockedStartTime) {
      return;
    }

    const lockedStartMinutes = dateTimeToAbsoluteMinutes(
      lockedStartDate,
      lockedStartTime,
    );
    const naturalEndMinutes = cursorMinutes + durationMinutes;
    const policy =
      lockedItem?.locked_start_conflict_policy ??
      'create_gap_if_previous_does_not_fit';

    if (cursorMinutes <= lockedStartMinutes) {
      const availableBeforeLockedStart = lockedStartMinutes - cursorMinutes;

      if (availableBeforeLockedStart > 0 && availableBeforeLockedStart < durationMinutes) {
        if (policy === 'split_previous_item') {
          const firstStart = absoluteMinutesToDateTime(cursorMinutes);
          const firstEnd = absoluteMinutesToDateTime(lockedStartMinutes);
          const secondStart = absoluteMinutesToDateTime(lockedStartMinutes);
          const secondEnd = absoluteMinutesToDateTime(
            lockedStartMinutes + durationMinutes - availableBeforeLockedStart,
          );

          segments.push({
            type: 'production',
            key: `production:${group.key}:split:1`,
            item: firstItem,
            groupKey: group.key,
            referenceLabel: group.referenceLabel,
            startDate: firstStart.dateIso,
            startTime: firstStart.time,
            endDate: firstEnd.dateIso,
            endTime: firstEnd.time,
            durationMinutes: availableBeforeLockedStart,
            isStartLocked: true,
            lockedStartDate,
            lockedStartTime,
            hasConflict: false,
            conflictType: null,
            conflictMessage: null,
            segmentIndex: 1,
            totalSegments: 2,
            isSplitSegment: true,
          });

          segments.push({
            type: 'production',
            key: `production:${group.key}:split:2`,
            item: firstItem,
            groupKey: group.key,
            referenceLabel: `${group.referenceLabel} · continuación`,
            startDate: secondStart.dateIso,
            startTime: secondStart.time,
            endDate: secondEnd.dateIso,
            endTime: secondEnd.time,
            durationMinutes: durationMinutes - availableBeforeLockedStart,
            isStartLocked: false,
            lockedStartDate,
            lockedStartTime,
            hasConflict: false,
            conflictType: null,
            conflictMessage: null,
            segmentIndex: 2,
            totalSegments: 2,
            isSplitSegment: true,
          });

          cursorMinutes = lockedStartMinutes + durationMinutes - availableBeforeLockedStart;
          return;
        }

        if (policy === 'move_locked_start_after_previous') {
          const start = absoluteMinutesToDateTime(cursorMinutes);
          const end = absoluteMinutesToDateTime(naturalEndMinutes);

          segments.push({
            type: 'production',
            key: `production:${group.key}:0`,
            item: firstItem,
            groupKey: group.key,
            referenceLabel: group.referenceLabel,
            startDate: start.dateIso,
            startTime: start.time,
            endDate: end.dateIso,
            endTime: end.time,
            durationMinutes,
            isStartLocked: true,
            lockedStartDate,
            lockedStartTime,
            hasConflict: true,
            conflictType: 'locked_start_moved_after_previous',
            conflictMessage: `Inicio fijado ${lockedStartDate} ${lockedStartTime} ajustado al final natural de la producción anterior.`,
            segmentIndex: 1,
            totalSegments: 1,
            isSplitSegment: false,
          });

          cursorMinutes = naturalEndMinutes;
          return;
        }

        const gapStart = absoluteMinutesToDateTime(cursorMinutes);
        const gapEnd = absoluteMinutesToDateTime(lockedStartMinutes);
        const start = absoluteMinutesToDateTime(lockedStartMinutes);
        const end = absoluteMinutesToDateTime(lockedStartMinutes + durationMinutes);

        segments.push({
          type: 'gap',
          key: `gap:${group.key}`,
          item: null,
          groupKey: null,
          referenceLabel: 'Hueco antes de inicio fijado',
          startDate: gapStart.dateIso,
          startTime: gapStart.time,
          endDate: gapEnd.dateIso,
          endTime: gapEnd.time,
          durationMinutes: availableBeforeLockedStart,
          isStartLocked: false,
          lockedStartDate,
          lockedStartTime,
          hasConflict: false,
          conflictType: null,
          conflictMessage: null,
          segmentIndex: 1,
          totalSegments: 1,
          isSplitSegment: false,
        });

        segments.push({
          type: 'production',
          key: `production:${group.key}:0`,
          item: firstItem,
          groupKey: group.key,
          referenceLabel: group.referenceLabel,
          startDate: start.dateIso,
          startTime: start.time,
          endDate: end.dateIso,
          endTime: end.time,
          durationMinutes,
          isStartLocked: true,
          lockedStartDate,
          lockedStartTime,
          hasConflict: false,
          conflictType: null,
          conflictMessage: null,
          segmentIndex: 1,
          totalSegments: 1,
          isSplitSegment: false,
        });

        cursorMinutes = lockedStartMinutes + durationMinutes;
        return;
      }

      if (availableBeforeLockedStart > 0) {
        const gapStart = absoluteMinutesToDateTime(cursorMinutes);
        const gapEnd = absoluteMinutesToDateTime(lockedStartMinutes);

        segments.push({
          type: 'gap',
          key: `gap:${group.key}`,
          item: null,
          groupKey: null,
          referenceLabel: 'Hueco antes de inicio fijado',
          startDate: gapStart.dateIso,
          startTime: gapStart.time,
          endDate: gapEnd.dateIso,
          endTime: gapEnd.time,
          durationMinutes: availableBeforeLockedStart,
          isStartLocked: false,
          lockedStartDate,
          lockedStartTime,
          hasConflict: false,
          conflictType: null,
          conflictMessage: null,
          segmentIndex: 1,
          totalSegments: 1,
          isSplitSegment: false,
        });
      }

      const start = absoluteMinutesToDateTime(lockedStartMinutes);
      const end = absoluteMinutesToDateTime(lockedStartMinutes + durationMinutes);

      segments.push({
        type: 'production',
        key: `production:${group.key}:0`,
        item: firstItem,
        groupKey: group.key,
        referenceLabel: group.referenceLabel,
        startDate: start.dateIso,
        startTime: start.time,
        endDate: end.dateIso,
        endTime: end.time,
        durationMinutes,
        isStartLocked: true,
        lockedStartDate,
        lockedStartTime,
        hasConflict: false,
        conflictType: null,
        conflictMessage: null,
        segmentIndex: 1,
        totalSegments: 1,
        isSplitSegment: false,
      });

      cursorMinutes = lockedStartMinutes + durationMinutes;
      return;
    }

    const start = absoluteMinutesToDateTime(cursorMinutes);
    const end = absoluteMinutesToDateTime(cursorMinutes + durationMinutes);

    segments.push({
      type: 'production',
      key: `production:${group.key}:0`,
      item: firstItem,
      groupKey: group.key,
      referenceLabel: group.referenceLabel,
      startDate: start.dateIso,
      startTime: start.time,
      endDate: end.dateIso,
      endTime: end.time,
      durationMinutes,
      isStartLocked: true,
      lockedStartDate,
      lockedStartTime,
      hasConflict: true,
      conflictType: 'previous_item_overlaps_locked_start',
      conflictMessage: `La cola previa llega más tarde que el inicio fijado ${lockedStartDate} ${lockedStartTime}.`,
      segmentIndex: 1,
      totalSegments: 1,
      isSplitSegment: false,
    });

    cursorMinutes += durationMinutes;
  });

  const productionSegments = segments.filter((segment) => segment.type === 'production');
  const gapSegments = segments.filter((segment) => segment.type === 'gap');
  const lastSegment = segments[segments.length - 1];

  return {
    lineId: line.id,
    lineLabel: buildLineLabel(line),
    segments,
    totalProductionMinutes: productionSegments.reduce(
      (sum, segment) => sum + segment.durationMinutes,
      0,
    ),
    totalGapMinutes: gapSegments.reduce(
      (sum, segment) => sum + segment.durationMinutes,
      0,
    ),
    projectedEndDate: lastSegment?.endDate ?? null,
    projectedEndTime: lastSegment?.endTime ?? null,
    conflictsCount: segments.filter((segment) => segment.hasConflict).length,
  };
}

function normalizePlanningReferenceForGrouping(value: string | null | undefined) {
  return ' '.concat(value ?? '').trim().toLocaleLowerCase('es-ES').replace(/\s+/g, ' ');
}

function normalizePlanningQuantityUnitForGrouping(value: string | null | undefined) {
  return value?.trim().toLocaleLowerCase('es-ES') || null;
}

function buildPlannedOrderGroups(items: ProductionPlanItem[]): PlannedOrderGroup[] {
  const groups: Array<{
    key: string;
    referenceKey: string | null;
    quantityUnitKey: string | null;
    items: ProductionPlanItem[];
  }> = [];

  const orderedItems = [...items].sort((a, b) => {
    const orderDiff = resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b);

    if (orderDiff !== 0) return orderDiff;

    return (
      normalizeIsoDateValue(a.planned_date).localeCompare(
        normalizeIsoDateValue(b.planned_date),
      ) ||
      formatTime(a.planned_start_time).localeCompare(formatTime(b.planned_start_time)) ||
      a.id.localeCompare(b.id)
    );
  });

  orderedItems.forEach((item) => {
    const reference = item.free_product_ref?.trim();
    const referenceKey = normalizePlanningReferenceForGrouping(reference) || null;
    const quantityUnitKey = normalizePlanningQuantityUnitForGrouping(item.quantity_unit);
    const previousGroup = groups[groups.length - 1];

    if (
      referenceKey &&
      previousGroup?.referenceKey === referenceKey &&
      previousGroup.quantityUnitKey === quantityUnitKey
    ) {
      previousGroup.items.push(item);
      return;
    }

    groups.push({
      key: referenceKey
        ? `ref:${referenceKey}:unit:${quantityUnitKey ?? 'sin-unidad'}:block:${item.id}`
        : `item:${item.id}`,
      referenceKey,
      quantityUnitKey,
      items: [item],
    });
  });

  return groups.map(({ key, items: groupItems }) => {
    const firstItem = groupItems[0];
    const reference = firstItem.free_product_ref?.trim();
    const isReferenceGroup = Boolean(reference);

    return {
      key,
      referenceLabel:
        reference || firstItem.manufacturing_order_number || 'OF sin referencia',
      isReferenceGroup,
      items: groupItems,
      totalQuantity: groupItems.reduce(
        (sum, item) => sum + Number(item.planned_quantity_value || 0),
        0,
      ),
      quantityUnit: firstItem.quantity_unit ?? null,
      totalDurationMinutes: groupItems.reduce(
        (sum, item) => sum + resolvePlanItemDurationMinutes(item),
        0,
      ),
      startTime:
        groupItems
          .map((item) => item.planned_start_time)
          .filter(Boolean)
          .sort()[0] ?? null,
      endTime: (() => {
        const sortedEndTimes = groupItems
          .map((item) => item.planned_end_time)
          .filter(Boolean)
          .sort();

        return sortedEndTimes.length > 0
          ? sortedEndTimes[sortedEndTimes.length - 1]
          : null;
      })(),
      status: firstItem.status,
      customers: uniqueNonEmpty(groupItems.map((item) => item.customer_number)),
      customerOrders: uniqueNonEmpty(groupItems.map((item) => item.customer_order_number)),
    };
  });
}

function buildWeeklyContinuousSegments({
  lines,
  planItems,
  weekDates,
  baseDailyCapacityMinutes,
}: {
  lines: ProductionLineConfig[];
  planItems: ProductionPlanItem[];
  weekDates: string[];
  baseDailyCapacityMinutes: number;
}) {
  const segments: WeeklyContinuousSegment[] = [];
  const firstWeekDate = weekDates[0];
  const lastWeekDate = weekDates[weekDates.length - 1];

  lines.forEach((line) => {
    const lineItems = planItems.filter(
      (item) => item.production_line_config_id === line.id,
    );

    const projectedLinePlan = buildProjectedLinePlan({
      line,
      items: lineItems,
      baseStartDate: firstWeekDate,
      baseStartTime: '06:00',
    });

    const lineGroups = projectedLinePlan.segments
      .filter((segment) => segment.type === 'production')
      .map((segment) => ({
        group: {
          key: segment.groupKey ?? segment.key,
          referenceLabel: segment.referenceLabel,
          isReferenceGroup: Boolean(segment.groupKey?.startsWith('ref:')),
          items: segment.item ? [segment.item] : [],
          totalQuantity: Number(segment.item?.planned_quantity_value || 0),
          quantityUnit: segment.item?.quantity_unit ?? null,
          totalDurationMinutes: segment.durationMinutes,
          startTime: segment.startTime,
          endTime: segment.endTime,
          status: segment.item?.status ?? 'planned',
          customers: segment.item ? uniqueNonEmpty([segment.item.customer_number]) : [],
          customerOrders: segment.item ? uniqueNonEmpty([segment.item.customer_order_number]) : [],
        } satisfies PlannedOrderGroup,
        originalDateIso: segment.startDate,
      }));

    let cursorDayIndex = 0;
    let cursorUsedMinutes = 0;

    lineGroups.forEach(({ group, originalDateIso }) => {
      if (group.totalDurationMinutes <= 0) {
        segments.push({
          lineId: line.id,
          dateIso: firstWeekDate,
          originalDateIso,
          groupKey: group.key,
          referenceLabel: group.referenceLabel,
          segmentMinutes: 0,
          totalGroupMinutes: 0,
          itemsCount: group.items.length,
          isContinuationFromPreviousDay: false,
          continuesNextDay: false,
          isOverflowBeyondWeek: false,
        });

        return;
      }

      if (baseDailyCapacityMinutes <= 0) {
        segments.push({
          lineId: line.id,
          dateIso: firstWeekDate,
          originalDateIso,
          groupKey: group.key,
          referenceLabel: group.referenceLabel,
          segmentMinutes: group.totalDurationMinutes,
          totalGroupMinutes: group.totalDurationMinutes,
          itemsCount: group.items.length,
          isContinuationFromPreviousDay: false,
          continuesNextDay: false,
          isOverflowBeyondWeek: false,
        });

        return;
      }

      let remainingMinutes = group.totalDurationMinutes;
      let segmentIndex = 0;

      while (remainingMinutes > 0) {
        if (cursorDayIndex >= weekDates.length) {
          segments.push({
            lineId: line.id,
            dateIso: lastWeekDate,
            originalDateIso,
            groupKey: group.key,
            referenceLabel: group.referenceLabel,
            segmentMinutes: remainingMinutes,
            totalGroupMinutes: group.totalDurationMinutes,
            itemsCount: group.items.length,
            isContinuationFromPreviousDay: true,
            continuesNextDay: true,
            isOverflowBeyondWeek: true,
          });

          break;
        }

        const availableMinutes = baseDailyCapacityMinutes - cursorUsedMinutes;

        if (availableMinutes <= 0) {
          cursorDayIndex += 1;
          cursorUsedMinutes = 0;
          continue;
        }

        const segmentMinutes = Math.min(remainingMinutes, availableMinutes);
        const dateIso = weekDates[cursorDayIndex];
        const remainingAfterSegment = remainingMinutes - segmentMinutes;

        segments.push({
          lineId: line.id,
          dateIso,
          originalDateIso,
          groupKey: group.key,
          referenceLabel: group.referenceLabel,
          segmentMinutes,
          totalGroupMinutes: group.totalDurationMinutes,
          itemsCount: group.items.length,
          isContinuationFromPreviousDay: segmentIndex > 0 || dateIso !== originalDateIso,
          continuesNextDay: remainingAfterSegment > 0,
          isOverflowBeyondWeek: false,
        });

        remainingMinutes = remainingAfterSegment;
        cursorUsedMinutes += segmentMinutes;
        segmentIndex += 1;

        if (cursorUsedMinutes >= baseDailyCapacityMinutes) {
          cursorDayIndex += 1;
          cursorUsedMinutes = 0;
        }
      }
    });
  });

  return segments;
}

function ProjectedLineTimelineBoard({
  projectedLinePlan,
  onEditGroup,
}: {
  projectedLinePlan: ProjectedLinePlan;
  onEditGroup: (
    groupKey: string,
    suggestedStartDate: string,
    suggestedStartTime: string,
  ) => void;
}) {
  const pixelsPerHour = 54;
  const hourStep = 2;
  const leftAxisWidth = 92;

  const segmentBounds = projectedLinePlan.segments
    .filter((segment) => segment.type === 'production')
    .map((segment) => ({
      segment,
      startMinutes: dateTimeToAbsoluteMinutes(
        segment.startDate,
        segment.startTime,
      ),
      endMinutes: dateTimeToAbsoluteMinutes(
        segment.endDate,
        segment.endTime,
      ),
    }));

  const firstStartMinutes = segmentBounds[0]?.startMinutes ?? 0;
  const lastEndMinutes =
    segmentBounds.length > 0
      ? Math.max(...segmentBounds.map((item) => item.endMinutes))
      : firstStartMinutes + 24 * 60;

  const timelineStartMinutes = Math.floor(firstStartMinutes / 120) * 120;
  const timelineEndMinutes = Math.max(
    Math.ceil(lastEndMinutes / 120) * 120,
    timelineStartMinutes + 24 * 60,
  );

  const totalTimelineMinutes = timelineEndMinutes - timelineStartMinutes;
  const boardHeight = Math.max((totalTimelineMinutes / 60) * pixelsPerHour, 520);

  const hourMarkers = Array.from(
    { length: Math.floor(totalTimelineMinutes / (hourStep * 60)) + 1 },
    (_, index) => timelineStartMinutes + index * hourStep * 60,
  );

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: boardHeight,
        borderRadius: 3,
        border: '1px solid rgba(148,163,184,0.14)',
        bgcolor: 'rgba(2,6,23,0.38)',
        overflow: 'hidden',
      }}
    >
      {hourMarkers.map((markerMinutes) => {
        const markerDateTime = absoluteMinutesToDateTime(markerMinutes);
        const top = ((markerMinutes - timelineStartMinutes) / 60) * pixelsPerHour;
        const isDayStart = markerDateTime.time === '00:00';

        return (
          <Box key={`marker-${markerMinutes}`}>
            <Box
              sx={{
                position: 'absolute',
                top,
                left: 0,
                width: leftAxisWidth,
                px: 1,
                transform:
                  markerMinutes === timelineStartMinutes
                    ? 'translateY(0)'
                    : 'translateY(-50%)',
              }}
            >
              <Typography
                sx={{
                  color: isDayStart ? '#bae6fd' : '#64748b',
                  fontSize: isDayStart ? '0.72rem' : '0.68rem',
                  fontWeight: isDayStart ? 950 : 750,
                  lineHeight: 1.15,
                }}
              >
                {isDayStart
                  ? formatShortWeekDay(markerDateTime.dateIso)
                  : markerDateTime.time}
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                top,
                left: leftAxisWidth,
                right: 0,
                borderTop: isDayStart
                  ? '1px solid rgba(56,189,248,0.28)'
                  : '1px solid rgba(148,163,184,0.08)',
              }}
            />
          </Box>
        );
      })}

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: leftAxisWidth,
          borderLeft: '1px solid rgba(148,163,184,0.12)',
        }}
      />

      {segmentBounds.map(({ segment, startMinutes, endMinutes }, index) => {
        const isGap = segment.type === 'gap';
        const top = ((startMinutes - timelineStartMinutes) / 60) * pixelsPerHour;
        const rawHeight = ((endMinutes - startMinutes) / 60) * pixelsPerHour;
        const height = Math.max(rawHeight, isGap ? 28 : 48);

        return (
          <Box
            key={segment.key}
            sx={{
              position: 'absolute',
              top,
              left: leftAxisWidth + 12,
              right: 12,
              minHeight: height,
              borderRadius: 2.5,
              border: segment.hasConflict
                ? '1px solid rgba(248,113,113,0.62)'
                : segment.isStartLocked
                  ? '1px solid rgba(251,191,36,0.52)'
                  : isGap
                    ? '1px dashed rgba(251,191,36,0.42)'
                    : '1px solid rgba(56,189,248,0.26)',
              bgcolor: segment.hasConflict
                ? 'rgba(127,29,29,0.28)'
                : segment.isStartLocked
                  ? 'rgba(120,53,15,0.26)'
                  : isGap
                    ? 'rgba(113,63,18,0.14)'
                    : 'rgba(8,47,73,0.42)',
              px: 1,
              py: 0.85,
              boxShadow: segment.hasConflict
                ? '0 0 0 1px rgba(248,113,113,0.12)'
                : 'none',
              overflow: 'hidden',
            }}
          >
            <Stack spacing={0.5}>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                justifyContent="space-between"
                sx={{ minWidth: 0 }}
              >
                <Typography
                  noWrap
                  sx={{
                    color: isGap ? '#fde68a' : '#f8fafc',
                    fontWeight: 950,
                    fontSize: '0.88rem',
                    minWidth: 0,
                  }}
                >
                  {segment.referenceLabel}
                </Typography>

                {!isGap && (
                  <Stack direction="row" spacing={0.55} alignItems="center" sx={{ flexShrink: 0 }}>
                    {segment.groupKey && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          if (!segment.groupKey) return;
                          onEditGroup(
                            segment.groupKey,
                            segment.startDate,
                            segment.startTime,
                          );
                        }}
                        sx={{
                          minWidth: 0,
                          height: 22,
                          px: 0.8,
                          fontSize: '0.62rem',
                          fontWeight: 900,
                        }}
                      >
                        Ajustar
                      </Button>
                    )}

                    <Chip
                      size="small"
                      label={`#${index + 1}`}
                      variant="outlined"
                      sx={{
                        height: 20,
                        color: '#bae6fd',
                        borderColor: 'rgba(56,189,248,0.24)',
                        fontSize: '0.64rem',
                        flexShrink: 0,
                      }}
                    />
                  </Stack>
                )}
              </Stack>

              <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={`${formatTime(segment.startTime)} → ${formatTime(segment.endTime)}`}
                  variant="outlined"
                  sx={{
                    height: 20,
                    color: '#bae6fd',
                    borderColor: 'rgba(56,189,248,0.22)',
                    bgcolor: 'rgba(14,165,233,0.08)',
                    fontSize: '0.64rem',
                    fontWeight: 850,
                  }}
                />

                <Chip
                  size="small"
                  label={formatDurationMinutes(segment.durationMinutes)}
                  variant="outlined"
                  sx={{
                    height: 20,
                    color: isGap ? '#fde68a' : '#cbd5e1',
                    borderColor: isGap
                      ? 'rgba(251,191,36,0.34)'
                      : 'rgba(148,163,184,0.18)',
                    fontSize: '0.64rem',
                  }}
                />

                {segment.isStartLocked && (
                  <Chip
                    size="small"
                    label="Inicio fijado"
                    variant="outlined"
                    sx={{
                      height: 20,
                      color: '#fde68a',
                      borderColor: 'rgba(251,191,36,0.34)',
                      bgcolor: 'rgba(251,191,36,0.08)',
                      fontSize: '0.64rem',
                      fontWeight: 900,
                    }}
                  />
                )}

                {segment.isSplitSegment && (
                  <Chip
                    size="small"
                    label={`Tramo ${segment.segmentIndex}/${segment.totalSegments}`}
                    variant="outlined"
                    sx={{
                      height: 20,
                      color: '#ddd6fe',
                      borderColor: 'rgba(167,139,250,0.34)',
                      bgcolor: 'rgba(109,40,217,0.12)',
                      fontSize: '0.64rem',
                      fontWeight: 900,
                    }}
                  />
                )}

                {segment.hasConflict && (
                  <Chip
                    size="small"
                    label="Conflicto"
                    color="error"
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: '0.64rem',
                      fontWeight: 900,
                    }}
                  />
                )}
              </Stack>

              {segment.conflictMessage && (
                <Typography sx={{ color: '#fecaca', fontSize: '0.68rem' }}>
                  {segment.conflictMessage}
                </Typography>
              )}

              {segment.isStartLocked && segment.item?.locked_start_note && (
                <Typography sx={{ color: '#fde68a', fontSize: '0.68rem' }}>
                  Ajuste operativo: {segment.item.locked_start_note}
                </Typography>
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

function TrackingPlannedGroupCard({
  positionLabel,
  group,
  segment,
  isExpiredAdjustment,
  isSaving,
  isUnplanning,
  isUnplanningItem,
  isUpdatingItemDuration,
  isMovingToLockedStart,
  isResolvingLockedStartConflict,
  canMoveToLockedStart,
  canPrioritizeOwnLockedStart,
  moveToLockedStartLabel,
  prioritizeOwnLockedStartLabel,
  onSaveAdjustment,
  onClearAdjustment,
  onUnplan,
  onUnplanItem,
  onSaveItemDuration,
  onMoveToLockedStart,
  onPrioritizeOwnLockedStart,
}: {
  positionLabel: string;
  group: PlannedOrderGroup;
  segment: ProjectedPlanSegment;
  isExpiredAdjustment: boolean;
  isSaving: boolean;
  isUnplanning: boolean;
  isUnplanningItem: boolean;
  isUpdatingItemDuration: boolean;
  isMovingToLockedStart: boolean;
  isResolvingLockedStartConflict: boolean;
  canMoveToLockedStart: boolean;
  canPrioritizeOwnLockedStart: boolean;
  moveToLockedStartLabel: string | null;
  prioritizeOwnLockedStartLabel: string | null;
  onSaveAdjustment: (params: {
    group: PlannedOrderGroup;
    lockedStartDate: string;
    lockedStartTime: string;
    lockedStartNote: string;
  }) => void;
  onClearAdjustment: (group: PlannedOrderGroup) => void;
  onUnplan: (group: PlannedOrderGroup) => void;
  onUnplanItem: (group: PlannedOrderGroup, item: ProductionPlanItem) => void;
  onSaveItemDuration: (
    group: PlannedOrderGroup,
    item: ProductionPlanItem,
    durationMinutes: number,
  ) => void;
  onMoveToLockedStart: (group: PlannedOrderGroup) => void;
  onPrioritizeOwnLockedStart: (group: PlannedOrderGroup) => void;
}) {
  const lockedItem = group.items.find((item) => item.is_start_locked);
  const activeLockedItem = isExpiredAdjustment ? null : lockedItem;
  const [editorOpen, setEditorOpen] = useState(false);
  const [referenceEditorOpen, setReferenceEditorOpen] = useState(false);
  const [itemDurationDrafts, setItemDurationDrafts] = useState<Record<string, string>>({});
  const [lockedStartDate, setLockedStartDate] = useState('');
  const [lockedStartTime, setLockedStartTime] = useState('');
  const [lockedStartNote, setLockedStartNote] = useState('');

  const isBusy =
    isSaving ||
    isUnplanning ||
    isUnplanningItem ||
    isUpdatingItemDuration ||
    isMovingToLockedStart ||
    isResolvingLockedStartConflict;

  const handleOpenReferenceEditor = () => {
    setItemDurationDrafts(
      Object.fromEntries(
        group.items.map((item) => [
          item.id,
          String(resolvePlanItemDurationMinutes(item) || 0),
        ]),
      ),
    );
    setReferenceEditorOpen((current) => !current);
  };

  const handleOpenEditor = () => {
    setLockedStartDate(
      normalizeIsoDateValue(activeLockedItem?.locked_start_date) ||
        normalizeIsoDateValue(segment.startDate),
    );
    setLockedStartTime(
      activeLockedItem?.locked_start_time
        ? normalizePlanningTime(activeLockedItem.locked_start_time)
        : normalizePlanningTime(segment.startTime),
    );
    setLockedStartNote(activeLockedItem?.locked_start_note ?? '');
    setEditorOpen(true);
  };

  const projectedEndLabel =
    segment.endDate === segment.startDate
      ? formatTime(segment.endTime)
      : `${formatShortWeekDay(segment.endDate)} ${formatTime(segment.endTime)}`;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.75,
        border: segment.hasConflict
          ? '1px solid rgba(248,113,113,0.48)'
          : activeLockedItem
            ? '1px solid rgba(251,191,36,0.38)'
            : '1px solid rgba(56,189,248,0.20)',
        bgcolor: segment.hasConflict
          ? 'rgba(127,29,29,0.18)'
          : activeLockedItem
            ? 'rgba(120,53,15,0.16)'
            : 'rgba(8,47,73,0.22)',
        px: 1.2,
        py: 0.95,
      }}
    >
      <Stack spacing={0.85}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 0.85, md: 1.25 }}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Box
            sx={{
              minWidth: { md: 190 },
              width: { md: '27%' },
              maxWidth: { md: 260 },
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: '#7dd3fc',
                fontSize: '0.68rem',
                fontWeight: 950,
                letterSpacing: '0.055em',
                textTransform: 'uppercase',
                lineHeight: 1.2,
              }}
            >
              {positionLabel}
            </Typography>

            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
                fontSize: '0.98rem',
                wordBreak: 'break-word',
                lineHeight: 1.18,
                mt: 0.15,
              }}
            >
              {group.referenceLabel}
            </Typography>
          </Box>

          <Stack spacing={0.55} sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', lineHeight: 1.35 }}>
              Inicio proyectado:{' '}
              <strong>
                {formatShortWeekDay(segment.startDate)}{' '}
                {formatTime(segment.startTime)}
              </strong>{' '}
              → {projectedEndLabel}
            </Typography>

            <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={`${group.items.length} ${
                  group.items.length === 1 ? 'OF' : 'OFs'
                }`}
                variant="outlined"
                sx={{
                  height: 22,
                  color: '#bae6fd',
                  borderColor: 'rgba(56,189,248,0.24)',
                  bgcolor: 'rgba(14,165,233,0.08)',
                  fontSize: '0.66rem',
                  fontWeight: 850,
                }}
              />

              <Chip
                size="small"
                label={formatQuantity(group.totalQuantity, group.quantityUnit)}
                variant="outlined"
                sx={{
                  height: 22,
                  color: '#cbd5e1',
                  borderColor: 'rgba(148,163,184,0.18)',
                  fontSize: '0.66rem',
                }}
              />

              <Chip
                size="small"
                label={formatDurationMinutes(group.totalDurationMinutes)}
                variant="outlined"
                sx={{
                  height: 22,
                  color: '#cbd5e1',
                  borderColor: 'rgba(148,163,184,0.18)',
                  fontSize: '0.66rem',
                }}
              />

              {activeLockedItem && (
                <Chip
                  size="small"
                  label="Ajuste manual activo"
                  variant="outlined"
                  sx={{
                    height: 22,
                    color: '#fde68a',
                    borderColor: 'rgba(251,191,36,0.34)',
                    bgcolor: 'rgba(251,191,36,0.08)',
                    fontSize: '0.66rem',
                    fontWeight: 900,
                  }}
                />
              )}

              {segment.hasConflict && (
                <Chip
                  size="small"
                  label="Conflicto"
                  color="error"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: '0.66rem',
                    fontWeight: 900,
                  }}
                />
              )}
            </Stack>

            {activeLockedItem?.locked_start_note && (
              <Typography sx={{ color: '#fde68a', fontSize: '0.72rem', lineHeight: 1.35 }}>
                Nota del ajuste: {activeLockedItem.locked_start_note}
              </Typography>
            )}

            {segment.conflictMessage && (
              <Typography sx={{ color: '#fecaca', fontSize: '0.72rem', lineHeight: 1.35 }}>
                {segment.conflictMessage}
              </Typography>
            )}
          </Stack>

          <Stack
            direction="row"
            spacing={0.65}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            sx={{
              width: { xs: '100%', md: 300 },
              flexShrink: 0,
            }}
          >
            {canMoveToLockedStart && (
              <Button
                size="small"
                variant="outlined"
                color="info"
                onClick={() => onMoveToLockedStart(group)}
                disabled={isBusy}
                sx={{
                  minWidth: 128,
                  height: 30,
                  px: 1,
                  fontSize: '0.72rem',
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                {moveToLockedStartLabel ?? 'Mantener inicio'}
              </Button>
            )}

            {canPrioritizeOwnLockedStart && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => onPrioritizeOwnLockedStart(group)}
                disabled={isBusy}
                sx={{
                  minWidth: 128,
                  height: 30,
                  px: 1,
                  fontSize: '0.72rem',
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                {prioritizeOwnLockedStartLabel ?? 'Priorizar inicio'}
              </Button>
            )}

            <Button
              size="small"
              variant="outlined"
              onClick={handleOpenReferenceEditor}
              disabled={isBusy}
              sx={{
                minWidth: 128,
                height: 30,
                px: 1,
                fontSize: '0.72rem',
                fontWeight: 900,
                lineHeight: 1.2,
              }}
            >
              {referenceEditorOpen ? 'Ocultar edición' : 'Editar referencia'}
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={handleOpenEditor}
              disabled={isBusy}
              sx={{
                minWidth: 128,
                height: 30,
                px: 1,
                fontSize: '0.72rem',
                fontWeight: 900,
                lineHeight: 1.2,
              }}
            >
              Ajustar previsión
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => onUnplan(group)}
              disabled={isBusy}
              sx={{
                minWidth: 128,
                height: 30,
                px: 1,
                fontSize: '0.72rem',
                fontWeight: 900,
                lineHeight: 1.2,
              }}
            >
              A pendientes
            </Button>
          </Stack>
        </Stack>

        {referenceEditorOpen && (
          <Box
            sx={{
              borderRadius: 2.25,
              border: '1px solid rgba(148,163,184,0.16)',
              bgcolor: 'rgba(2,6,23,0.30)',
              px: 1.1,
              py: 0.95,
            }}
          >
            <Stack spacing={0.8}>
              <Stack spacing={0.25}>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                    fontSize: '0.82rem',
                  }}
                >
                  OFs incluidas en esta referencia
                </Typography>

                <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', lineHeight: 1.35 }}>
                  Desmarcar una OF la devuelve al panel de pendientes. No se
                  desactiva ni se elimina del snapshot pendiente.
                </Typography>
              </Stack>

              <Stack spacing={0.55}>
                {group.items.map((item) => {
                  const orderNumber =
                    item.manufacturing_order_number || 'OF sin número';
                  const canUnplanItem = group.items.length > 1 && !isBusy;

                  return (
                    <Paper
                      key={item.id}
                      elevation={0}
                      sx={{
                        borderRadius: 1.8,
                        border: '1px solid rgba(148,163,184,0.14)',
                        bgcolor: 'rgba(15,23,42,0.42)',
                        px: 0.85,
                        py: 0.55,
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={0.75}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Stack
                          direction="row"
                          spacing={0.65}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Checkbox
                            checked
                            size="small"
                            disabled={!canUnplanItem}
                            onChange={(event) => {
                              if (event.target.checked) return;

                              onUnplanItem(group, item);
                            }}
                            sx={{
                              p: 0.25,
                              color: '#38bdf8',
                              '&.Mui-checked': {
                                color: '#38bdf8',
                              },
                            }}
                          />

                          <Stack spacing={0.1} sx={{ minWidth: 0 }}>
                            <Typography
                              noWrap
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 900,
                                fontSize: '0.78rem',
                              }}
                            >
                              {orderNumber}
                            </Typography>

                            <Typography
                              noWrap
                              sx={{
                                color: '#94a3b8',
                                fontSize: '0.68rem',
                              }}
                            >
                              {item.customer_order_number
                                ? `Pedido cliente ${item.customer_order_number}`
                                : item.customer_number
                                  ? `Cliente ${item.customer_number}`
                                  : 'Sin cliente informado'}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0.55}
                          flexWrap="wrap"
                          useFlexGap
                          alignItems="center"
                          justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                        >
                          <Chip
                            size="small"
                            label={formatQuantity(
                              item.planned_quantity_value,
                              item.quantity_unit,
                            )}
                            variant="outlined"
                            sx={{
                              height: 20,
                              color: '#cbd5e1',
                              borderColor: 'rgba(148,163,184,0.18)',
                              fontSize: '0.62rem',
                            }}
                          />

                          <TextField
                            label="Duración min"
                            type="number"
                            value={
                              itemDurationDrafts[item.id] ??
                              String(resolvePlanItemDurationMinutes(item) || 0)
                            }
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setItemDurationDrafts((current) => ({
                                ...current,
                                [item.id]: nextValue,
                              }));
                            }}
                            size="small"
                            disabled={isBusy}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{
                              width: 112,
                              '& .MuiInputBase-input': {
                                py: 0.65,
                                fontSize: '0.74rem',
                                fontWeight: 800,
                              },
                              '& .MuiInputLabel-root': {
                                fontSize: '0.68rem',
                              },
                            }}
                          />

                          <Button
                            size="small"
                            variant="outlined"
                            disabled={
                              isBusy ||
                              Number(itemDurationDrafts[item.id] ?? resolvePlanItemDurationMinutes(item)) ===
                                resolvePlanItemDurationMinutes(item) ||
                              Number(itemDurationDrafts[item.id] ?? 0) < 0 ||
                              !Number.isFinite(Number(itemDurationDrafts[item.id] ?? 0))
                            }
                            onClick={() => {
                              const durationMinutes = Math.max(
                                0,
                                Math.round(Number(itemDurationDrafts[item.id] ?? 0)),
                              );

                              onSaveItemDuration(group, item, durationMinutes);
                            }}
                            sx={{
                              minWidth: 78,
                              height: 30,
                              px: 0.9,
                              fontSize: '0.68rem',
                              fontWeight: 900,
                            }}
                          >
                            Guardar
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>

              {group.items.length <= 1 && (
                <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                  Para retirar la última OF de la referencia utiliza la acción
                  principal “A pendientes”.
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {editorOpen && (
          <Box
            sx={{
              borderRadius: 2.25,
              border: '1px solid rgba(148,163,184,0.16)',
              bgcolor: 'rgba(2,6,23,0.36)',
              px: 1.1,
              py: 0.95,
            }}
          >
            <Stack spacing={1}>
              <Stack spacing={0.3}>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                    fontSize: '0.82rem',
                  }}
                >
                  Ajuste manual de previsión
                </Typography>

                <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem', lineHeight: 1.4 }}>
                  Corrige el comienzo esperado de esta referencia. El sistema
                  recalculará automáticamente la proyección de las referencias
                  posteriores sin modificar el orden de la cola.
                </Typography>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={0.85}
              >
                <TextField
                  label="Fecha revisada"
                  type="date"
                  value={lockedStartDate}
                  onChange={(event) => setLockedStartDate(event.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Hora revisada"
                  type="time"
                  value={lockedStartTime}
                  onChange={(event) => setLockedStartTime(event.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <TextField
                label="Motivo o nota"
                value={lockedStartNote}
                onChange={(event) => setLockedStartNote(event.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={2}
                inputProps={{ maxLength: 1000 }}
                helperText="Opcional. Máximo 1.000 caracteres."
              />

              <Stack
                direction="row"
                spacing={0.75}
                justifyContent="flex-end"
                flexWrap="wrap"
                useFlexGap
              >
                <Button
                  size="small"
                  onClick={() => setEditorOpen(false)}
                  disabled={isSaving}
                  sx={{ fontWeight: 850 }}
                >
                  Cancelar
                </Button>

                {activeLockedItem && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={() => {
                      setEditorOpen(false);
                      onClearAdjustment(group);
                    }}
                    disabled={isSaving}
                    sx={{ fontWeight: 850 }}
                  >
                    Retirar ajuste
                  </Button>
                )}

                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setEditorOpen(false);
                    onSaveAdjustment({
                      group,
                      lockedStartDate,
                      lockedStartTime,
                      lockedStartNote,
                    });
                  }}
                  disabled={isSaving}
                  sx={{ fontWeight: 900 }}
                >
                  Guardar ajuste
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function WeeklyProductionTimelineBoard({
  weekDates,
  lines,
  planItems,
  pixelsPerHour,
  markerStepHours,
  lineColumnWidth,
  draggedPendingGroupKey,
  draggedPlannedGroupKey,
  pendingPanelOpen,
  pendingOrderGroupsCount,
  onRequestPendingDrop,
  onRequestPlannedGroupDrop,
  onPlannedGroupDragStart,
  onPlannedGroupDragEnd,
  onOpenLineTracking,
  onTogglePendingPanel,
}: {
  weekDates: string[];
  lines: ProductionLineConfig[];
  planItems: ProductionPlanItem[];
  pixelsPerHour: number;
  markerStepHours: number;
  lineColumnWidth: number;
  draggedPendingGroupKey: string | null;
  draggedPlannedGroupKey: string | null;
  pendingPanelOpen: boolean;
  pendingOrderGroupsCount: number;
  onRequestPendingDrop: (params: {
    groupKey: string | null;
    line: ProductionLineConfig;
    dateIso: string;
    suggestedStartTime: string;
    insertBeforeSequenceOrder: number | null;
  }) => void;
  onRequestPlannedGroupDrop: (params: {
    groupKey: string | null;
    line: ProductionLineConfig;
    insertBeforeSequenceOrder: number | null;
  }) => void;
  onPlannedGroupDragStart: (groupKey: string) => void;
  onPlannedGroupDragEnd: () => void;
  onOpenLineTracking: (line: ProductionLineConfig) => void;
  onTogglePendingPanel: () => void;
}) {
  const axisWidth = 118;

  const visibleWeekStartDate = weekDates[0];
  const visibleWeekEndDate = weekDates[6];
  const timelineStartMinutes = dateTimeToAbsoluteMinutes(visibleWeekStartDate, '00:00');
  const timelineEndMinutes = dateTimeToAbsoluteMinutes(
    addDaysToIsoDate(visibleWeekEndDate, 1),
    '00:00',
  );
  const totalTimelineMinutes = timelineEndMinutes - timelineStartMinutes;
  const boardHeight = Math.max((totalTimelineMinutes / 60) * pixelsPerHour, 720);
  const [currentTimeTick, setCurrentTimeTick] = useState(() => Date.now());
  const [weeklyPlannedGroupDropPreview, setWeeklyPlannedGroupDropPreview] =
    useState<WeeklyPlannedGroupDropPreview | null>(null);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTimeTick(Date.now());
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!draggedPlannedGroupKey) {
      setWeeklyPlannedGroupDropPreview(null);
    }
  }, [draggedPlannedGroupKey]);

  const currentDate = new Date(currentTimeTick);
  const currentDateIso = toLocalIsoDate(currentDate);
  const currentTime = minutesToTime(
    currentDate.getHours() * 60 + currentDate.getMinutes(),
  );
  const currentAbsoluteMinutes = dateTimeToAbsoluteMinutes(
    currentDateIso,
    currentTime,
  );
  const showCurrentTimeMarker =
    currentAbsoluteMinutes >= timelineStartMinutes &&
    currentAbsoluteMinutes <= timelineEndMinutes;
  const currentTimeMarkerTop =
    ((currentAbsoluteMinutes - timelineStartMinutes) / 60) * pixelsPerHour;
  const boardMinWidth = Math.max(980, axisWidth + lines.length * lineColumnWidth);
  const weeklyBoardContainerRef = useRef<HTMLDivElement | null>(null);
  const weeklyBoardHeaderScrollRef = useRef<HTMLDivElement | null>(null);
  const weeklyBoardFloatingHeaderScrollRef = useRef<HTMLDivElement | null>(null);
  const weeklyBoardBodyScrollRef = useRef<HTMLDivElement | null>(null);
  const [floatingWeeklyHeader, setFloatingWeeklyHeader] = useState({
    visible: false,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    const updateFloatingHeader = () => {
      const container = weeklyBoardContainerRef.current;

      if (!container) {
        setFloatingWeeklyHeader((current) =>
          current.visible
            ? {
                visible: false,
                left: 0,
                width: 0,
              }
            : current,
        );
        return;
      }

      const rect = container.getBoundingClientRect();
      const shouldShow = rect.top < 0 && rect.bottom > 150;
      const nextLeft = Math.max(rect.left, 0);
      const nextWidth = Math.max(rect.width, 0);

      setFloatingWeeklyHeader((current) => {
        if (
          current.visible === shouldShow &&
          Math.abs(current.left - nextLeft) < 1 &&
          Math.abs(current.width - nextWidth) < 1
        ) {
          return current;
        }

        return {
          visible: shouldShow,
          left: nextLeft,
          width: nextWidth,
        };
      });
    };

    updateFloatingHeader();

    window.addEventListener('scroll', updateFloatingHeader, true);
    window.addEventListener('resize', updateFloatingHeader);

    return () => {
      window.removeEventListener('scroll', updateFloatingHeader, true);
      window.removeEventListener('resize', updateFloatingHeader);
    };
  }, []);

  const scrollWeeklyBoardToCurrentTime = () => {
    if (!showCurrentTimeMarker) return;

    const body = weeklyBoardBodyScrollRef.current;

    if (!body) return;

    const bodyTop = body.getBoundingClientRect().top + window.scrollY;
    const viewportAnchorTop = Math.max(
      180,
      Math.round(window.innerHeight * 0.38),
    );
    const targetScrollTop = Math.max(
      bodyTop + currentTimeMarkerTop - viewportAnchorTop,
      0,
    );

    window.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth',
    });
  };

  const scrollWeeklyBoardToStart = () => {
    const container = weeklyBoardContainerRef.current;

    if (!container) return;

    const containerTop = container.getBoundingClientRect().top + window.scrollY;
    const targetScrollTop = Math.max(containerTop - 16, 0);

    window.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth',
    });
  };

  const syncWeeklyBoardHorizontalScroll = (scrollLeft: number) => {
    const header = weeklyBoardHeaderScrollRef.current;
    const floatingHeader = weeklyBoardFloatingHeaderScrollRef.current;
    const body = weeklyBoardBodyScrollRef.current;

    if (header && header.scrollLeft !== scrollLeft) {
      header.scrollLeft = scrollLeft;
    }

    if (floatingHeader && floatingHeader.scrollLeft !== scrollLeft) {
      floatingHeader.scrollLeft = scrollLeft;
    }

    if (body && body.scrollLeft !== scrollLeft) {
      body.scrollLeft = scrollLeft;
    }
  };

  const handleWeeklyBoardHeaderScroll = () => {
    const header = weeklyBoardHeaderScrollRef.current;
    if (!header) return;

    syncWeeklyBoardHorizontalScroll(header.scrollLeft);
  };

  const handleWeeklyBoardFloatingHeaderScroll = () => {
    const floatingHeader = weeklyBoardFloatingHeaderScrollRef.current;
    if (!floatingHeader) return;

    syncWeeklyBoardHorizontalScroll(floatingHeader.scrollLeft);
  };

  const handleWeeklyBoardBodyScroll = () => {
    const body = weeklyBoardBodyScrollRef.current;
    if (!body) return;

    syncWeeklyBoardHorizontalScroll(body.scrollLeft);
  };

  const hourMarkers = Array.from(
    { length: Math.floor(totalTimelineMinutes / (markerStepHours * 60)) + 1 },
    (_, index) => timelineStartMinutes + index * markerStepHours * 60,
  );

  const weeklyProjectionReference = {
    dateIso: weekDates[0],
    time: '06:00',
  };

  const buildPreviewLineItemsForWeeklyPlannedGroupDrop = ({
    lineItems,
    movingGroupKey,
    insertBeforeSequenceOrder,
  }: {
    lineItems: ProductionPlanItem[];
    movingGroupKey: string;
    insertBeforeSequenceOrder: number | null;
  }) => {
    const currentGroups = buildPlannedOrderGroups(lineItems);
    const movingGroupIndex = currentGroups.findIndex(
      (group) => group.key === movingGroupKey,
    );

    if (movingGroupIndex === -1) {
      return lineItems;
    }

    const reorderedGroups = [...currentGroups];
    const [movingGroup] = reorderedGroups.splice(movingGroupIndex, 1);

    if (!movingGroup) {
      return lineItems;
    }

    let insertIndex = reorderedGroups.length;

    if (insertBeforeSequenceOrder !== null) {
      const targetIndex = reorderedGroups.findIndex((candidateGroup) =>
        candidateGroup.items.some(
          (item) =>
            resolvePlanItemOrderValue(item) >= insertBeforeSequenceOrder,
        ),
      );

      if (targetIndex >= 0) {
        insertIndex = targetIndex;
      }
    }

    reorderedGroups.splice(insertIndex, 0, movingGroup);

    let nextSequenceOrder = 1;

    return reorderedGroups.flatMap((group) =>
      group.items.map((item) => {
        const currentSequenceOrder = nextSequenceOrder;

        const nextItem = {
          ...item,
          priority: currentSequenceOrder,
          line_sequence_order: currentSequenceOrder,
        };

        nextSequenceOrder += 1;

        return nextItem;
      }),
    );
  };

  const weeklyLinePlans = lines.map((line) => {
    const lineItems = planItems
      .filter((item) => item.production_line_config_id === line.id)
      .sort((a, b) => {
        const orderDiff =
          resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b);

        if (orderDiff !== 0) return orderDiff;

        return (
          normalizeIsoDateValue(a.planned_date).localeCompare(
            normalizeIsoDateValue(b.planned_date),
          ) ||
          formatTime(a.planned_start_time).localeCompare(
            formatTime(b.planned_start_time),
          ) ||
          a.id.localeCompare(b.id)
        );
      });

    const effectiveLineItems = lineItems.map((item) =>
      sanitizePlanItemForLiveProjection(
        item,
        weeklyProjectionReference.dateIso,
        weeklyProjectionReference.time,
      ),
    );

    const previewLineItems =
      weeklyPlannedGroupDropPreview?.lineId === line.id
        ? buildPreviewLineItemsForWeeklyPlannedGroupDrop({
            lineItems: effectiveLineItems,
            movingGroupKey: weeklyPlannedGroupDropPreview.groupKey,
            insertBeforeSequenceOrder:
              weeklyPlannedGroupDropPreview.insertBeforeSequenceOrder,
          })
        : effectiveLineItems;

    return {
      line,
      baseStartDate: weeklyProjectionReference.dateIso,
      lineStartTime: weeklyProjectionReference.time,
      projectedPlan: buildProjectedLinePlan({
        line,
        items: previewLineItems,
        baseStartDate: weeklyProjectionReference.dateIso,
        baseStartTime: weeklyProjectionReference.time,
      }),
      hasQueue: lineItems.length > 0,
    };
  });

  if (lines.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        No hay líneas activas para el filtro seleccionado.
      </Alert>
    );
  }

  return (
    <Box ref={weeklyBoardContainerRef} sx={{ pb: 1 }}>
      {floatingWeeklyHeader.visible && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: floatingWeeklyHeader.left,
            width: floatingWeeklyHeader.width,
            zIndex: 1400,
            bgcolor: 'rgba(2,6,23,0.98)',
            borderBottom: '1px solid rgba(56,189,248,0.22)',
            boxShadow: '0 18px 34px rgba(2,6,23,0.72)',
            py: 0.75,
            pointerEvents: 'auto',
          }}
        >
          <Button
            size="small"
            variant={pendingPanelOpen ? 'outlined' : 'contained'}
            onClick={onTogglePendingPanel}
            sx={{
              position: 'absolute',
              top: 8,
              right: 10,
              zIndex: 2,
              height: 28,
              px: 1.2,
              fontSize: '0.68rem',
              fontWeight: 950,
              bgcolor: pendingPanelOpen ? 'rgba(15,23,42,0.96)' : undefined,
              borderColor: pendingPanelOpen ? 'rgba(56,189,248,0.42)' : undefined,
              color: pendingPanelOpen ? '#bae6fd' : undefined,
              boxShadow: '0 10px 24px rgba(2,6,23,0.45)',
              '&:hover': {
                borderColor: 'rgba(56,189,248,0.68)',
                bgcolor: pendingPanelOpen ? 'rgba(8,47,73,0.92)' : undefined,
              },
            }}
          >
            {pendingPanelOpen
              ? 'Ocultar pendientes'
              : `Pendientes (${pendingOrderGroupsCount})`}
          </Button>

          <Box
            ref={weeklyBoardFloatingHeaderScrollRef}
            onScroll={handleWeeklyBoardFloatingHeaderScroll}
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <Box
              sx={{
                minWidth: boardMinWidth,
                display: 'grid',
                gridTemplateColumns: `${axisWidth}px repeat(${lines.length}, minmax(${lineColumnWidth}px, 1fr))`,
                gap: 1,
                px: 0,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid rgba(148,163,184,0.14)',
                  bgcolor: 'rgba(15,23,42,0.72)',
                  p: 1,
                }}
              >
                <Stack spacing={0.7}>
                  <Box>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 850 }}>
                      Tiempo
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.68rem' }}>
                      Semana continua
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap>
                    {showCurrentTimeMarker && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={scrollWeeklyBoardToCurrentTime}
                        sx={{
                          minWidth: 0,
                          height: 24,
                          px: 0.9,
                          color: '#fed7aa',
                          borderColor: 'rgba(251,146,60,0.42)',
                          bgcolor: 'rgba(154,52,18,0.10)',
                          fontSize: '0.66rem',
                          fontWeight: 900,
                          '&:hover': {
                            borderColor: 'rgba(251,146,60,0.70)',
                            bgcolor: 'rgba(154,52,18,0.18)',
                          },
                        }}
                      >
                        Ir a ahora
                      </Button>
                    )}

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={scrollWeeklyBoardToStart}
                      sx={{
                        minWidth: 0,
                        height: 24,
                        px: 0.9,
                        color: '#bae6fd',
                        borderColor: 'rgba(56,189,248,0.34)',
                        bgcolor: 'rgba(8,47,73,0.18)',
                        fontSize: '0.66rem',
                        fontWeight: 900,
                        '&:hover': {
                          borderColor: 'rgba(56,189,248,0.58)',
                          bgcolor: 'rgba(8,47,73,0.30)',
                        },
                      }}
                    >
                      Ir al inicio
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              {weeklyLinePlans.map(({ line, baseStartDate, lineStartTime, projectedPlan }) => (
                <Paper
                  key={`weekly-board-floating-line-header-${line.id}`}
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border:
                      projectedPlan.conflictsCount > 0
                        ? '1px solid rgba(248,113,113,0.42)'
                        : '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.92)',
                    p: 1,
                  }}
                >
                  <Stack spacing={0.4}>
                    <Typography
                      noWrap
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 950,
                        fontSize: '0.82rem',
                      }}
                    >
                      {buildLineLabel(line)}
                    </Typography>

                    <Typography noWrap sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                      {line.code}
                      {line.process_group ? ` · ${line.process_group}` : ''}
                    </Typography>

                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={formatDurationMinutes(projectedPlan.totalProductionMinutes)}
                        variant="outlined"
                        sx={{
                          height: 21,
                          color: '#bae6fd',
                          borderColor: 'rgba(56,189,248,0.22)',
                          fontSize: '0.66rem',
                        }}
                      />

                      <Chip
                        size="small"
                        label={`Inicio ${formatShortWeekDay(baseStartDate)} ${lineStartTime}`}
                        variant="outlined"
                        sx={{
                          height: 21,
                          color: '#cbd5e1',
                          borderColor: 'rgba(148,163,184,0.18)',
                          fontSize: '0.64rem',
                        }}
                      />

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onOpenLineTracking(line)}
                        sx={{
                          minWidth: 0,
                          height: 22,
                          px: 0.8,
                          fontSize: '0.62rem',
                          fontWeight: 900,
                          color: '#67e8f9',
                          borderColor: 'rgba(34,211,238,0.38)',
                          bgcolor: 'rgba(8,145,178,0.10)',
                          '&:hover': {
                            borderColor: 'rgba(34,211,238,0.62)',
                            bgcolor: 'rgba(8,145,178,0.18)',
                          },
                        }}
                      >
                        Seguimiento
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <Box
        ref={weeklyBoardHeaderScrollRef}
        onScroll={handleWeeklyBoardHeaderScroll}
        sx={{
          position: 'sticky',
          top: 8,
          zIndex: 30,
          overflowX: 'auto',
          overflowY: 'hidden',
          mb: 1,
          bgcolor: 'rgba(2,6,23,0.98)',
          py: 0.75,
          boxShadow: '0 14px 28px rgba(2,6,23,0.65)',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <Box
          sx={{
            minWidth: boardMinWidth,
            display: 'grid',
            gridTemplateColumns: `${axisWidth}px repeat(${lines.length}, minmax(${lineColumnWidth}px, 1fr))`,
            gap: 1,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: '1px solid rgba(148,163,184,0.14)',
              bgcolor: 'rgba(15,23,42,0.92)',
              p: 1,
            }}
          >
            <Stack spacing={0.7}>
              <Box>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 850 }}>
                  Tiempo
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.68rem' }}>
                  Semana continua
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap>
                {showCurrentTimeMarker && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={scrollWeeklyBoardToCurrentTime}
                    sx={{
                      minWidth: 0,
                      height: 24,
                      px: 0.9,
                      color: '#fed7aa',
                      borderColor: 'rgba(251,146,60,0.42)',
                      bgcolor: 'rgba(154,52,18,0.10)',
                      fontSize: '0.66rem',
                      fontWeight: 900,
                      '&:hover': {
                        borderColor: 'rgba(251,146,60,0.70)',
                        bgcolor: 'rgba(154,52,18,0.18)',
                      },
                    }}
                  >
                    Ir a ahora
                  </Button>
                )}

                <Button
                  size="small"
                  variant="outlined"
                  onClick={scrollWeeklyBoardToStart}
                  sx={{
                    minWidth: 0,
                    height: 24,
                    px: 0.9,
                    color: '#bae6fd',
                    borderColor: 'rgba(56,189,248,0.34)',
                    bgcolor: 'rgba(8,47,73,0.18)',
                    fontSize: '0.66rem',
                    fontWeight: 900,
                    '&:hover': {
                      borderColor: 'rgba(56,189,248,0.58)',
                      bgcolor: 'rgba(8,47,73,0.30)',
                    },
                  }}
                >
                  Ir al inicio
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {weeklyLinePlans.map(({ line, baseStartDate, lineStartTime, projectedPlan }) => (
            <Paper
              key={`weekly-board-line-header-${line.id}`}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border:
                  projectedPlan.conflictsCount > 0
                    ? '1px solid rgba(248,113,113,0.42)'
                    : '1px solid rgba(148,163,184,0.14)',
                bgcolor: 'rgba(15,23,42,0.72)',
                p: 1,
              }}
            >
              <Stack spacing={0.4}>
                <Typography
                  noWrap
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 950,
                    fontSize: '0.82rem',
                  }}
                >
                  {buildLineLabel(line)}
                </Typography>

                <Typography noWrap sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                  {line.code}
                  {line.process_group ? ` · ${line.process_group}` : ''}
                </Typography>

                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={formatDurationMinutes(projectedPlan.totalProductionMinutes)}
                    variant="outlined"
                    sx={{
                      height: 21,
                      color: '#bae6fd',
                      borderColor: 'rgba(56,189,248,0.22)',
                      fontSize: '0.66rem',
                    }}
                  />

                  <Chip
                    size="small"
                    label={`Inicio ${formatShortWeekDay(baseStartDate)} ${lineStartTime}`}
                    variant="outlined"
                    sx={{
                      height: 21,
                      color: '#cbd5e1',
                      borderColor: 'rgba(148,163,184,0.18)',
                      fontSize: '0.64rem',
                    }}
                  />

                  {projectedPlan.conflictsCount > 0 && (
                    <Chip
                      size="small"
                      label={`${projectedPlan.conflictsCount} conflicto${projectedPlan.conflictsCount === 1 ? '' : 's'}`}
                      color="error"
                      variant="outlined"
                      sx={{
                        height: 21,
                        fontSize: '0.66rem',
                        fontWeight: 900,
                      }}
                    />
                  )}
                </Stack>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onOpenLineTracking(line)}
                  sx={{
                    alignSelf: 'flex-start',
                    minWidth: 0,
                    mt: 0.25,
                    px: 0.9,
                    py: 0.25,
                    fontSize: '0.66rem',
                    fontWeight: 900,
                  }}
                >
                  Seguimiento
                </Button>
              </Stack>
            </Paper>
          ))}
        </Box>
      </Box>

      <Box
        ref={weeklyBoardBodyScrollRef}
        onScroll={handleWeeklyBoardBodyScroll}
        sx={{
          overflowX: 'auto',
          overflowY: 'visible',
          pb: 1,
        }}
      >
        <Box
          sx={{
            minWidth: boardMinWidth,
          }}
        >
          <Box
            sx={{
              position: 'relative',
            minHeight: boardHeight,
            borderRadius: 3,
            border: '1px solid rgba(148,163,184,0.14)',
            bgcolor: 'rgba(2,6,23,0.42)',
            overflow: 'hidden',
          }}
        >
          {hourMarkers.map((markerMinutes) => {
            const markerDateTime = absoluteMinutesToDateTime(markerMinutes);
            const top = ((markerMinutes - timelineStartMinutes) / 60) * pixelsPerHour;
            const isDayStart = markerDateTime.time === '00:00';

            return (
              <Box key={`weekly-board-marker-${markerMinutes}`}>
                <Box
                  sx={{
                    position: 'absolute',
                    top,
                    left: 0,
                    width: axisWidth,
                    px: 1,
                    transform:
                      markerMinutes === timelineStartMinutes
                        ? 'translateY(0)'
                        : 'translateY(-50%)',
                    zIndex: 2,
                  }}
                >
                  <Typography
                    sx={{
                      color: isDayStart ? '#bae6fd' : '#64748b',
                      fontSize: isDayStart ? '0.72rem' : '0.66rem',
                      fontWeight: isDayStart ? 950 : 750,
                      lineHeight: 1.15,
                    }}
                  >
                    {isDayStart
                      ? formatShortWeekDay(markerDateTime.dateIso)
                      : markerDateTime.time}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: 'absolute',
                    top,
                    left: axisWidth,
                    right: 0,
                    borderTop: isDayStart
                      ? '1px solid rgba(56,189,248,0.28)'
                      : '1px solid rgba(148,163,184,0.08)',
                  }}
                />
              </Box>
            );
          })}

          {showCurrentTimeMarker && (
            <Box
              sx={{
                position: 'absolute',
                top: currentTimeMarkerTop,
                left: 0,
                right: 0,
                zIndex: 4,
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  width: axisWidth,
                  px: 1,
                  transform: 'translateY(-50%)',
                }}
              >
                <Chip
                  size="small"
                  label={`Ahora ${currentTime}`}
                  sx={{
                    height: 22,
                    color: '#fff7ed',
                    borderColor: 'rgba(251,146,60,0.5)',
                    bgcolor: 'rgba(154,52,18,0.88)',
                    fontSize: '0.64rem',
                    fontWeight: 950,
                  }}
                  variant="outlined"
                />
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  left: axisWidth,
                  right: 0,
                  borderTop: '2px solid rgba(251,146,60,0.92)',
                  boxShadow: '0 0 0 1px rgba(251,146,60,0.16), 0 0 18px rgba(251,146,60,0.20)',
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              gridTemplateColumns: `${axisWidth}px repeat(${lines.length}, minmax(${lineColumnWidth}px, 1fr))`,
              gap: 1,
            }}
          >
            <Box
              sx={{
                borderRight: '1px solid rgba(148,163,184,0.12)',
                bgcolor: 'rgba(2,6,23,0.18)',
              }}
            />

            {weeklyLinePlans.map(({ line, projectedPlan, hasQueue }) => {
              const visibleSegments = projectedPlan.segments
                .map((segment) => {
                      const rawStartMinutes = dateTimeToAbsoluteMinutes(
                        segment.startDate,
                        segment.startTime,
                      );
                      const rawEndMinutes = dateTimeToAbsoluteMinutes(
                        segment.endDate,
                        segment.endTime,
                      );
                      const isZeroDurationProduction =
                        segment.type === 'production' && segment.durationMinutes <= 0;
                      const clippedStartMinutes = Math.max(rawStartMinutes, timelineStartMinutes);
                      const clippedEndMinutes = isZeroDurationProduction
                        ? clippedStartMinutes
                        : Math.min(rawEndMinutes, timelineEndMinutes);

                      return {
                        segment,
                        startMinutes: clippedStartMinutes,
                        endMinutes: clippedEndMinutes,
                        isOutside: isZeroDurationProduction
                          ? rawStartMinutes < timelineStartMinutes ||
                            rawStartMinutes >= timelineEndMinutes
                          : rawEndMinutes <= timelineStartMinutes ||
                            rawStartMinutes >= timelineEndMinutes,
                      };
                    })
                    .filter(
                      (item) =>
                        !item.isOutside &&
                        item.segment.type === 'production',
                    );

              return (
                <Box
                  key={`weekly-board-line-column-${line.id}`}
                  onDragOver={(event) => {
                    if (!(draggedPendingGroupKey || draggedPlannedGroupKey)) {
                      return;
                    }

                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';

                    if (draggedPendingGroupKey) {
                      setWeeklyPlannedGroupDropPreview(null);
                      return;
                    }

                    if (!draggedPlannedGroupKey) {
                      return;
                    }

                    const hasDraggedGroupInLine = visibleSegments.some(
                      ({ segment }) => segment.groupKey === draggedPlannedGroupKey,
                    );

                    if (!hasDraggedGroupInLine) {
                      setWeeklyPlannedGroupDropPreview(null);
                      return;
                    }

                    const rect = event.currentTarget.getBoundingClientRect();
                    const relativeY = Math.max(0, event.clientY - rect.top);
                    const rawDroppedMinutes =
                      timelineStartMinutes + (relativeY / pixelsPerHour) * 60;
                    const snappedDroppedMinutes =
                      Math.round(rawDroppedMinutes / 15) * 15;
                    const minimumAllowedDropMinutes =
                      dateTimeToAbsoluteMinutes(
                        weeklyProjectionReference.dateIso,
                        weeklyProjectionReference.time,
                      );
                    const effectiveDroppedMinutes = Math.max(
                      snappedDroppedMinutes,
                      minimumAllowedDropMinutes,
                    );
                    const insertBeforeSegment = visibleSegments.find(
                      ({ segment, startMinutes }) =>
                        segment.type === 'production' &&
                        Boolean(segment.item) &&
                        segment.groupKey !== draggedPlannedGroupKey &&
                        startMinutes > effectiveDroppedMinutes,
                    );
                    const insertBeforeSequenceOrder = insertBeforeSegment?.segment.item
                      ? resolvePlanItemOrderValue(insertBeforeSegment.segment.item)
                      : null;

                    setWeeklyPlannedGroupDropPreview((current) => {
                      const nextPreview = {
                        lineId: line.id,
                        groupKey: draggedPlannedGroupKey,
                        insertBeforeSequenceOrder,
                      };

                      if (
                        current?.lineId === nextPreview.lineId &&
                        current?.groupKey === nextPreview.groupKey &&
                        current?.insertBeforeSequenceOrder ===
                          nextPreview.insertBeforeSequenceOrder
                      ) {
                        return current;
                      }

                      return nextPreview;
                    });
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setWeeklyPlannedGroupDropPreview(null);

                    const rect = event.currentTarget.getBoundingClientRect();
                    const relativeY = Math.max(0, event.clientY - rect.top);
                    const rawDroppedMinutes =
                      timelineStartMinutes + (relativeY / pixelsPerHour) * 60;
                    const snappedDroppedMinutes =
                      Math.round(rawDroppedMinutes / 15) * 15;
                    const minimumAllowedDropMinutes =
                      dateTimeToAbsoluteMinutes(
                        weeklyProjectionReference.dateIso,
                        weeklyProjectionReference.time,
                      );
                    const effectiveDroppedMinutes = Math.max(
                      snappedDroppedMinutes,
                      minimumAllowedDropMinutes,
                    );
                    const droppedDateTime =
                      absoluteMinutesToDateTime(effectiveDroppedMinutes);
                    const insertBeforeSegment = visibleSegments.find(
                      ({ segment, startMinutes }) =>
                        segment.type === 'production' &&
                        Boolean(segment.item) &&
                        segment.groupKey !== draggedPlannedGroupKey &&
                        startMinutes > effectiveDroppedMinutes,
                    );
                    const insertBeforeSequenceOrder = insertBeforeSegment?.segment.item
                      ? resolvePlanItemOrderValue(insertBeforeSegment.segment.item)
                      : null;

                    const droppedGroupKey =
                      event.dataTransfer.getData('text/plain') ||
                      draggedPendingGroupKey ||
                      draggedPlannedGroupKey;

                    if (draggedPendingGroupKey) {
                      onRequestPendingDrop({
                        groupKey: droppedGroupKey,
                        line,
                        dateIso: droppedDateTime.dateIso,
                        suggestedStartTime: droppedDateTime.time,
                        insertBeforeSequenceOrder,
                      });

                      return;
                    }

                    if (draggedPlannedGroupKey) {
                      onRequestPlannedGroupDrop({
                        groupKey: droppedGroupKey,
                        line,
                        insertBeforeSequenceOrder,
                      });
                    }
                  }}
                  sx={{
                    position: 'relative',
                    minHeight: boardHeight,
                    borderLeft: '1px solid rgba(148,163,184,0.08)',
                    bgcolor: draggedPendingGroupKey || draggedPlannedGroupKey
                      ? 'rgba(14,165,233,0.04)'
                      : 'transparent',
                    transition: 'background 0.18s ease',
                  }}
                >
                  {!hasQueue && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 10,
                        right: 10,
                        borderRadius: 2.5,
                        border: '1px dashed rgba(56,189,248,0.26)',
                        bgcolor: 'rgba(8,47,73,0.12)',
                        px: 1,
                        py: 1.1,
                        pointerEvents: 'none',
                      }}
                    >
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem', fontWeight: 800 }}>
                        Línea vacía
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.68rem' }}>
                        Suelta un pedido para crear el primer inicio.
                      </Typography>
                    </Box>
                  )}

                  {visibleSegments.map(({ segment, startMinutes, endMinutes }, index) => {
                    const isGap = segment.type === 'gap';
                    const isZeroDurationProduction =
                      segment.type === 'production' && segment.durationMinutes <= 0;
                    const top = ((startMinutes - timelineStartMinutes) / 60) * pixelsPerHour;
                    const rawHeight = ((endMinutes - startMinutes) / 60) * pixelsPerHour;
                    const height = isZeroDurationProduction
                      ? 28
                      : Math.max(rawHeight, 1);
                    const showFullContent = height >= 34;
                    const showCompactContent = height >= 16;
                    const isDraggedPlannedPreviewSegment =
                      segment.type === 'production' &&
                      Boolean(segment.groupKey) &&
                      segment.groupKey === draggedPlannedGroupKey;

                    return (
                      <Box
                        key={`weekly-board-segment-${line.id}-${segment.key}-${index}`}
                        draggable={segment.type === 'production' && Boolean(segment.groupKey)}
                        onDragStart={(event) => {
                          if (segment.type !== 'production' || !segment.groupKey) return;

                          event.stopPropagation();
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', segment.groupKey);
                          onPlannedGroupDragStart(segment.groupKey);
                        }}
                        onDragEnd={(event) => {
                          event.stopPropagation();
                          setWeeklyPlannedGroupDropPreview(null);
                          onPlannedGroupDragEnd();
                        }}
                        sx={{
                          position: 'absolute',
                          top,
                          left: 8,
                          right: 8,
                          height,
                          opacity: isDraggedPlannedPreviewSegment ? 0.46 : 1,
                          zIndex: isDraggedPlannedPreviewSegment ? 6 : 3,
                          transition:
                            'top 0.14s ease, height 0.14s ease, opacity 0.12s ease, border-color 0.18s ease, background 0.18s ease',
                          borderRadius: 2.3,
                          border: segment.hasConflict
                            ? '1px solid rgba(248,113,113,0.62)'
                            : isZeroDurationProduction
                              ? '1px dashed rgba(248,250,252,0.34)'
                              : segment.isStartLocked
                                ? '1px solid rgba(251,191,36,0.52)'
                                : isGap
                                  ? '1px dashed rgba(251,191,36,0.36)'
                                  : '1px solid rgba(56,189,248,0.28)',
                          bgcolor: segment.hasConflict
                            ? 'rgba(127,29,29,0.30)'
                            : isZeroDurationProduction
                              ? 'rgba(15,23,42,0.82)'
                              : segment.isStartLocked
                                ? 'rgba(120,53,15,0.28)'
                                : isGap
                                  ? 'rgba(113,63,18,0.12)'
                                  : 'rgba(8,47,73,0.46)',
                          px: 1,
                          py: showFullContent ? 0.75 : 0.25,
                          overflow: 'hidden',
                          boxShadow: segment.hasConflict
                            ? '0 0 0 1px rgba(248,113,113,0.12)'
                            : 'none',
                          cursor: segment.type === 'production' ? 'grab' : 'default',
                          '&:active': {
                            cursor: segment.type === 'production' ? 'grabbing' : 'default',
                          },
                        }}
                      >
                        {showFullContent ? (
                          <Stack spacing={0.45}>
                            <Stack
                              direction="row"
                              spacing={0.7}
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ minWidth: 0 }}
                            >
                              <Typography
                                noWrap
                                sx={{
                                  color: isGap ? '#fde68a' : '#f8fafc',
                                  fontWeight: 950,
                                  fontSize: '0.78rem',
                                  minWidth: 0,
                                }}
                              >
                                {segment.referenceLabel}
                              </Typography>

                              {!isGap && (
                                <Chip
                                  size="small"
                                  label={`#${index + 1}`}
                                  variant="outlined"
                                  sx={{
                                    height: 19,
                                    color: '#bae6fd',
                                    borderColor: 'rgba(56,189,248,0.22)',
                                    fontSize: '0.61rem',
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Stack>

                            <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                label={`${formatShortWeekDay(segment.startDate)} ${formatTime(segment.startTime)} → ${formatTime(segment.endTime)}`}
                                variant="outlined"
                                sx={{
                                  height: 19,
                                  color: '#bae6fd',
                                  borderColor: 'rgba(56,189,248,0.20)',
                                  bgcolor: 'rgba(14,165,233,0.08)',
                                  fontSize: '0.6rem',
                                  fontWeight: 850,
                                }}
                              />

                              <Chip
                                size="small"
                                label={formatDurationMinutes(segment.durationMinutes)}
                                variant="outlined"
                                sx={{
                                  height: 19,
                                  color: isGap ? '#fde68a' : '#cbd5e1',
                                  borderColor: isGap
                                    ? 'rgba(251,191,36,0.30)'
                                    : 'rgba(148,163,184,0.18)',
                                  fontSize: '0.6rem',
                                }}
                              />

                              {segment.isStartLocked && (
                                <Chip
                                  size="small"
                                  label="Inicio fijado"
                                  variant="outlined"
                                  sx={{
                                    height: 19,
                                    color: '#fde68a',
                                    borderColor: 'rgba(251,191,36,0.32)',
                                    bgcolor: 'rgba(251,191,36,0.08)',
                                    fontSize: '0.6rem',
                                    fontWeight: 900,
                                  }}
                                />
                              )}

                              {segment.hasConflict && (
                                <Chip
                                  size="small"
                                  label="Conflicto"
                                  color="error"
                                  variant="outlined"
                                  sx={{
                                    height: 19,
                                    fontSize: '0.6rem',
                                    fontWeight: 900,
                                  }}
                                />
                              )}
                            </Stack>

                            {segment.conflictMessage && (
                              <Typography sx={{ color: '#fecaca', fontSize: '0.62rem' }}>
                                {segment.conflictMessage}
                              </Typography>
                            )}
                          </Stack>
                        ) : isZeroDurationProduction ? (
                          <Stack
                            direction="row"
                            spacing={0.6}
                            alignItems="center"
                            sx={{ minWidth: 0, height: '100%' }}
                          >
                            <Typography
                              noWrap
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 950,
                                fontSize: '0.68rem',
                                minWidth: 0,
                              }}
                            >
                              {segment.referenceLabel}
                            </Typography>

                            <Chip
                              size="small"
                              label="Sin duración"
                              variant="outlined"
                              sx={{
                                height: 18,
                                color: '#fef3c7',
                                borderColor: 'rgba(251,191,36,0.34)',
                                bgcolor: 'rgba(251,191,36,0.08)',
                                fontSize: '0.58rem',
                                fontWeight: 900,
                                flexShrink: 0,
                              }}
                            />
                          </Stack>
                        ) : showCompactContent ? (
                          <Stack
                            direction="row"
                            spacing={0.6}
                            alignItems="center"
                            sx={{ minWidth: 0, height: '100%' }}
                          >
                            <Typography
                              noWrap
                              sx={{
                                color: isGap ? '#fde68a' : '#f8fafc',
                                fontWeight: 950,
                                fontSize: '0.68rem',
                                minWidth: 0,
                              }}
                            >
                              {segment.referenceLabel}
                            </Typography>

                            <Typography
                              noWrap
                              sx={{
                                color: isGap ? '#fde68a' : '#bae6fd',
                                fontWeight: 850,
                                fontSize: '0.62rem',
                                flexShrink: 0,
                              }}
                            >
                              {formatTime(segment.startTime)} → {formatTime(segment.endTime)}
                            </Typography>
                          </Stack>
                        ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 6,
                              right: 6,
                              top: '50%',
                              height: 3,
                              transform: 'translateY(-50%)',
                              borderRadius: 999,
                              bgcolor: isGap
                                ? 'rgba(251,191,36,0.72)'
                                : segment.isStartLocked
                                  ? 'rgba(251,191,36,0.82)'
                                  : 'rgba(56,189,248,0.82)',
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
  );
}

function PendingOrderGroupCard({
  group,
  onDragStart,
  onDragEnd,
}: {
  group: PendingOrderGroup;
  onDragStart: (groupKey: string) => void;
  onDragEnd: () => void;
}) {
  const sampleOrders = group.orders.slice(0, 4);

  return (
    <Paper
      elevation={0}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', group.key);
        onDragStart(group.key);
      }}
      onDragEnd={onDragEnd}
      sx={{
        borderRadius: 3,
        border: group.isReferenceGroup
          ? '1px solid rgba(56,189,248,0.18)'
          : '1px solid rgba(148,163,184,0.12)',
        bgcolor: group.isReferenceGroup ? 'rgba(8,47,73,0.22)' : 'rgba(2,6,23,0.32)',
        p: 1.35,
        transition: 'border-color 0.18s ease, background 0.18s ease, transform 0.18s ease',
        '&:hover': {
          borderColor: 'rgba(56,189,248,0.42)',
          bgcolor: group.isReferenceGroup ? 'rgba(8,47,73,0.34)' : 'rgba(15,23,42,0.48)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
                fontSize: '0.94rem',
                wordBreak: 'break-word',
              }}
            >
              {group.referenceLabel}
            </Typography>

            <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
              {group.isReferenceGroup ? 'Referencia agrupada' : 'OF individual'}
            </Typography>
          </Stack>

          <Chip
            size="small"
            label={`${group.orders.length} ${group.orders.length === 1 ? 'OF' : 'OFs'}`}
            variant="outlined"
            sx={{
              height: 22,
              color: '#bae6fd',
              borderColor: 'rgba(56,189,248,0.24)',
              bgcolor: 'rgba(14,165,233,0.08)',
              fontSize: '0.7rem',
              fontWeight: 850,
              flexShrink: 0,
            }}
          />
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={formatQuantity(group.totalQuantity, group.quantityUnit)}
            variant="outlined"
            sx={{
              height: 22,
              color: '#cbd5e1',
              borderColor: 'rgba(148,163,184,0.18)',
              fontSize: '0.7rem',
            }}
          />

          {group.customers.length > 0 && (
            <Chip
              size="small"
              label={`${group.customers.length} cliente${group.customers.length === 1 ? '' : 's'}`}
              variant="outlined"
              sx={{
                height: 22,
                color: '#cbd5e1',
                borderColor: 'rgba(148,163,184,0.18)',
                fontSize: '0.7rem',
              }}
            />
          )}

          {group.customerOrders.length > 0 && (
            <Chip
              size="small"
              label={`${group.customerOrders.length} pedido${group.customerOrders.length === 1 ? '' : 's'}`}
              variant="outlined"
              sx={{
                height: 22,
                color: '#cbd5e1',
                borderColor: 'rgba(148,163,184,0.18)',
                fontSize: '0.7rem',
              }}
            />
          )}
        </Stack>

        <Stack spacing={0.35}>
          {sampleOrders.map((order) => (
            <Typography
              key={order.id}
              sx={{
                color: '#94a3b8',
                fontSize: '0.78rem',
                lineHeight: 1.35,
              }}
            >
              {order.manufacturing_order_number} ·{' '}
              {formatQuantity(order.planned_quantity_value, order.quantity_unit)}
            </Typography>
          ))}

          {group.orders.length > sampleOrders.length && (
            <Typography sx={{ color: '#64748b', fontSize: '0.76rem' }}>
              +{group.orders.length - sampleOrders.length} OFs más en el conjunto
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function IndustrialCostProductionPlanningView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [plannedDate, setPlannedDate] = useState(getTodayLocalIsoDate());
  const [planningViewMode, setPlanningViewMode] = useState<'line' | 'day' | 'week'>('week');
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [lineTrackingDialogOpen, setLineTrackingDialogOpen] = useState(false);
  const [lineTrackingReference, setLineTrackingReference] = useState(
    () => getCurrentTrackingReference(),
  );
  const [pendingPanelOpen, setPendingPanelOpen] = useState(getInitialPendingPanelOpen);
  const [weeklyTimelineScale, setWeeklyTimelineScale] = useState(50);
  const [selectedProcessGroup, setSelectedProcessGroup] = useState<string>('all');
  const [hasInitializedProcessGroupFilter, setHasInitializedProcessGroupFilter] =
    useState(false);
  const [pendingGroupSearch, setPendingGroupSearch] = useState('');
  const [draggedPendingGroupKey, setDraggedPendingGroupKey] = useState<string | null>(null);
  const [draggedPlannedGroupKey, setDraggedPlannedGroupKey] = useState<string | null>(null);
  const [plannedGroupDropPreview, setPlannedGroupDropPreview] =
    useState<PlannedGroupDropPreview | null>(null);
  const [weeklyPendingDropRequest, setWeeklyPendingDropRequest] =
    useState<WeeklyPendingDropRequest | null>(null);
  const [
    pendingDurationAssignmentRequest,
    setPendingDurationAssignmentRequest,
  ] = useState<PendingDurationAssignmentRequest | null>(null);
  const [pendingDurationMinutesInput, setPendingDurationMinutesInput] = useState('');
  const [weeklyLockedStartTime, setWeeklyLockedStartTime] = useState('06:00');
  const [
    plannedGroupScheduleAdjustmentRequest,
    setPlannedGroupScheduleAdjustmentRequest,
  ] = useState<PlannedGroupScheduleAdjustmentRequest | null>(null);
  const [plannedGroupLockedStartDate, setPlannedGroupLockedStartDate] =
    useState(getTodayLocalIsoDate());
  const [plannedGroupLockedStartTime, setPlannedGroupLockedStartTime] = useState('06:00');
  const [plannedGroupLockedStartNote, setPlannedGroupLockedStartNote] = useState('');
  const [trackingConfirmDialogRequest, setTrackingConfirmDialogRequest] =
    useState<TrackingConfirmDialogRequest | null>(null);

  const weekStartDate = useMemo(() => getWeekStartIsoDate(plannedDate), [plannedDate]);
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDaysToIsoDate(weekStartDate, index)),
    [weekStartDate],
  );

  const weeklyTimelineScaleConfig = useMemo(() => {
    const normalizedScale = Math.min(Math.max(weeklyTimelineScale, 0), 100);
    const pixelsPerHour = 6 + normalizedScale * 0.58;

    const markerStepHours =
      pixelsPerHour >= 48
        ? 1
        : pixelsPerHour >= 32
          ? 2
          : pixelsPerHour >= 20
            ? 4
            : pixelsPerHour >= 12
              ? 8
              : 24;

    return {
      label: `${Math.round(pixelsPerHour)} px/h`,
      pixelsPerHour,
      markerStepHours,
      lineColumnWidth: 245,
    };
  }, [weeklyTimelineScale]);

  const planningContextQuery = useQuery({
    queryKey: ['industrial-cost-production-planning-context', plannedDate],
    staleTime: PLANNING_CONTEXT_STALE_TIME_MS,
    queryFn: () =>
      getIndustrialCostProductionPlanningContext({
        planned_date: plannedDate,
      }),
  });

  const planningWeekContextQuery = useQuery({
    queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
    staleTime: PLANNING_CONTEXT_STALE_TIME_MS,
    queryFn: () =>
      getIndustrialCostProductionPlanningWeekContext({
        week_start_date: weekStartDate,
      }),
  });

  const lines = useMemo(
    () =>
      [...(planningContextQuery.data?.lines ?? [])]
        .filter((line) => line.is_active)
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [planningContextQuery.data?.lines],
  );

  const selectedLine = useMemo(
    () =>
      lines.find((line) => line.id === selectedLineId) ??
      lines[0] ??
      null,
    [lines, selectedLineId],
  );

  const lineQueueQuery = useQuery({
    queryKey: [
      'industrial-cost-production-planning-line-queue',
      selectedLine?.id ?? null,
    ],
    enabled: Boolean(selectedLine?.id) && planningViewMode === 'line',
    staleTime: 5_000,
    queryFn: () => {
      const lineId = selectedLine?.id;

      if (!lineId) {
        return Promise.resolve([]);
      }

      return getIndustrialCostProductionPlanningLineQueue({
        production_line_config_id: lineId,
      });
    },
  });

  const handleOpenLineTracking = (line: ProductionLineConfig) => {
    setSelectedLineId(line.id);
    setLineTrackingReference(getWeekTrackingReference(weekStartDate));
    setLineTrackingDialogOpen(true);
  };

  const importedOrders = planningContextQuery.data?.imported_orders ?? [];
  const planItems = planningContextQuery.data?.plan_items ?? [];
  const lineDaySettings = planningContextQuery.data?.line_day_settings ?? [];
  const shifts = planningContextQuery.data?.shifts ?? [];

  const planItemsByLineId = useMemo(() => {
    const map = new Map<string, ProductionPlanItem[]>();

    planItems.forEach((item) => {
      const current = map.get(item.production_line_config_id) ?? [];
      current.push(item);
      map.set(item.production_line_config_id, current);
    });

    return map;
  }, [planItems]);

  const weeklyPlannedGroupsByKey = useMemo(() => {
    const map = new Map<string, PlannedOrderGroup>();
    const weeklyPlanItems = planningWeekContextQuery.data?.plan_items ?? [];
    const weeklyLines = planningWeekContextQuery.data?.lines ?? lines;

    weeklyLines.forEach((line) => {
      const lineItems = weeklyPlanItems.filter(
        (item) => item.production_line_config_id === line.id,
      );
      const lineGroups = buildPlannedOrderGroups(lineItems);

      lineGroups.forEach((group) => {
        map.set(group.key, group);
      });
    });

    return map;
  }, [planningWeekContextQuery.data?.plan_items, planningWeekContextQuery.data?.lines, lines]);

  const activeShifts = useMemo(
    () =>
      [...shifts]
        .filter((shift) => shift.is_active)
        .sort(
          (a, b) =>
            (a.display_order ?? 0) - (b.display_order ?? 0) ||
            normalizePlanningTime(a.start_time).localeCompare(normalizePlanningTime(b.start_time)),
        ),
    [shifts],
  );

  const defaultPlanningStartTime = activeShifts[0]?.start_time
    ? normalizePlanningTime(activeShifts[0].start_time)
    : '06:00';

  const baseDailyCapacityMinutes = activeShifts.reduce(
    (sum, shift) =>
      sum +
      calculateTimeRangeDurationMinutes(
        normalizePlanningTime(shift.start_time),
        normalizePlanningTime(shift.end_time),
      ),
    0,
  );

  const baseDailyCapacityLabel =
    baseDailyCapacityMinutes > 0
      ? formatDurationMinutes(baseDailyCapacityMinutes)
      : 'Sin capacidad base';

  const weeklyContinuousSegments = useMemo(
    () =>
      buildWeeklyContinuousSegments({
        lines: planningWeekContextQuery.data?.lines ?? lines,
        planItems: planningWeekContextQuery.data?.plan_items ?? [],
        weekDates,
        baseDailyCapacityMinutes,
      }),
    [
      planningWeekContextQuery.data?.lines,
      planningWeekContextQuery.data?.plan_items,
      lines,
      weekDates,
      baseDailyCapacityMinutes,
    ],
  );

  const lineDaySettingsByLineId = useMemo(() => {
    const map = new Map<string, string>();

    lineDaySettings.forEach((setting) => {
      map.set(
        setting.production_line_config_id,
        normalizePlanningTime(setting.planned_start_time, defaultPlanningStartTime),
      );
    });

    return map;
  }, [lineDaySettings, defaultPlanningStartTime]);

  const plannedImportedOrderIds = useMemo(
    () => new Set(planItems.map((item) => item.imported_manufacturing_order_id)),
    [planItems],
  );

  const pendingOrders = useMemo(
    () => importedOrders.filter((order) => !plannedImportedOrderIds.has(order.id)),
    [importedOrders, plannedImportedOrderIds],
  );

  const pendingOrderGroups = useMemo<PendingOrderGroup[]>(() => {
    const groups = new Map<string, ImportedManufacturingOrder[]>();

    pendingOrders.forEach((order) => {
      const reference = order.free_product_ref?.trim();
      const key = reference ? `ref:${reference}` : `order:${order.id}`;
      const current = groups.get(key) ?? [];
      current.push(order);
      groups.set(key, current);
    });

    return Array.from(groups.entries())
      .map(([key, orders]) => {
        const firstOrder = orders[0];
        const reference = firstOrder.free_product_ref?.trim();
        const isReferenceGroup = Boolean(reference);

        return {
          key,
          referenceLabel: reference || firstOrder.manufacturing_order_number,
          isReferenceGroup,
          orders,
          totalQuantity: orders.reduce(
            (sum, order) => sum + Number(order.planned_quantity_value || 0),
            0,
          ),
          quantityUnit: firstOrder.quantity_unit ?? null,
          customers: uniqueNonEmpty(orders.map((order) => order.customer_number)),
          customerOrders: uniqueNonEmpty(orders.map((order) => order.customer_order_number)),
        };
      })
      .sort((a, b) => {
        if (a.isReferenceGroup !== b.isReferenceGroup) {
          return a.isReferenceGroup ? -1 : 1;
        }

        return a.referenceLabel.localeCompare(b.referenceLabel);
      });
  }, [pendingOrders]);

  const filteredPendingOrderGroups = useMemo(() => {
    const query = pendingGroupSearch.trim().toLowerCase();

    if (!query) {
      return pendingOrderGroups;
    }

    return pendingOrderGroups.filter((group) => {
      const groupText = [
        group.referenceLabel,
        ...group.customers,
        ...group.customerOrders,
        ...group.orders.map((order) => order.manufacturing_order_number),
        ...group.orders.map((order) => order.free_product_ref ?? ''),
        ...group.orders.map((order) => order.customer_number ?? ''),
        ...group.orders.map((order) => order.customer_order_number ?? ''),
      ]
        .join(' ')
        .toLowerCase();

      return groupText.includes(query);
    });
  }, [pendingOrderGroups, pendingGroupSearch]);

  const visiblePendingOrderGroups = filteredPendingOrderGroups.slice(0, 10);

  const lineProcessGroups = useMemo<LineProcessGroup[]>(() => {
    const groups = new Map<string, LineProcessGroup>();

    lines.forEach((line) => {
      const key = buildProcessGroupKey(line.process_group);
      const label = buildProcessGroupLabel(line.process_group);
      const current = groups.get(key);

      if (current) {
        current.lines.push(line);
        current.firstDisplayOrder = Math.min(
          current.firstDisplayOrder,
          line.display_order ?? 0,
        );
      } else {
        groups.set(key, {
          key,
          label,
          lines: [line],
          firstDisplayOrder: line.display_order ?? 0,
        });
      }
    });

    return Array.from(groups.values()).sort(
      (a, b) =>
        a.firstDisplayOrder - b.firstDisplayOrder ||
        a.label.localeCompare(b.label),
    );
  }, [lines]);

  useEffect(() => {
    if (hasInitializedProcessGroupFilter || lineProcessGroups.length === 0) {
      return;
    }

    setSelectedProcessGroup(lineProcessGroups[0].key);
    setHasInitializedProcessGroupFilter(true);
  }, [hasInitializedProcessGroupFilter, lineProcessGroups]);

  const visibleLineProcessGroups = useMemo(
    () =>
      selectedProcessGroup === 'all'
        ? lineProcessGroups
        : lineProcessGroups.filter((group) => group.key === selectedProcessGroup),
    [lineProcessGroups, selectedProcessGroup],
  );

  const totalPlannedMinutes = planItems.reduce(
    (sum, item) => sum + resolvePlanItemDurationMinutes(item),
    0,
  );

  const selectedLineQueueItems = lineQueueQuery.data ?? [];

  const selectedLineQueueGroups = useMemo(
    () => buildPlannedOrderGroups(selectedLineQueueItems),
    [selectedLineQueueItems],
  );

  const selectedLineProjectedPlan = useMemo(() => {
    if (!selectedLine) return null;

    const selectedLineStartTime =
      lineDaySettingsByLineId.get(selectedLine.id) ?? defaultPlanningStartTime;

    return buildProjectedLinePlan({
      line: selectedLine,
      items: selectedLineQueueItems,
      baseStartDate: plannedDate,
      baseStartTime: selectedLineStartTime,
    });
  }, [
    selectedLine,
    selectedLineQueueItems,
    plannedDate,
    lineDaySettingsByLineId,
    defaultPlanningStartTime,
  ]);

  const selectedLineQueueTotalMinutes =
    selectedLineProjectedPlan?.totalProductionMinutes ??
    selectedLineQueueItems.reduce(
      (sum, item) => sum + resolvePlanItemDurationMinutes(item),
      0,
    );

  const selectedLineTrackingQueueItems = useMemo(
    () =>
      (planningWeekContextQuery.data?.plan_items ?? []).filter(
        (item) =>
          selectedLine &&
          item.production_line_config_id === selectedLine.id &&
          item.status !== 'completed' &&
          item.status !== 'cancelled',
      ),
    [planningWeekContextQuery.data?.plan_items, selectedLine],
  );

  const selectedLineTrackingQueueGroups = useMemo(
    () => buildPlannedOrderGroups(selectedLineTrackingQueueItems),
    [selectedLineTrackingQueueItems],
  );

  const selectedLineTrackingProjectedPlan = useMemo(() => {
    if (!selectedLine) return null;

    const effectiveTrackingItems = selectedLineTrackingQueueItems.map((item) =>
      sanitizePlanItemForLiveProjection(
        item,
        lineTrackingReference.dateIso,
        lineTrackingReference.time,
      ),
    );

    return buildProjectedLinePlan({
      line: selectedLine,
      items: effectiveTrackingItems,
      baseStartDate: lineTrackingReference.dateIso,
      baseStartTime: lineTrackingReference.time,
    });
  }, [
    selectedLine,
    selectedLineTrackingQueueItems,
    lineTrackingReference,
  ]);

  const selectedLineTrackingCards = useMemo(
    () =>
      selectedLineTrackingQueueGroups.flatMap((group) => {
        const segment = selectedLineTrackingProjectedPlan?.segments.find(
          (item) =>
            item.type === 'production' &&
            item.groupKey === group.key,
        );

        if (!segment) return [];

        return [
          {
            group,
            segment,
            isExpiredAdjustment: group.items.some((item) =>
              isPlanItemLockedStartBeforeReference(
                item,
                lineTrackingReference.dateIso,
                lineTrackingReference.time,
              ),
            ),
          },
        ];
      }),
    [
      selectedLineTrackingQueueGroups,
      selectedLineTrackingProjectedPlan,
      lineTrackingReference,
    ],
  );

  const selectedLineVisibleTrackingCards = useMemo(() => {
    const weekStartMinutes = dateTimeToAbsoluteMinutes(weekDates[0], '00:00');
    const weekEndMinutes = dateTimeToAbsoluteMinutes(
      addDaysToIsoDate(weekDates[6], 1),
      '00:00',
    );

    return selectedLineTrackingCards.filter(({ segment }) => {
      const segmentStartMinutes = dateTimeToAbsoluteMinutes(
        segment.startDate,
        segment.startTime,
      );
      const segmentEndMinutes = dateTimeToAbsoluteMinutes(
        segment.endDate,
        segment.endTime,
      );

      return (
        segmentEndMinutes > weekStartMinutes &&
        segmentStartMinutes < weekEndMinutes
      );
    });
  }, [selectedLineTrackingCards, weekDates]);

  const selectedLineVisibleTrackingTotalMinutes = useMemo(
    () =>
      selectedLineVisibleTrackingCards.reduce(
        (sum, item) => sum + item.segment.durationMinutes,
        0,
      ),
    [selectedLineVisibleTrackingCards],
  );

  const selectedLineVisibleTrackingConflictsCount = useMemo(
    () =>
      selectedLineVisibleTrackingCards.filter((item) => item.segment.hasConflict)
        .length,
    [selectedLineVisibleTrackingCards],
  );

  const selectedLineTrackingDayGroups = useMemo(() => {
    const weekDateSet = new Set(weekDates);
    const groupedByDate = new Map<string, typeof selectedLineVisibleTrackingCards>();

    selectedLineVisibleTrackingCards.forEach((card) => {
      const groupDate = weekDateSet.has(card.segment.startDate)
        ? card.segment.startDate
        : weekDates[0];
      const currentCards = groupedByDate.get(groupDate) ?? [];

      currentCards.push(card);
      groupedByDate.set(groupDate, currentCards);
    });

    return Array.from(groupedByDate.entries())
      .sort(
        ([leftDate], [rightDate]) =>
          isoDateToEpochDay(leftDate) - isoDateToEpochDay(rightDate),
      )
      .map(([dateIso, cards]) => ({
        dateIso,
        cards,
        totalMinutes: cards.reduce(
          (sum, item) => sum + item.segment.durationMinutes,
          0,
        ),
        conflictsCount: cards.filter((item) => item.segment.hasConflict).length,
      }));
  }, [selectedLineVisibleTrackingCards, weekDates]);

  const upsertLineDaySettingsMutation = useMutation({
    mutationFn: upsertIndustrialCostProductionPlanLineDaySettings,
    onMutate: async (payload) => {
      const queryKey = ['industrial-cost-production-planning-context', plannedDate];

      await queryClient.cancelQueries({ queryKey });

      const previousContext = queryClient.getQueryData<ProductionPlanningContext>(queryKey);

      queryClient.setQueryData<ProductionPlanningContext>(queryKey, (current) => {
        if (!current) return current;

        const now = new Date().toISOString();
        const existing = current.line_day_settings.find(
          (setting) =>
            setting.production_line_config_id === payload.production_line_config_id &&
            setting.planned_date === payload.planned_date,
        );

        const optimisticSetting = {
          id: existing?.id ?? `optimistic-line-setting-${payload.production_line_config_id}`,
          tenant_id: existing?.tenant_id ?? current.lines[0]?.tenant_id ?? '',
          production_line_config_id: payload.production_line_config_id,
          planned_date: payload.planned_date,
          planned_start_time: normalizePlanningTime(payload.planned_start_time),
          notes: payload.notes ?? null,
          created_by: existing?.created_by ?? null,
          created_at: existing?.created_at ?? now,
          updated_at: now,
        };

        return {
          ...current,
          line_day_settings: [
            ...current.line_day_settings.filter(
              (setting) =>
                !(
                  setting.production_line_config_id === payload.production_line_config_id &&
                  setting.planned_date === payload.planned_date
                ),
            ),
            optimisticSetting,
          ],
        };
      });

      return {
        previousContext,
      };
    },
    onError: (error, _variables, context) => {
      const queryKey = ['industrial-cost-production-planning-context', plannedDate];

      if (context?.previousContext) {
        queryClient.setQueryData(queryKey, context.previousContext);
      }

      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context', plannedDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
        }),
      ]);
    },
  });

  const assignPendingGroupToLineMutation = useMutation({
    mutationFn: async ({
      group,
      line,
      targetDate,
      startMode,
      lockedStartTime,
      insertBeforeSequenceOrder,
      manualDurationMinutes,
    }: {
      group: PendingOrderGroup;
      line: ProductionLineConfig;
      targetDate: string;
      startMode: 'append_to_queue' | 'locked_start';
      lockedStartTime?: string;
      insertBeforeSequenceOrder?: number | null;
      manualDurationMinutes?: number;
    }) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];
      const latestContext =
        queryClient.getQueryData<ProductionPlanningContext>(contextQueryKey);
      const latestWeekContext =
        queryClient.getQueryData<ProductionPlanningContext>(weekContextQueryKey);
      const latestPlanItems =
        latestWeekContext?.plan_items ?? latestContext?.plan_items ?? planItems;
      const groupOrderIds = new Set(group.orders.map((order) => order.id));

      const existingLineItems = latestPlanItems
        .filter(
          (item) =>
            item.production_line_config_id === line.id &&
            !groupOrderIds.has(item.imported_manufacturing_order_id),
        )
        .sort((a, b) => resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b));

      const safeInsertBeforeOrder =
        insertBeforeSequenceOrder && insertBeforeSequenceOrder > 0
          ? insertBeforeSequenceOrder
          : null;
      const insertedItemsCount = group.orders.length;
      const nextSequenceBase = safeInsertBeforeOrder
        ? safeInsertBeforeOrder - 1
        : resolveNextLineSequenceOrder(existingLineItems);
      const shiftedLineItems = safeInsertBeforeOrder
        ? existingLineItems.filter(
            (item) => resolvePlanItemOrderValue(item) >= safeInsertBeforeOrder,
          )
        : [];
      const normalizedLockedStartTime = normalizePlanningTime(
        lockedStartTime,
        defaultPlanningStartTime,
      );
      const isLockedStart = startMode === 'locked_start';
      const manualDurationsByOrderId =
        manualDurationMinutes && manualDurationMinutes > 0
          ? distributePendingGroupDurationMinutesAcrossOrders(
              group,
              manualDurationMinutes,
            )
          : null;

      const persistedPlanItems = await Promise.all([
        ...shiftedLineItems.map((item) => {
          const currentOrder = resolvePlanItemOrderValue(item);
          const nextSequenceOrder = currentOrder + insertedItemsCount;

          return updateIndustrialCostProductionPlanItem(item.id, {
            imported_manufacturing_order_id: item.imported_manufacturing_order_id,
            production_line_config_id: item.production_line_config_id,
            planned_date: item.planned_date,
            planned_start_time: item.planned_start_time
              ? formatTime(item.planned_start_time)
              : defaultPlanningStartTime,
            planned_end_time: item.planned_end_time
              ? formatTime(item.planned_end_time)
              : defaultPlanningStartTime,
            planned_quantity_value: Number(item.planned_quantity_value || 0),
            estimated_duration_minutes: resolvePlanItemDurationMinutes(item),
            status: item.status,
            priority: nextSequenceOrder,
            line_sequence_order: nextSequenceOrder,
            is_start_locked: item.is_start_locked,
            locked_start_date: item.locked_start_date,
            locked_start_time: item.locked_start_time,
            locked_start_conflict_policy: item.locked_start_conflict_policy,
            notes: item.notes,
          });
        }),
        ...group.orders.map((order, index) => {
          const nextSequenceOrder = nextSequenceBase + index + 1;
          const estimatedDurationMinutes =
            manualDurationsByOrderId?.get(order.id) ??
            Math.max(Number(order.planning_duration_minutes || 0), 0);
          const plannedStartTime = isLockedStart
            ? normalizedLockedStartTime
            : defaultPlanningStartTime;

          return createIndustrialCostProductionPlanItem({
            imported_manufacturing_order_id: order.id,
            production_line_config_id: line.id,
            planned_date: targetDate,
            planned_start_time: plannedStartTime,
            planned_end_time: addMinutesToTime(plannedStartTime, estimatedDurationMinutes),
            planned_quantity_value: Number(order.planned_quantity_value || 0),
            estimated_duration_minutes: estimatedDurationMinutes,
            status: 'planned',
            priority: nextSequenceOrder,
            line_sequence_order: nextSequenceOrder,
            is_start_locked: isLockedStart,
            locked_start_date: isLockedStart ? targetDate : null,
            locked_start_time: isLockedStart ? normalizedLockedStartTime : null,
            locked_start_conflict_policy: isLockedStart
              ? 'create_gap_if_previous_does_not_fit'
              : null,
            notes: null,
          });
        }),
      ]);

      return {
        group,
        line,
        persistedPlanItems,
      };
    },
    onMutate: async ({
      group,
      line,
      targetDate,
      startMode,
      lockedStartTime,
      insertBeforeSequenceOrder,
      manualDurationMinutes,
    }: {
      group: PendingOrderGroup;
      line: ProductionLineConfig;
      targetDate: string;
      startMode: 'append_to_queue' | 'locked_start';
      lockedStartTime?: string;
      insertBeforeSequenceOrder?: number | null;
      manualDurationMinutes?: number;
    }) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];

      await Promise.all([
        queryClient.cancelQueries({ queryKey: contextQueryKey }),
        queryClient.cancelQueries({ queryKey: weekContextQueryKey }),
      ]);

      const previousContext =
        queryClient.getQueryData<ProductionPlanningContext>(contextQueryKey);
      const previousWeekContext =
        queryClient.getQueryData<ProductionPlanningContext>(weekContextQueryKey);

      const applyOptimisticAssignment = (
        current: ProductionPlanningContext | undefined,
      ) => {
        if (!current) return current;

        const groupOrderIds = new Set(group.orders.map((order) => order.id));
        const existingLineItems = current.plan_items
          .filter(
            (item) =>
              item.production_line_config_id === line.id &&
              !groupOrderIds.has(item.imported_manufacturing_order_id),
          )
          .sort((a, b) => resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b));
        const safeInsertBeforeOrder =
          insertBeforeSequenceOrder && insertBeforeSequenceOrder > 0
            ? insertBeforeSequenceOrder
            : null;
        const insertedItemsCount = group.orders.length;
        const nextSequenceBase = safeInsertBeforeOrder
          ? safeInsertBeforeOrder - 1
          : resolveNextLineSequenceOrder(existingLineItems);
        const shiftedItemIds = new Set(
          safeInsertBeforeOrder
            ? existingLineItems
                .filter((item) => resolvePlanItemOrderValue(item) >= safeInsertBeforeOrder)
                .map((item) => item.id)
            : [],
        );
        const now = new Date().toISOString();
        const normalizedLockedStartTime = normalizePlanningTime(
          lockedStartTime,
          defaultPlanningStartTime,
        );
        const isLockedStart = startMode === 'locked_start';
        const manualDurationsByOrderId =
          manualDurationMinutes && manualDurationMinutes > 0
            ? distributePendingGroupDurationMinutesAcrossOrders(
                group,
                manualDurationMinutes,
              )
            : null;

        const optimisticItems: ProductionPlanItem[] = group.orders.map((order, index) => {
          const nextSequenceOrder = nextSequenceBase + index + 1;
          const estimatedDurationMinutes =
            manualDurationsByOrderId?.get(order.id) ??
            Math.max(Number(order.planning_duration_minutes || 0), 0);
          const plannedStartTime = isLockedStart
            ? normalizedLockedStartTime
            : defaultPlanningStartTime;

          return {
            id: `optimistic-${order.id}`,
            tenant_id: order.tenant_id,
            imported_manufacturing_order_id: order.id,
            production_line_config_id: line.id,
            planned_date: targetDate,
            planned_start_time: plannedStartTime,
            planned_end_time: addMinutesToTime(plannedStartTime, estimatedDurationMinutes),
            planned_quantity_value: Number(order.planned_quantity_value || 0),
            estimated_duration_minutes: estimatedDurationMinutes,
            status: 'planned',
            priority: nextSequenceOrder,
            line_sequence_order: nextSequenceOrder,
            is_start_locked: isLockedStart,
            locked_start_date: isLockedStart ? targetDate : null,
            locked_start_time: isLockedStart ? normalizedLockedStartTime : null,
            locked_start_conflict_policy: isLockedStart
              ? 'create_gap_if_previous_does_not_fit'
              : null,
            locked_start_note: null,
            notes: null,
            linked_report_production_line_id: null,
            created_by: null,
            created_at: now,
            updated_at: now,

          manufacturing_order_number: order.manufacturing_order_number,
          imported_order_planned_quantity_value: order.planned_quantity_value,
          free_product_ref: order.free_product_ref,
          customer_number: order.customer_number,
          customer_order_number: order.customer_order_number,
          quantity_unit: order.quantity_unit,
          speed_value: order.speed_value,
          imported_order_planning_duration_source_value:
            order.planning_duration_source_value,
          imported_order_planning_duration_source_unit:
            order.planning_duration_source_unit,
          imported_order_planning_duration_minutes:
            order.planning_duration_minutes,
          imported_order_notes: order.notes,
          imported_order_extra_fields: order.extra_fields ?? {},

            production_line_code: line.code,
            production_line_name: line.name,
            production_line_process_group: line.process_group,
          };
        });

        return {
          ...current,
          plan_items: [
            ...current.plan_items
              .filter(
                (item) =>
                  !groupOrderIds.has(item.imported_manufacturing_order_id),
              )
              .map((item) => {
                if (!shiftedItemIds.has(item.id)) return item;

                const nextSequenceOrder =
                  resolvePlanItemOrderValue(item) + insertedItemsCount;

                return {
                  ...item,
                  priority: nextSequenceOrder,
                  line_sequence_order: nextSequenceOrder,
                  updated_at: now,
                };
              }),
            ...optimisticItems,
          ],
        };
      };

      queryClient.setQueryData<ProductionPlanningContext>(
        contextQueryKey,
        applyOptimisticAssignment,
      );
      queryClient.setQueryData<ProductionPlanningContext>(
        weekContextQueryKey,
        applyOptimisticAssignment,
      );

      setDraggedPendingGroupKey(null);

      return {
        previousContext,
        previousWeekContext,
      };
    },
    onSuccess: ({ line, persistedPlanItems }) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];
      const lineQueueQueryKey = [
        'industrial-cost-production-planning-line-queue',
        line.id,
      ];

      const persistedItemsById = new Map(
        persistedPlanItems.map((item) => [item.id, item]),
      );
      const persistedItemsByImportedOrderId = new Map(
        persistedPlanItems.map((item) => [item.imported_manufacturing_order_id, item]),
      );

      const applyPersistedPlanItems = (
        current: ProductionPlanningContext | undefined,
      ) => {
        if (!current) return current;

        return {
          ...current,
          plan_items: [
            ...current.plan_items.filter(
              (item) =>
                !persistedItemsById.has(item.id) &&
                !persistedItemsByImportedOrderId.has(
                  item.imported_manufacturing_order_id,
                ),
            ),
            ...persistedPlanItems,
          ].sort((a, b) => {
            if (a.production_line_config_id !== b.production_line_config_id) {
              return a.production_line_config_id.localeCompare(
                b.production_line_config_id,
              );
            }

            return resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b);
          }),
        };
      };

      const applyPersistedLineQueueItems = (
        current: ProductionPlanItem[] | undefined,
      ) => {
        if (!current) return current;

        return [
          ...current.filter(
            (item) =>
              !persistedItemsById.has(item.id) &&
              !persistedItemsByImportedOrderId.has(
                item.imported_manufacturing_order_id,
              ),
          ),
          ...persistedPlanItems.filter(
            (item) => item.production_line_config_id === line.id,
          ),
        ].sort((a, b) => resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b));
      };

      queryClient.setQueryData<ProductionPlanningContext>(
        contextQueryKey,
        applyPersistedPlanItems,
      );
      queryClient.setQueryData<ProductionPlanningContext>(
        weekContextQueryKey,
        applyPersistedPlanItems,
      );
      queryClient.setQueryData<ProductionPlanItem[]>(
        lineQueueQueryKey,
        applyPersistedLineQueueItems,
      );
    },
    onError: (error, _variables, context) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];

      if (context?.previousContext) {
        queryClient.setQueryData(contextQueryKey, context.previousContext);
      }

      if (context?.previousWeekContext) {
        queryClient.setQueryData(weekContextQueryKey, context.previousWeekContext);
      }

      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      setDraggedPendingGroupKey(null);
    },
    onSettled: async () => {
      setDraggedPendingGroupKey(null);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context', plannedDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-line-queue'],
        }),
      ]);
    },
  });

  const movePlannedGroupToLineMutation = useMutation({
    mutationFn: async ({
      group,
      line,
    }: {
      group: PlannedOrderGroup;
      line: ProductionLineConfig;
    }) => {
      const existingLineItems = planItemsByLineId
        .get(line.id)
        ?.filter(
          (item) =>
            !group.items.some((groupItem) => groupItem.id === item.id),
        ) ?? [];

      const nextSequenceBase = resolveNextLineSequenceOrder(existingLineItems);

      await Promise.all(
        group.items.map((item, index) => {
          const nextSequenceOrder = nextSequenceBase + index + 1;

          return updateIndustrialCostProductionPlanItem(item.id, {
            imported_manufacturing_order_id: item.imported_manufacturing_order_id,
            production_line_config_id: line.id,
            planned_date: item.planned_date,
            planned_start_time: formatTime(item.planned_start_time),
            planned_end_time: formatTime(item.planned_end_time),
            planned_quantity_value: Number(item.planned_quantity_value || 0),
            estimated_duration_minutes: resolvePlanItemDurationMinutes(item),
            status: item.status,
            priority: nextSequenceOrder,
            line_sequence_order: nextSequenceOrder,
            is_start_locked: item.is_start_locked,
            locked_start_date: item.locked_start_date,
            locked_start_time: item.locked_start_time,
            locked_start_conflict_policy: item.locked_start_conflict_policy,
            notes: item.notes,
          });
        }),
      );

      return {
        group,
        line,
      };
    },
    onMutate: async ({
      group,
      line,
    }: {
      group: PlannedOrderGroup;
      line: ProductionLineConfig;
    }) => {
      const queryKey = ['industrial-cost-production-planning-context', plannedDate];

      await queryClient.cancelQueries({ queryKey });

      const previousContext = queryClient.getQueryData<ProductionPlanningContext>(queryKey);

      queryClient.setQueryData<ProductionPlanningContext>(queryKey, (current) => {
        if (!current) return current;

        const movedItemIds = new Set(group.items.map((item) => item.id));

        const destinationExistingItems = current.plan_items.filter(
          (item) =>
            item.production_line_config_id === line.id &&
            !movedItemIds.has(item.id),
        );

        const nextSequenceBase = resolveNextLineSequenceOrder(destinationExistingItems);

        const movedItems = group.items.map((item, index) => {
          const nextSequenceOrder = nextSequenceBase + index + 1;

          return {
            ...item,
            production_line_config_id: line.id,
            production_line_code: line.code,
            production_line_name: line.name,
            production_line_process_group: line.process_group,
            estimated_duration_minutes: resolvePlanItemDurationMinutes(item),
            priority: nextSequenceOrder,
            line_sequence_order: nextSequenceOrder,
            updated_at: new Date().toISOString(),
          };
        });

        const movedItemsById = new Map(
          movedItems.map((item) => [item.id, item]),
        );

        return {
          ...current,
          plan_items: current.plan_items.map((item) => movedItemsById.get(item.id) ?? item),
        };
      });

      setDraggedPlannedGroupKey(null);

      return {
        previousContext,
      };
    },
    onError: (error, _variables, context) => {
      const queryKey = ['industrial-cost-production-planning-context', plannedDate];

      if (context?.previousContext) {
        queryClient.setQueryData(queryKey, context.previousContext);
      }

      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      setDraggedPlannedGroupKey(null);
    },
    onSettled: async () => {
      setDraggedPlannedGroupKey(null);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context', plannedDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
        }),
      ]);
    },
  });

  const handleChangeLineStartTime = (
    line: ProductionLineConfig,
    nextStartTime: string,
  ) => {
    const normalizedStartTime = normalizePlanningTime(nextStartTime);

    upsertLineDaySettingsMutation.mutate({
      production_line_config_id: line.id,
      planned_date: plannedDate,
      planned_start_time: normalizedStartTime,
      notes: null,
    });
  };

  const requestAssignPendingGroupToLine = (
    request: PendingDurationAssignmentRequest,
  ) => {
    if (assignPendingGroupToLineMutation.isPending) return;

    const groupDurationMinutes = resolvePendingOrderGroupDurationMinutes(
      request.group,
    );

    if (groupDurationMinutes <= 0) {
      setPendingDurationAssignmentRequest(request);
      setPendingDurationMinutesInput('');
      setDraggedPendingGroupKey(null);
      return;
    }

    assignPendingGroupToLineMutation.mutate(request);
  };

  const handleClosePendingDurationAssignmentDialog = () => {
    setPendingDurationAssignmentRequest(null);
    setPendingDurationMinutesInput('');
    setDraggedPendingGroupKey(null);
  };

  const handleConfirmPendingDurationAssignment = () => {
    if (
      !pendingDurationAssignmentRequest ||
      assignPendingGroupToLineMutation.isPending
    ) {
      return;
    }

    const manualDurationMinutes = Math.round(
      Number(pendingDurationMinutesInput || 0),
    );
    const minimumDurationMinutes =
      getMinimumPendingGroupManualDurationMinutes(
        pendingDurationAssignmentRequest.group,
      );

    if (
      !Number.isFinite(manualDurationMinutes) ||
      manualDurationMinutes < minimumDurationMinutes
    ) {
      enqueueSnackbar(
        `Introduce una duración válida de al menos ${minimumDurationMinutes} min.`,
        { variant: 'warning' },
      );
      return;
    }

    assignPendingGroupToLineMutation.mutate({
      ...pendingDurationAssignmentRequest,
      manualDurationMinutes,
    });

    setPendingDurationAssignmentRequest(null);
    setPendingDurationMinutesInput('');
  };

  const handleDropPendingGroupOnLine = (
    groupKey: string | null,
    line: ProductionLineConfig,
  ) => {
    if (!groupKey || assignPendingGroupToLineMutation.isPending) return;

    const group = pendingOrderGroups.find((item) => item.key === groupKey);
    if (!group) return;

    requestAssignPendingGroupToLine({
      group,
      line,
      targetDate: plannedDate,
      startMode: 'append_to_queue',
    });
  };

  const handleRequestWeeklyPendingDrop = ({
    groupKey,
    line,
    dateIso,
    suggestedStartTime,
    insertBeforeSequenceOrder,
  }: {
    groupKey: string | null;
    line: ProductionLineConfig;
    dateIso: string;
    suggestedStartTime?: string;
    insertBeforeSequenceOrder?: number | null;
  }) => {
    if (!groupKey || assignPendingGroupToLineMutation.isPending) return;

    const group = pendingOrderGroups.find((item) => item.key === groupKey);
    if (!group) return;

    const lineHasQueue = (planningWeekContextQuery.data?.plan_items ?? planItems).some(
      (item) => item.production_line_config_id === line.id,
    );

    setWeeklyLockedStartTime(
      normalizePlanningTime(suggestedStartTime, defaultPlanningStartTime),
    );
    setWeeklyPendingDropRequest({
      groupKey,
      line,
      dateIso,
      lineHasQueue,
      insertBeforeSequenceOrder: insertBeforeSequenceOrder ?? null,
    });
  };

  const handleCloseWeeklyPendingDropDialog = () => {
    setWeeklyPendingDropRequest(null);
    setDraggedPendingGroupKey(null);
  };

  const handleConfirmWeeklyPendingDrop = (
    startMode: 'append_to_queue' | 'locked_start',
  ) => {
    if (!weeklyPendingDropRequest || isPlanningWriteInProgress) return;

    const group = pendingOrderGroups.find(
      (item) => item.key === weeklyPendingDropRequest.groupKey,
    );
    if (!group) return;

    const assignmentRequest: PendingDurationAssignmentRequest = {
      group,
      line: weeklyPendingDropRequest.line,
      targetDate: weeklyPendingDropRequest.dateIso,
      startMode,
      lockedStartTime: weeklyLockedStartTime,
      insertBeforeSequenceOrder: weeklyPendingDropRequest.insertBeforeSequenceOrder,
    };

    setPlannedDate(weeklyPendingDropRequest.dateIso);
    setWeeklyPendingDropRequest(null);
    requestAssignPendingGroupToLine(assignmentRequest);
  };


  const handleRequestWeeklyPlannedGroupDrop = ({
    groupKey,
    line,
    insertBeforeSequenceOrder,
  }: WeeklyPlannedGroupDropRequest) => {
    if (!groupKey || isPlanningWriteInProgress) return;

    const group = weeklyPlannedGroupsByKey.get(groupKey);
    if (!group) {
      enqueueSnackbar('No se pudo localizar el conjunto planificado para reordenarlo.', {
        variant: 'warning',
      });
      setDraggedPlannedGroupKey(null);
      return;
    }

    const alreadyInLine = group.items.every(
      (item) => item.production_line_config_id === line.id,
    );

    if (!alreadyInLine) {
      handleDropPlannedGroupOnLine(groupKey, line);
      return;
    }

    const lineItems = (planningWeekContextQuery.data?.plan_items ?? planItems)
      .filter((item) => item.production_line_config_id === line.id)
      .sort((a, b) => resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b));

    const currentGroups = buildPlannedOrderGroups(lineItems);
    const movingGroupIndex = currentGroups.findIndex((item) => item.key === groupKey);

    if (movingGroupIndex === -1) {
      setDraggedPlannedGroupKey(null);
      return;
    }

    const reorderedGroups = [...currentGroups];
    const [movingGroup] = reorderedGroups.splice(movingGroupIndex, 1);

    let insertIndex = reorderedGroups.length;

    if (insertBeforeSequenceOrder !== null) {
      const targetIndex = reorderedGroups.findIndex((candidateGroup) =>
        candidateGroup.items.some(
          (item) => resolvePlanItemOrderValue(item) >= insertBeforeSequenceOrder,
        ),
      );

      if (targetIndex >= 0) {
        insertIndex = targetIndex;
      }
    }

    reorderedGroups.splice(insertIndex, 0, movingGroup);

    const unchanged =
      reorderedGroups.map((item) => item.key).join('|') ===
      currentGroups.map((item) => item.key).join('|');

    if (unchanged) {
      setDraggedPlannedGroupKey(null);
      return;
    }

    let nextSequenceOrder = 1;

    const reorderedItems = reorderedGroups.flatMap((plannedGroup) =>
      plannedGroup.items.map((item) => {
        const currentSequenceOrder = nextSequenceOrder;

        const nextItem = {
          ...item,
          priority: currentSequenceOrder,
          line_sequence_order: currentSequenceOrder,
        };

        nextSequenceOrder += 1;

        return nextItem;
      }),
    );

    reorderPlannedGroupsWithinLineMutation.mutate({
      line,
      reorderedItems,
    });
  };
 
  const handleDropPlannedGroupOnLine = (
    groupKey: string | null,
    line: ProductionLineConfig,
  ) => {
    if (!groupKey || isPlanningWriteInProgress) return;

    const group = weeklyPlannedGroupsByKey.get(groupKey);
    if (!group) {
      enqueueSnackbar('No se pudo localizar el conjunto planificado para moverlo de línea.', {
        variant: 'warning',
      });
      setDraggedPlannedGroupKey(null);
      return;
    }

    const alreadyInLine = group.items.every(
      (item) => item.production_line_config_id === line.id,
    );

    if (alreadyInLine) {
      setDraggedPlannedGroupKey(null);
      return;
    }

    movePlannedGroupToLineMutation.mutate({
      group,
      line,
    });
  };

  const unplanPlannedGroupMutation = useMutation({
    mutationFn: async (group: PlannedOrderGroup) => {
      const firstItem = group.items[0];
      const reference = getCurrentTrackingReference();

      const shouldPreserveSuccessorStart = group.items.some(
        (item) =>
          item.is_start_locked &&
          !isPlanItemLockedStartBeforeReference(
            item,
            reference.dateIso,
            reference.time,
          ),
      );

      const lineItems = (
        planningWeekContextQuery.data?.plan_items ?? planItems
      )
        .filter(
          (item) =>
            firstItem &&
            item.production_line_config_id === firstItem.production_line_config_id,
        )
        .map((item) =>
          sanitizePlanItemForLiveProjection(
            item,
            reference.dateIso,
            reference.time,
          ),
        );

      const lineGroups = buildPlannedOrderGroups(lineItems);
      const groupIndex = lineGroups.findIndex((item) => item.key === group.key);
      const successorGroup =
        shouldPreserveSuccessorStart && groupIndex >= 0
          ? lineGroups[groupIndex + 1] ?? null
          : null;
      const line = firstItem
        ? lines.find((item) => item.id === firstItem.production_line_config_id) ?? null
        : null;
      const successorSegment =
        line && successorGroup
          ? buildProjectedLinePlan({
              line,
              items: lineItems,
              baseStartDate: reference.dateIso,
              baseStartTime: reference.time,
            }).segments.find(
              (item) =>
                item.type === 'production' &&
                item.groupKey === successorGroup.key,
            ) ?? null
          : null;

      await unplanIndustrialCostProductionPlanGroup({
        production_line_config_id: firstItem?.production_line_config_id ?? '',
        plan_item_ids: group.items.map((item) => item.id),
        preserve_successor_start_date: successorSegment?.startDate ?? null,
        preserve_successor_start_time: successorSegment?.startTime ?? null,
        preserve_successor_start_note: successorSegment
          ? `Continuidad preservada al devolver ${group.referenceLabel} a pendientes.`
          : null,
      });

      return group;
    },
    onMutate: async (group: PlannedOrderGroup) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];
      const lineQueueQueryKey = ['industrial-cost-production-planning-line-queue'];

      await Promise.all([
        queryClient.cancelQueries({ queryKey: contextQueryKey }),
        queryClient.cancelQueries({ queryKey: weekContextQueryKey }),
        queryClient.cancelQueries({ queryKey: lineQueueQueryKey }),
      ]);

      const previousContext =
        queryClient.getQueryData<ProductionPlanningContext>(contextQueryKey);
      const previousWeekContext =
        queryClient.getQueryData<ProductionPlanningContext>(weekContextQueryKey);
      const previousLineQueues =
        queryClient.getQueriesData<ProductionPlanItem[]>({
          queryKey: lineQueueQueryKey,
        });
      const deletedItemIds = new Set(group.items.map((item) => item.id));

      const removeDeletedItems = (current: ProductionPlanningContext | undefined) => {
        if (!current) return current;

        return {
          ...current,
          plan_items: current.plan_items.filter((item) => !deletedItemIds.has(item.id)),
        };
      };

      const removeDeletedLineQueueItems = (
        current: ProductionPlanItem[] | undefined,
      ) => {
        if (!current) return current;

        return current.filter((item) => !deletedItemIds.has(item.id));
      };

      queryClient.setQueryData<ProductionPlanningContext>(
        contextQueryKey,
        removeDeletedItems,
      );
      queryClient.setQueryData<ProductionPlanningContext>(
        weekContextQueryKey,
        removeDeletedItems,
      );
      queryClient.setQueriesData<ProductionPlanItem[]>(
        { queryKey: lineQueueQueryKey },
        removeDeletedLineQueueItems,
      );

      setDraggedPlannedGroupKey(null);

      return {
        previousContext,
        previousWeekContext,
        previousLineQueues,
      };
    },
    onError: (error, _variables, context) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];

      if (context?.previousContext) {
        queryClient.setQueryData(contextQueryKey, context.previousContext);
      }

      if (context?.previousWeekContext) {
        queryClient.setQueryData(weekContextQueryKey, context.previousWeekContext);
      }

      context?.previousLineQueues?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      setDraggedPlannedGroupKey(null);
    },
    onSettled: async () => {
      setDraggedPlannedGroupKey(null);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context', plannedDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-line-queue'],
        }),
      ]);
    },
  });

  const handleDropPlannedGroupOnPendingPanel = (groupKey: string | null) => {
    if (!groupKey || isPlanningWriteInProgress) return;

    const group = weeklyPlannedGroupsByKey.get(groupKey);

    if (!group) {
      enqueueSnackbar('No se pudo localizar el conjunto planificado para devolverlo a pendientes.', {
        variant: 'warning',
      });
      setDraggedPlannedGroupKey(null);
      return;
    }

    unplanPlannedGroupMutation.mutate(group);
  };

  const reorderPlannedGroupsWithinLineMutation = useMutation({
    mutationFn: async ({
      line,
      reorderedItems,
    }: {
      line: ProductionLineConfig;
      reorderedItems: ProductionPlanItem[];
    }) => {
      const persistedReorderedItems = await Promise.all(
        reorderedItems.map((item) =>
          updateIndustrialCostProductionPlanItem(item.id, {
            imported_manufacturing_order_id: item.imported_manufacturing_order_id,
            production_line_config_id: item.production_line_config_id,
            planned_date: item.planned_date,
            planned_start_time: formatTime(item.planned_start_time),
            planned_end_time: formatTime(item.planned_end_time),
            planned_quantity_value: Number(item.planned_quantity_value || 0),
            estimated_duration_minutes: resolvePlanItemDurationMinutes(item),
            status: item.status,
            priority: Number(item.line_sequence_order ?? item.priority ?? 0),
            line_sequence_order: Number(item.line_sequence_order ?? item.priority ?? 0),
            is_start_locked: item.is_start_locked,
            locked_start_date: item.locked_start_date,
            locked_start_time: item.locked_start_time,
            locked_start_conflict_policy: item.locked_start_conflict_policy,
            notes: item.notes,
          }),
        ),
      );

      return {
        line,
        persistedReorderedItems,
      };
    },
    onMutate: async ({
      line,
      reorderedItems,
    }: {
      line: ProductionLineConfig;
      reorderedItems: ProductionPlanItem[];
    }) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];
      const lineQueueQueryKey = ['industrial-cost-production-planning-line-queue'];

      await Promise.all([
        queryClient.cancelQueries({ queryKey: contextQueryKey }),
        queryClient.cancelQueries({ queryKey: weekContextQueryKey }),
        queryClient.cancelQueries({ queryKey: lineQueueQueryKey }),
      ]);

      const previousContext =
        queryClient.getQueryData<ProductionPlanningContext>(contextQueryKey);
      const previousWeekContext =
        queryClient.getQueryData<typeof planningWeekContextQuery.data>(
          weekContextQueryKey,
        );
      const previousLineQueues =
        queryClient.getQueriesData<ProductionPlanItem[]>({
          queryKey: lineQueueQueryKey,
        });

      const reorderedItemsById = new Map(
        reorderedItems.map((item) => [item.id, item]),
      );

      const sortPlanItems = (items: ProductionPlanItem[]) =>
        [...items].sort((a, b) => {
          if (a.production_line_config_id !== b.production_line_config_id) {
            return a.production_line_config_id.localeCompare(
              b.production_line_config_id,
            );
          }

          return resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b);
        });

      const applyReorderedItemsToContext = (
        current: typeof planningContextQuery.data,
      ) => {
        if (!current) return current;

        return {
          ...current,
          plan_items: sortPlanItems(
            current.plan_items.map((item) => reorderedItemsById.get(item.id) ?? item),
          ),
        };
      };

      const applyReorderedItemsToWeekContext = (
        current: typeof planningWeekContextQuery.data,
      ) => {
        if (!current) return current;

        return {
          ...current,
          plan_items: sortPlanItems(
            current.plan_items.map((item) => reorderedItemsById.get(item.id) ?? item),
          ),
        };
      };

      const applyReorderedItemsToLineQueue = (
        current: ProductionPlanItem[] | undefined,
      ) => {
        if (!current) return current;

        return current
          .map((item) => reorderedItemsById.get(item.id) ?? item)
          .sort((a, b) => resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b));
      };

      queryClient.setQueryData(
        contextQueryKey,
        applyReorderedItemsToContext,
      );
      queryClient.setQueryData(
        weekContextQueryKey,
        applyReorderedItemsToWeekContext,
      );
      queryClient.setQueriesData<ProductionPlanItem[]>(
        { queryKey: lineQueueQueryKey },
        applyReorderedItemsToLineQueue,
      );

      return {
        previousContext,
        previousWeekContext,
        previousLineQueues,
        line,
      };
    },
    onSuccess: ({ persistedReorderedItems }) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];
      const lineQueueQueryKey = ['industrial-cost-production-planning-line-queue'];

      const persistedItemsById = new Map(
        persistedReorderedItems.map((item) => [item.id, item]),
      );

      const sortPlanItems = (items: ProductionPlanItem[]) =>
        [...items].sort((a, b) => {
          if (a.production_line_config_id !== b.production_line_config_id) {
            return a.production_line_config_id.localeCompare(
              b.production_line_config_id,
            );
          }

          return resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b);
        });

      const applyPersistedItemsToContext = (
        current: typeof planningContextQuery.data,
      ) => {
        if (!current) return current;

        return {
          ...current,
          plan_items: sortPlanItems(
            current.plan_items.map((item) => persistedItemsById.get(item.id) ?? item),
          ),
        };
      };

      const applyPersistedItemsToWeekContext = (
        current: typeof planningWeekContextQuery.data,
      ) => {
        if (!current) return current;

        return {
          ...current,
          plan_items: sortPlanItems(
            current.plan_items.map((item) => persistedItemsById.get(item.id) ?? item),
          ),
        };
      };

      const applyPersistedItemsToLineQueue = (
        current: ProductionPlanItem[] | undefined,
      ) => {
        if (!current) return current;

        return current
          .map((item) => persistedItemsById.get(item.id) ?? item)
          .sort((a, b) => resolvePlanItemOrderValue(a) - resolvePlanItemOrderValue(b));
      };

      queryClient.setQueryData(
        contextQueryKey,
        applyPersistedItemsToContext,
      );
      queryClient.setQueryData(
        weekContextQueryKey,
        applyPersistedItemsToWeekContext,
      );
      queryClient.setQueriesData<ProductionPlanItem[]>(
        { queryKey: lineQueueQueryKey },
        applyPersistedItemsToLineQueue,
      );
    },
    onError: (error, _variables, context) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];

      if (context?.previousContext) {
        queryClient.setQueryData(contextQueryKey, context.previousContext);
      }

      if (context?.previousWeekContext) {
        queryClient.setQueryData(weekContextQueryKey, context.previousWeekContext);
      }

      context?.previousLineQueues?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      setDraggedPlannedGroupKey(null);
    },
    onSettled: async () => {
      setDraggedPlannedGroupKey(null);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context', plannedDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-line-queue'],
        }),
      ]);
    },
  });

  const updatePlannedGroupScheduleAdjustmentMutation = useMutation({
    mutationFn: updateIndustrialCostProductionPlanGroupScheduleAdjustment,
    onSuccess: (_items, payload) => {
      enqueueSnackbar(
        payload.is_start_locked
          ? 'Previsión horaria revisada guardada.'
          : 'Ajuste horario retirado.',
        { variant: 'success' },
      );
      setPlannedGroupScheduleAdjustmentRequest(null);
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context', plannedDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-line-queue'],
        }),
      ]);
    },
  });

  const moveTrackingGroupToLockedStartMutation = useMutation({
    mutationFn: moveIndustrialCostProductionPlanGroupToLockedStart,
    onSuccess: () => {
      enqueueSnackbar('Referencia movida al inicio fijado seleccionado.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context', plannedDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-line-queue'],
        }),
      ]);
    },
  });

  const resolveLockedStartConflictMutation = useMutation({
    mutationFn: resolveIndustrialCostProductionPlanGroupLockedStartConflict,
    onSuccess: () => {
      enqueueSnackbar('Conflicto de inicio fijado resuelto.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context', plannedDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-line-queue'],
        }),
      ]);
    },
  });

  const unplanTrackingPlanItemMutation = useMutation({
    mutationFn: async (item: ProductionPlanItem) => {
      await deleteIndustrialCostProductionPlanItem(item.id);

      return item;
    },
    onMutate: async (item) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];
      const lineQueueQueryKey = ['industrial-cost-production-planning-line-queue'];

      await Promise.all([
        queryClient.cancelQueries({ queryKey: contextQueryKey }),
        queryClient.cancelQueries({ queryKey: weekContextQueryKey }),
        queryClient.cancelQueries({ queryKey: lineQueueQueryKey }),
      ]);

      const previousContext =
        queryClient.getQueryData<ProductionPlanningContext>(contextQueryKey);
      const previousWeekContext =
        queryClient.getQueryData<ProductionPlanningContext>(weekContextQueryKey);
      const previousLineQueues =
        queryClient.getQueriesData<ProductionPlanItem[]>({
          queryKey: lineQueueQueryKey,
        });

      const removeDeletedItemFromContext = (
        current: ProductionPlanningContext | undefined,
      ) => {
        if (!current) return current;

        return {
          ...current,
          plan_items: current.plan_items.filter(
            (planItem) => planItem.id !== item.id,
          ),
        };
      };

      const removeDeletedItemFromLineQueue = (
        current: ProductionPlanItem[] | undefined,
      ) => {
        if (!current) return current;

        return current.filter((planItem) => planItem.id !== item.id);
      };

      queryClient.setQueryData<ProductionPlanningContext>(
        contextQueryKey,
        removeDeletedItemFromContext,
      );
      queryClient.setQueryData<ProductionPlanningContext>(
        weekContextQueryKey,
        removeDeletedItemFromContext,
      );
      queryClient.setQueriesData<ProductionPlanItem[]>(
        { queryKey: lineQueueQueryKey },
        removeDeletedItemFromLineQueue,
      );

      return {
        previousContext,
        previousWeekContext,
        previousLineQueues,
      };
    },
    onSuccess: () => {
      enqueueSnackbar('OF devuelta al panel de pendientes.', {
        variant: 'success',
      });
    },
    onError: (error, _variables, context) => {
      const contextQueryKey = ['industrial-cost-production-planning-context', plannedDate];
      const weekContextQueryKey = [
        'industrial-cost-production-planning-week-context',
        weekStartDate,
      ];

      if (context?.previousContext) {
        queryClient.setQueryData(contextQueryKey, context.previousContext);
      }

      if (context?.previousWeekContext) {
        queryClient.setQueryData(weekContextQueryKey, context.previousWeekContext);
      }

      context?.previousLineQueues?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context', plannedDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-line-queue'],
        }),
      ]);
    },
  });

  const updateTrackingPlanItemDurationMutation = useMutation({
    mutationFn: async ({
      item,
      durationMinutes,
    }: {
      item: ProductionPlanItem;
      durationMinutes: number;
    }) => {
      const plannedStartTime = normalizePlanningTime(item.planned_start_time);
      const plannedEndTime = addMinutesToTime(plannedStartTime, durationMinutes);

      return updateIndustrialCostProductionPlanItem(item.id, {
        imported_manufacturing_order_id: item.imported_manufacturing_order_id,
        production_line_config_id: item.production_line_config_id,
        planned_date: normalizeIsoDateValue(item.planned_date),
        planned_start_time: plannedStartTime,
        planned_end_time: plannedEndTime,
        planned_quantity_value: Number(item.planned_quantity_value || 0),
        estimated_duration_minutes: durationMinutes,
        status: item.status,
        priority: Number(item.priority || 0),
        line_sequence_order: item.line_sequence_order ?? null,
        is_start_locked: item.is_start_locked,
        locked_start_date: item.locked_start_date
          ? normalizeIsoDateValue(item.locked_start_date)
          : null,
        locked_start_time: item.locked_start_time
          ? normalizePlanningTime(item.locked_start_time)
          : null,
        locked_start_conflict_policy: item.locked_start_conflict_policy,
        notes: item.notes,
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Duración de OF actualizada.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context', plannedDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context', weekStartDate],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-line-queue'],
        }),
      ]);
    },
  });

  const isPlanningWriteInProgress =
    upsertLineDaySettingsMutation.isPending ||
    assignPendingGroupToLineMutation.isPending ||
    movePlannedGroupToLineMutation.isPending ||
    unplanPlannedGroupMutation.isPending ||
    reorderPlannedGroupsWithinLineMutation.isPending ||
    updatePlannedGroupScheduleAdjustmentMutation.isPending ||
    moveTrackingGroupToLockedStartMutation.isPending ||
    resolveLockedStartConflictMutation.isPending ||
    unplanTrackingPlanItemMutation.isPending ||
    updateTrackingPlanItemDurationMutation.isPending;

  const hasTransferableActiveLockedStart = (
    candidate: (typeof selectedLineVisibleTrackingCards)[number] | null | undefined,
  ) =>
    Boolean(
      candidate &&
        !candidate.isExpiredAdjustment &&
        candidate.group.items.some(
          (item) =>
            item.is_start_locked &&
            item.locked_start_date &&
            item.locked_start_time,
        ),
    );

  const resolveTrackingGroupLockedStartSource = (
    group: PlannedOrderGroup,
  ) => {
    const groupIndex = selectedLineVisibleTrackingCards.findIndex(
      (item) => item.group.key === group.key,
    );

    if (groupIndex < 0) return null;

    const immediateNextCandidate = selectedLineVisibleTrackingCards[groupIndex + 1] ?? null;

    if (
      immediateNextCandidate?.segment.hasConflict &&
      hasTransferableActiveLockedStart(immediateNextCandidate)
    ) {
      return immediateNextCandidate;
    }

    return null;
  };

  const resolveOwnLockedStartConflictBlockingGroup = (
    group: PlannedOrderGroup,
  ) => {
    const groupIndex = selectedLineVisibleTrackingCards.findIndex(
      (item) => item.group.key === group.key,
    );

    if (groupIndex <= 0) return null;

    const ownCard = selectedLineVisibleTrackingCards[groupIndex];
    const immediatePreviousCandidate =
      selectedLineVisibleTrackingCards[groupIndex - 1] ?? null;

    if (
      ownCard?.segment.hasConflict &&
      hasTransferableActiveLockedStart(ownCard) &&
      immediatePreviousCandidate
    ) {
      return immediatePreviousCandidate;
    }

    return null;
  };

  const handleMoveTrackingGroupToLockedStart = (
    group: PlannedOrderGroup,
  ) => {
    if (!selectedLine || moveTrackingGroupToLockedStartMutation.isPending) {
      return;
    }

    const source = resolveTrackingGroupLockedStartSource(group);
    if (!source) {
      enqueueSnackbar('No hay un inicio fijado anterior al que mover esta referencia.', {
        variant: 'warning',
      });
      return;
    }

    const sourceLockedItem = source.group.items.find(
      (item) =>
        item.is_start_locked &&
        item.locked_start_date &&
        item.locked_start_time,
    );
    const sourceLockedDate = normalizeIsoDateValue(
      sourceLockedItem?.locked_start_date,
    );
    const sourceLockedTime = sourceLockedItem?.locked_start_time
      ? normalizePlanningTime(sourceLockedItem.locked_start_time)
      : '';

    setTrackingConfirmDialogRequest({
      title: 'Mantener este inicio fijado',
      description: `Se transferirá el inicio fijado de ${source.group.referenceLabel} (${formatShortWeekDay(
        sourceLockedDate,
      )} ${sourceLockedTime}) a ${group.referenceLabel}. ${source.group.referenceLabel} quedará después sin ajuste manual.`,
      confirmLabel: 'Mantener este inicio',
      confirmColor: 'info',
      onConfirm: () => {
        resolveLockedStartConflictMutation.mutate({
          production_line_config_id: selectedLine.id,
          locked_plan_item_ids: source.group.items.map((item) => item.id),
          blocking_plan_item_ids: group.items.map((item) => item.id),
          resolution: 'keep_blocking_group_first',
          locked_start_note: `Inicio mantenido en ${group.referenceLabel} al resolver conflicto con ${source.group.referenceLabel}.`,
        });
      },
    });
  };

  const handlePrioritizeOwnLockedStart = (
    group: PlannedOrderGroup,
  ) => {
    if (!selectedLine || resolveLockedStartConflictMutation.isPending) {
      return;
    }

    const blockingGroup = resolveOwnLockedStartConflictBlockingGroup(group);
    if (!blockingGroup) {
      enqueueSnackbar('No hay un bloque previo inmediato que bloquee este inicio fijado.', {
        variant: 'warning',
      });
      return;
    }

    const lockedItem = group.items.find(
      (item) =>
        item.is_start_locked &&
        item.locked_start_date &&
        item.locked_start_time,
    );
    const lockedDate = normalizeIsoDateValue(lockedItem?.locked_start_date);
    const lockedTime = lockedItem?.locked_start_time
      ? normalizePlanningTime(lockedItem.locked_start_time)
      : '';

    setTrackingConfirmDialogRequest({
      title: 'Priorizar inicio fijado',
      description: `Se moverá ${group.referenceLabel} antes de ${blockingGroup.group.referenceLabel} y conservará su inicio fijado (${formatShortWeekDay(
        lockedDate,
      )} ${lockedTime}). ${blockingGroup.group.referenceLabel} quedará después en la cola.`,
      confirmLabel: 'Priorizar inicio',
      confirmColor: 'warning',
      onConfirm: () => {
        resolveLockedStartConflictMutation.mutate({
          production_line_config_id: selectedLine.id,
          locked_plan_item_ids: group.items.map((item) => item.id),
          blocking_plan_item_ids: blockingGroup.group.items.map((item) => item.id),
          resolution: 'prioritize_locked_group',
          locked_start_note: `Inicio priorizado para ${group.referenceLabel} al resolver conflicto con ${blockingGroup.group.referenceLabel}.`,
        });
      },
    });
  };

  const handleOpenPlannedGroupScheduleAdjustmentDialog = (
    line: ProductionLineConfig,
    group: PlannedOrderGroup,
    suggestedStartDate: string,
    suggestedStartTime: string,
  ) => {
    const lockedItem = group.items.find((item) => item.is_start_locked);

    setPlannedGroupLockedStartDate(
      normalizeIsoDateValue(lockedItem?.locked_start_date) ||
        normalizeIsoDateValue(suggestedStartDate) ||
        plannedDate,
    );
    setPlannedGroupLockedStartTime(
      lockedItem?.locked_start_time
        ? normalizePlanningTime(lockedItem.locked_start_time)
        : normalizePlanningTime(suggestedStartTime, defaultPlanningStartTime),
    );
    setPlannedGroupLockedStartNote(lockedItem?.locked_start_note ?? '');
    setPlannedGroupScheduleAdjustmentRequest({
      line,
      group,
    });
  };

  const handleClosePlannedGroupScheduleAdjustmentDialog = () => {
    if (updatePlannedGroupScheduleAdjustmentMutation.isPending) return;

    setPlannedGroupScheduleAdjustmentRequest(null);
  };

  const handleSavePlannedGroupScheduleAdjustment = () => {
    if (
      !plannedGroupScheduleAdjustmentRequest ||
      updatePlannedGroupScheduleAdjustmentMutation.isPending
    ) {
      return;
    }

    const lockedStartDate = normalizeIsoDateValue(plannedGroupLockedStartDate);
    const lockedStartTime = plannedGroupLockedStartTime.trim();
    const [hoursRaw, minutesRaw] = lockedStartTime.split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);

    if (
      !lockedStartDate ||
      !/^\d{2}:\d{2}$/.test(lockedStartTime) ||
      !Number.isInteger(hours) ||
      !Number.isInteger(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      enqueueSnackbar('Debes indicar una fecha y una hora válidas.', {
        variant: 'warning',
      });
      return;
    }

    updatePlannedGroupScheduleAdjustmentMutation.mutate({
      production_line_config_id: plannedGroupScheduleAdjustmentRequest.line.id,
      plan_item_ids: plannedGroupScheduleAdjustmentRequest.group.items.map(
        (item) => item.id,
      ),
      is_start_locked: true,
      locked_start_date: lockedStartDate,
      locked_start_time: lockedStartTime,
      locked_start_note: plannedGroupLockedStartNote.trim() || null,
    });
  };

  const handleClearPlannedGroupScheduleAdjustment = () => {
    if (
      !plannedGroupScheduleAdjustmentRequest ||
      updatePlannedGroupScheduleAdjustmentMutation.isPending
    ) {
      return;
    }

    updatePlannedGroupScheduleAdjustmentMutation.mutate({
      production_line_config_id: plannedGroupScheduleAdjustmentRequest.line.id,
      plan_item_ids: plannedGroupScheduleAdjustmentRequest.group.items.map(
        (item) => item.id,
      ),
      is_start_locked: false,
    });
  };

  const handleSaveInlinePlannedGroupScheduleAdjustment = ({
    group,
    lockedStartDate,
    lockedStartTime,
    lockedStartNote,
  }: {
    group: PlannedOrderGroup;
    lockedStartDate: string;
    lockedStartTime: string;
    lockedStartNote: string;
  }) => {
    if (
      !selectedLine ||
      updatePlannedGroupScheduleAdjustmentMutation.isPending
    ) {
      return;
    }

    const normalizedLockedStartDate =
      normalizeIsoDateValue(lockedStartDate);
    const normalizedLockedStartTime = lockedStartTime.trim();
    const [hoursRaw, minutesRaw] = normalizedLockedStartTime.split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);

    if (
      !normalizedLockedStartDate ||
      !/^\d{2}:\d{2}$/.test(normalizedLockedStartTime) ||
      !Number.isInteger(hours) ||
      !Number.isInteger(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      enqueueSnackbar('Debes indicar una fecha y una hora válidas.', {
        variant: 'warning',
      });
      return;
    }

    updatePlannedGroupScheduleAdjustmentMutation.mutate({
      production_line_config_id: selectedLine.id,
      plan_item_ids: group.items.map((item) => item.id),
      is_start_locked: true,
      locked_start_date: normalizedLockedStartDate,
      locked_start_time: normalizedLockedStartTime,
      locked_start_note: lockedStartNote.trim() || null,
    });
  };

  const handleClearInlinePlannedGroupScheduleAdjustment = (
    group: PlannedOrderGroup,
  ) => {
    if (
      !selectedLine ||
      updatePlannedGroupScheduleAdjustmentMutation.isPending
    ) {
      return;
    }

    updatePlannedGroupScheduleAdjustmentMutation.mutate({
      production_line_config_id: selectedLine.id,
      plan_item_ids: group.items.map((item) => item.id),
      is_start_locked: false,
    });
  };

  const buildReorderedPlanItemsFromGroups = (groups: PlannedOrderGroup[]) => {
    let nextSequenceOrder = 1;

    return groups.flatMap((group) =>
      group.items.map((item) => {
        const currentSequenceOrder = nextSequenceOrder;

        const nextItem = {
          ...item,
          priority: currentSequenceOrder,
          line_sequence_order: currentSequenceOrder,
        };

        nextSequenceOrder += 1;

        return nextItem;
      }),
    );
  };

  const buildReorderedItemsForPlannedGroupDrop = ({
    line,
    movingGroupKey,
    targetGroupKey,
    position,
  }: {
    line: ProductionLineConfig;
    movingGroupKey: string;
    targetGroupKey: string;
    position: 'before' | 'after';
  }) => {
    const lineItems = planItemsByLineId.get(line.id) ?? [];
    const currentGroups = buildPlannedOrderGroups(lineItems);
    const movingGroupIndex = currentGroups.findIndex(
      (group) => group.key === movingGroupKey,
    );
    const targetGroupIndex = currentGroups.findIndex(
      (group) => group.key === targetGroupKey,
    );

    if (
      movingGroupIndex === -1 ||
      targetGroupIndex === -1 ||
      movingGroupKey === targetGroupKey
    ) {
      return null;
    }

    const reorderedGroups = [...currentGroups];
    const [movingGroup] = reorderedGroups.splice(movingGroupIndex, 1);

    const targetIndexAfterRemoval = reorderedGroups.findIndex(
      (group) => group.key === targetGroupKey,
    );

    if (targetIndexAfterRemoval === -1) return null;

    const insertIndex =
      position === 'before'
        ? targetIndexAfterRemoval
        : targetIndexAfterRemoval + 1;

    reorderedGroups.splice(insertIndex, 0, movingGroup);

    return buildReorderedPlanItemsFromGroups(reorderedGroups);
  };

  const handlePlannedGroupCardDragOver = (
    event: DragEvent<HTMLElement>,
    line: ProductionLineConfig,
    targetGroup: PlannedOrderGroup,
  ) => {
    if (isPlanningWriteInProgress || !draggedPlannedGroupKey) return;

    if (draggedPlannedGroupKey === targetGroup.key) {
      setPlannedGroupDropPreview(null);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';

    const rect = event.currentTarget.getBoundingClientRect();
    const position: PlannedGroupDropPreview['position'] =
      event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';

    setPlannedGroupDropPreview((current) => {
      const nextPreview = {
        lineId: line.id,
        targetGroupKey: targetGroup.key,
        position,
      };

      if (
        current?.lineId === nextPreview.lineId &&
        current?.targetGroupKey === nextPreview.targetGroupKey &&
        current?.position === nextPreview.position
      ) {
        return current;
      }

      return nextPreview;
    });
  };

  const handlePlannedGroupDropZoneDragOver = (
    event: DragEvent<HTMLElement>,
    line: ProductionLineConfig,
    targetGroup: PlannedOrderGroup,
    position: PlannedGroupDropPreview['position'],
  ) => {
    if (
      isPlanningWriteInProgress ||
      !draggedPlannedGroupKey ||
      draggedPlannedGroupKey === targetGroup.key
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';

    setPlannedGroupDropPreview((current) => {
      const nextPreview = {
        lineId: line.id,
        targetGroupKey: targetGroup.key,
        position,
      };

      if (
        current?.lineId === nextPreview.lineId &&
        current?.targetGroupKey === nextPreview.targetGroupKey &&
        current?.position === nextPreview.position
      ) {
        return current;
      }

      return nextPreview;
    });
  };

  const handlePlannedGroupCardDrop = (
    event: DragEvent<HTMLElement>,
    line: ProductionLineConfig,
    targetGroup: PlannedOrderGroup,
    forcedPosition?: PlannedGroupDropPreview['position'],
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (isPlanningWriteInProgress) {
      setPlannedGroupDropPreview(null);
      setDraggedPlannedGroupKey(null);
      return;
    }

    const droppedGroupKey =
      event.dataTransfer.getData('text/plain') || draggedPlannedGroupKey;

    if (!droppedGroupKey || droppedGroupKey === targetGroup.key) {
      setPlannedGroupDropPreview(null);
      setDraggedPlannedGroupKey(null);
      return;
    }

    const activePreview =
      forcedPosition
        ? {
            lineId: line.id,
            targetGroupKey: targetGroup.key,
            position: forcedPosition,
          }
        : plannedGroupDropPreview?.lineId === line.id &&
            plannedGroupDropPreview.targetGroupKey === targetGroup.key
          ? plannedGroupDropPreview
          : {
              lineId: line.id,
              targetGroupKey: targetGroup.key,
              position: 'before' as const,
            };

    const sourceGroup = weeklyPlannedGroupsByKey.get(droppedGroupKey);
    const sourceAlreadyInLine = sourceGroup?.items.every(
      (item) => item.production_line_config_id === line.id,
    );

    if (!sourceAlreadyInLine) {
      setPlannedGroupDropPreview(null);
      handleDropPlannedGroupOnLine(droppedGroupKey, line);
      return;
    }

    const reorderedItems = buildReorderedItemsForPlannedGroupDrop({
      line,
      movingGroupKey: droppedGroupKey,
      targetGroupKey: activePreview.targetGroupKey,
      position: activePreview.position,
    });

    if (!reorderedItems) {
      setPlannedGroupDropPreview(null);
      setDraggedPlannedGroupKey(null);
      return;
    }

    setPlannedGroupDropPreview(null);

    reorderPlannedGroupsWithinLineMutation.mutate({
      line,
      reorderedItems,
    });
  };

  const handleMovePlannedGroupWithinLine = (
    line: ProductionLineConfig,
    groupKey: string,
    direction: 'up' | 'down',
  ) => {
    if (isPlanningWriteInProgress) {
      return;
    }

    const lineItems = planItemsByLineId.get(line.id) ?? [];
    const currentGroups = buildPlannedOrderGroups(lineItems);
    const currentIndex = currentGroups.findIndex((group) => group.key === groupKey);

    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= currentGroups.length) return;

    const reorderedGroups = [...currentGroups];
    const [movedGroup] = reorderedGroups.splice(currentIndex, 1);
    reorderedGroups.splice(targetIndex, 0, movedGroup);

    const reorderedItems = buildReorderedPlanItemsFromGroups(reorderedGroups);

    reorderPlannedGroupsWithinLineMutation.mutate({
      line,
      reorderedItems,
    });
  };
  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid rgba(148,163,184,0.16)',
          bgcolor: 'rgba(15,23,42,0.66)',
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.75, md: 2 },
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            justifyContent="space-between"
          >
            <Stack spacing={0.35} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonthRoundedIcon sx={{ color: '#38bdf8', fontSize: '1.25rem' }} />
                <Typography sx={{ color: '#f8fafc', fontSize: '1.25rem', fontWeight: 950 }}>
                  Planificador de producción
                </Typography>
              </Stack>

              <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem', maxWidth: 920 }}>
                Programa máquinas arrastrando conjuntos pendientes a cada línea. La cola de cada
                línea define el orden real; la semana proyecta esa planificación sobre el calendario.
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button
                size="small"
                variant={pendingPanelOpen ? 'contained' : 'outlined'}
                onClick={() => setPendingPanelOpen((current) => !current)}
              >
                Pendientes ({pendingOrderGroups.length})
              </Button>
            </Stack>
          </Stack>

          {planningContextQuery.isLoading && <LinearProgress />}

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<FactoryRoundedIcon />}
              label={`${lines.length} líneas`}
              variant="outlined"
              sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.18)' }}
            />
            <Chip
              icon={<Inventory2RoundedIcon />}
              label={`${pendingOrderGroups.length} conjuntos pendientes`}
              variant="outlined"
              sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.18)' }}
            />
            <Chip
              label={`${pendingOrders.length} OFs sin planificar`}
              variant="outlined"
              sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.18)' }}
            />
            <Chip
              label={`${planItems.length} asignaciones`}
              variant="outlined"
              sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.18)' }}
            />
            <Chip
              label={`${formatDurationMinutes(totalPlannedMinutes)} planificadas`}
              variant="outlined"
              sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.18)' }}
            />
            <Chip
              label={`Capacidad base ${baseDailyCapacityLabel}`}
              variant="outlined"
              sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.18)' }}
            />
            {pendingGroupSearch.trim() && (
              <Chip
                label={`${filteredPendingOrderGroups.length} encontrados`}
                variant="outlined"
                sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.18)' }}
              />
            )}
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <FilterAltRoundedIcon sx={{ color: '#38bdf8', fontSize: '1.05rem' }} />
              <Typography sx={{ color: '#cbd5e1', fontWeight: 850, fontSize: '0.86rem' }}>
                Grupo
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant={selectedProcessGroup === 'all' ? 'contained' : 'outlined'}
                onClick={() => setSelectedProcessGroup('all')}
              >
                Todos
              </Button>

              {lineProcessGroups.map((group) => (
                <Button
                  key={group.key}
                  size="small"
                  variant={selectedProcessGroup === group.key ? 'contained' : 'outlined'}
                  onClick={() => setSelectedProcessGroup(group.key)}
                >
                  {group.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {planningContextQuery.isError && (
        <Alert severity="error" variant="outlined">
          No se pudo cargar el contexto del planificador.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: pendingPanelOpen ? 'minmax(0, 1fr) 320px' : 'minmax(0, 1fr)',
            xl: pendingPanelOpen ? 'minmax(0, 1fr) 360px' : 'minmax(0, 1fr)',
          },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {planningViewMode === 'line' ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(56,189,248,0.22)',
              bgcolor: 'rgba(2,6,23,0.62)',
              p: { xs: 2, md: 2.5 },
              minHeight: 520,
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', lg: 'center' }}
                justifyContent="space-between"
              >
                <Stack spacing={0.35}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.08rem' }}>
                    Editor temporal de línea
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem', maxWidth: 900 }}>
                    Organiza una máquina sobre una línea de tiempo continua. La fecha de inicio
                    solo marca el punto de entrada; la cola puede extenderse hacia pasado o futuro.
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={selectedLine ? buildLineLabel(selectedLine) : 'Sin línea'}
                    variant="outlined"
                    sx={{
                      color: '#bae6fd',
                      borderColor: 'rgba(56,189,248,0.28)',
                      bgcolor: 'rgba(14,165,233,0.08)',
                      fontWeight: 900,
                    }}
                  />
                  <Chip
                    label={`${selectedLineQueueGroups.length} referencias/conjuntos`}
                    variant="outlined"
                    sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.18)' }}
                  />
                  <Chip
                    label={`${formatDurationMinutes(selectedLineQueueTotalMinutes)} de producción`}
                    variant="outlined"
                    sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.18)' }}
                  />
                  <Chip
                    label={
                      selectedLineProjectedPlan?.projectedEndDate
                        ? `Fin ${formatShortWeekDay(selectedLineProjectedPlan.projectedEndDate)} ${formatTime(selectedLineProjectedPlan.projectedEndTime)}`
                        : 'Sin fin previsto'
                    }
                    variant="outlined"
                    sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.18)' }}
                  />
                  {Boolean(selectedLineProjectedPlan?.totalGapMinutes) && (
                    <Chip
                      label={`Huecos ${formatDurationMinutes(selectedLineProjectedPlan?.totalGapMinutes)}`}
                      variant="outlined"
                      sx={{
                        color: '#fde68a',
                        borderColor: 'rgba(251,191,36,0.34)',
                        bgcolor: 'rgba(251,191,36,0.08)',
                      }}
                    />
                  )}
                  {Boolean(selectedLineProjectedPlan?.conflictsCount) && (
                    <Chip
                      label={`${selectedLineProjectedPlan?.conflictsCount} conflicto${selectedLineProjectedPlan?.conflictsCount === 1 ? '' : 's'}`}
                      color="error"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Stack>

              <Stack spacing={1}>
                <Typography
                  sx={{
                    color: '#38bdf8',
                    fontWeight: 950,
                    fontSize: '0.78rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  Máquina / línea de trabajo
                </Typography>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {lines.map((line) => (
                    <Button
                      key={line.id}
                      size="small"
                      variant={selectedLine?.id === line.id ? 'contained' : 'outlined'}
                      onClick={() => setSelectedLineId(line.id)}
                    >
                      {buildLineLabel(line)}
                    </Button>
                  ))}
                </Stack>
              </Stack>

              {lineQueueQuery.isLoading ? (
                <LinearProgress />
              ) : lineQueueQuery.isError ? (
                <Alert severity="error" variant="outlined">
                  No se pudo cargar la cola continua de la línea seleccionada.
                </Alert>
              ) : !selectedLine ? (
                <Alert severity="info" variant="outlined">
                  No hay líneas activas para planificar.
                </Alert>
              ) : !selectedLineProjectedPlan || selectedLineProjectedPlan.segments.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  Esta línea todavía no tiene referencias en cola.
                </Alert>
              ) : (
                <ProjectedLineTimelineBoard
                  projectedLinePlan={selectedLineProjectedPlan}
                  onEditGroup={(groupKey, suggestedStartDate, suggestedStartTime) => {
                    const group = selectedLineQueueGroups.find(
                      (item) => item.key === groupKey,
                    );

                    if (!group) {
                      enqueueSnackbar(
                        'No se pudo localizar el bloque planificado seleccionado.',
                        { variant: 'warning' },
                      );
                      return;
                    }

                    handleOpenPlannedGroupScheduleAdjustmentDialog(
                      selectedLine,
                      group,
                      suggestedStartDate,
                      suggestedStartTime,
                    );
                  }}
                />
              )}
            </Stack>
          </Paper>
        ) : planningViewMode === 'week' ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.18)',
              bgcolor: 'rgba(2,6,23,0.55)',
              p: { xs: 2, md: 2.5 },
              minHeight: 520,
              overflow: 'hidden',
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
              >
                <Stack spacing={0.35}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.05rem' }}>
                    Pizarra semanal de producción
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                    La semana muestra la proyección viva de las colas pendientes. Las líneas son
                    columnas y las referencias se posicionan según su previsión actual.
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    <Button
                      size="small"
                      variant="outlined"
                      aria-label="Semana anterior"
                      onClick={() =>
                        setPlannedDate(addDaysToIsoDate(weekStartDate, -7))
                      }
                      sx={{
                        minWidth: 34,
                        px: 0.8,
                        fontWeight: 950,
                      }}
                    >
                      ←
                    </Button>

                    <Button
                      size="small"
                      variant={
                        weekStartDate ===
                        getWeekStartIsoDate(getTodayLocalIsoDate())
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => setPlannedDate(getTodayLocalIsoDate())}
                      sx={{
                        minWidth: 0,
                        px: 1,
                        fontWeight: 900,
                      }}
                    >
                      Esta semana
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      aria-label="Semana siguiente"
                      onClick={() =>
                        setPlannedDate(addDaysToIsoDate(weekStartDate, 7))
                      }
                      sx={{
                        minWidth: 34,
                        px: 0.8,
                        fontWeight: 950,
                      }}
                    >
                      →
                    </Button>

                    <Chip
                      label={`Semana ${formatShortWeekDay(
                        weekDates[0],
                      )} – ${formatShortWeekDay(weekDates[6])}`}
                      variant="outlined"
                      sx={{
                        color: '#cbd5e1',
                        borderColor: 'rgba(148,163,184,0.18)',
                        fontWeight: 850,
                      }}
                    />
                  </Stack>

                  <Box sx={{ width: 220 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 800 }}>
                        Escala temporal
                      </Typography>
                      <Typography sx={{ color: '#38bdf8', fontSize: '0.7rem', fontWeight: 850 }}>
                        {weeklyTimelineScaleConfig.label}
                      </Typography>
                    </Stack>

                    <Slider
                      size="small"
                      min={0}
                      max={100}
                      step={1}
                      value={weeklyTimelineScale}
                      onChange={(_event, value) => {
                        setWeeklyTimelineScale(Array.isArray(value) ? value[0] : value);
                      }}
                    />
                  </Box>
                </Stack>
              </Stack>

              {planningWeekContextQuery.isLoading ? (
                <LinearProgress />
              ) : planningWeekContextQuery.isError ? (
                <Alert severity="error" variant="outlined">
                  No se pudo cargar la pizarra semanal del planificador.
                </Alert>
              ) : (
                <WeeklyProductionTimelineBoard
                  weekDates={weekDates}
                  lines={(planningWeekContextQuery.data?.lines ?? lines)
                    .filter((line) =>
                      selectedProcessGroup === 'all'
                        ? line.is_active
                        : line.is_active &&
                          buildProcessGroupKey(line.process_group) === selectedProcessGroup,
                    )
                    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))}
                  planItems={planningWeekContextQuery.data?.plan_items ?? []}
                  pixelsPerHour={weeklyTimelineScaleConfig.pixelsPerHour}
                  markerStepHours={weeklyTimelineScaleConfig.markerStepHours}
                  lineColumnWidth={weeklyTimelineScaleConfig.lineColumnWidth}
                  draggedPendingGroupKey={draggedPendingGroupKey}
                  draggedPlannedGroupKey={draggedPlannedGroupKey}
                  pendingPanelOpen={pendingPanelOpen}
                  pendingOrderGroupsCount={pendingOrderGroups.length}
                  onRequestPendingDrop={handleRequestWeeklyPendingDrop}
                  onRequestPlannedGroupDrop={handleRequestWeeklyPlannedGroupDrop}
                  onPlannedGroupDragStart={setDraggedPlannedGroupKey}
                  onPlannedGroupDragEnd={() => setDraggedPlannedGroupKey(null)}
                  onOpenLineTracking={handleOpenLineTracking}
                  onTogglePendingPanel={() =>
                    setPendingPanelOpen((current) => !current)
                  }
                />
              )}
            </Stack>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(2,6,23,0.55)',
            p: { xs: 2, md: 2.5 },
            minWidth: 0,
          }}
        >
          <Stack spacing={2.5}>
            <Stack spacing={0.35}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ViewTimelineRoundedIcon sx={{ color: '#38bdf8' }} />
                <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.05rem' }}>
                  Programación por líneas
                </Typography>
              </Stack>

              <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                Las líneas se agrupan por proceso para que la planificación refleje mejor la
                estructura real de fábrica. En el siguiente paso convertiremos los conjuntos en
                tarjetas movibles.
              </Typography>
            </Stack>

            {visibleLineProcessGroups.length === 0 ? (
              <Alert severity="info" variant="outlined">
                No hay líneas activas para el filtro seleccionado.
              </Alert>
            ) : (
              <Stack spacing={2.25}>
                {visibleLineProcessGroups.map((group) => (
                  <Stack key={group.key} spacing={1.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        sx={{
                          color: '#38bdf8',
                          fontWeight: 950,
                          fontSize: '0.82rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                        }}
                      >
                        {group.label}
                      </Typography>

                      <Chip
                        size="small"
                        label={`${group.lines.length} ${group.lines.length === 1 ? 'línea' : 'líneas'}`}
                        variant="outlined"
                        sx={{
                          height: 22,
                          color: '#94a3b8',
                          borderColor: 'rgba(148,163,184,0.18)',
                          fontSize: '0.7rem',
                        }}
                      />
                    </Stack>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(2, minmax(0, 1fr))',
                          xl: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 1.5,
                      }}
                    >
                      {group.lines.map((line) => {
                        const lineItems = planItemsByLineId.get(line.id) ?? [];
                        const linePlannedGroups = buildPlannedOrderGroups(lineItems);
                        const lineStartTime =
                          lineDaySettingsByLineId.get(line.id) ?? defaultPlanningStartTime;

                        const projectedLinePlan = buildProjectedLinePlan({
                          line,
                          items: lineItems,
                          baseStartDate: plannedDate,
                          baseStartTime: lineStartTime,
                        });

                        const projectedContinuationSegments = weeklyContinuousSegments.filter(
                          (segment) =>
                            segment.lineId === line.id &&
                            segment.dateIso === plannedDate &&
                            segment.originalDateIso !== plannedDate,
                        );
                        const projectedContinuationMinutes = projectedContinuationSegments.reduce(
                          (sum, segment) => sum + segment.segmentMinutes,
                          0,
                        );
                        const visualPlannedMinutes =
                          projectedContinuationMinutes + projectedLinePlan.totalProductionMinutes;
                        const projectedLineEndLabel = projectedLinePlan.projectedEndDate
                          ? `${formatShortWeekDay(projectedLinePlan.projectedEndDate)} ${formatTime(
                              projectedLinePlan.projectedEndTime,
                            )}`
                          : '—';
                        const lineOverCapacityMinutes = Math.max(
                          visualPlannedMinutes - baseDailyCapacityMinutes,
                          0,
                        );

                        return (
                          <Paper
                            key={line.id}
                            elevation={0}
                            onDragOver={(event) => {
                              if (
                                !isPlanningWriteInProgress &&
                                (draggedPendingGroupKey || draggedPlannedGroupKey)
                              ) {
                                event.preventDefault();
                                event.dataTransfer.dropEffect = 'move';
                              }
                            }}
                            onDrop={(event) => {
                              event.preventDefault();
                              setPlannedGroupDropPreview(null);

                              if (isPlanningWriteInProgress) {
                                setDraggedPendingGroupKey(null);
                                setDraggedPlannedGroupKey(null);
                                return;
                              }

                              const droppedGroupKey =
                                event.dataTransfer.getData('text/plain') ||
                                draggedPendingGroupKey ||
                                draggedPlannedGroupKey;

                              if (draggedPendingGroupKey) {
                                handleDropPendingGroupOnLine(droppedGroupKey, line);
                                return;
                              }

                              if (draggedPlannedGroupKey) {
                                handleDropPlannedGroupOnLine(droppedGroupKey, line);
                              }
                            }}
                            sx={{
                              borderRadius: 3,
                              border: draggedPendingGroupKey || draggedPlannedGroupKey
                                ? '1px solid rgba(56,189,248,0.42)'
                                : '1px solid rgba(148,163,184,0.14)',
                              bgcolor: draggedPendingGroupKey || draggedPlannedGroupKey
                                ? 'rgba(8,47,73,0.3)'
                                : 'rgba(15,23,42,0.58)',
                              p: 1.5,
                              height: 430,
                              display: 'flex',
                              flexDirection: 'column',
                              overflow: 'hidden',
                              transition: 'border-color 0.18s ease, background 0.18s ease',
                            }}
                          >
                            <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
                              <Stack spacing={0.35}>
                                <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                                  {buildLineLabel(line)}
                                </Typography>

                                <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                                  {line.code}
                                  {line.process_group ? ` · ${line.process_group}` : ''}
                                </Typography>

                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  <Chip
                                    size="small"
                                    label={`${linePlannedGroups.length} conjuntos`}
                                    variant="outlined"
                                    sx={{
                                      height: 22,
                                      color: '#94a3b8',
                                      borderColor: 'rgba(148,163,184,0.16)',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                  <Chip
                                    size="small"
                                    label={`${lineItems.length} OFs`}
                                    variant="outlined"
                                    sx={{
                                      height: 22,
                                      color: '#94a3b8',
                                      borderColor: 'rgba(148,163,184,0.16)',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                  <Chip
                                    size="small"
                                    label={formatDurationMinutes(visualPlannedMinutes)}
                                    variant="outlined"
                                    sx={{
                                      height: 22,
                                      color: visualPlannedMinutes > 0 ? '#94a3b8' : '#64748b',
                                      borderColor: 'rgba(148,163,184,0.16)',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                  {projectedContinuationSegments.length > 0 && (
                                    <Chip
                                      size="small"
                                      label={`${projectedContinuationSegments.length} continuación${
                                        projectedContinuationSegments.length === 1 ? '' : 'es'
                                      }`}
                                      variant="outlined"
                                      sx={{
                                        height: 22,
                                        color: '#bae6fd',
                                        borderColor: 'rgba(56,189,248,0.22)',
                                        bgcolor: 'rgba(14,165,233,0.08)',
                                        fontSize: '0.7rem',
                                      }}
                                    />
                                  )}
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  useFlexGap
                                  sx={{ pt: 0.5, minWidth: 0 }}
                                >
                                  <TextField
                                    size="small"
                                    type="time"
                                    label="Inicio línea"
                                    value={lineStartTime}
                                    onChange={(event) =>
                                      handleChangeLineStartTime(line, event.target.value)
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                      width: 126,
                                      flexShrink: 0,
                                      '& .MuiInputBase-input': {
                                        fontWeight: 850,
                                      },
                                    }}
                                  />

                                  <Chip
                                    size="small"
                                    label={`Fin previsto ${projectedLineEndLabel}`}
                                    variant="outlined"
                                    sx={{
                                      height: 30,
                                      color:
                                        projectedLinePlan.totalProductionMinutes > 0
                                          ? '#bae6fd'
                                          : '#64748b',
                                      borderColor: 'rgba(56,189,248,0.22)',
                                      bgcolor:
                                        projectedLinePlan.totalProductionMinutes > 0
                                          ? 'rgba(14,165,233,0.08)'
                                          : 'transparent',
                                      fontSize: '0.74rem',
                                      fontWeight: 800,
                                    }}
                                  />

                                  <Chip
                                    size="small"
                                    label={`Capacidad ${baseDailyCapacityLabel}`}
                                    variant="outlined"
                                    sx={{
                                      height: 30,
                                      color: '#94a3b8',
                                      borderColor: 'rgba(148,163,184,0.18)',
                                      fontSize: '0.74rem',
                                      fontWeight: 800,
                                    }}
                                  />

                                  {lineOverCapacityMinutes > 0 && (
                                    <Chip
                                      size="small"
                                      label={`Exceso ${formatDurationMinutes(lineOverCapacityMinutes)}`}
                                      color="warning"
                                      variant="outlined"
                                      sx={{
                                        height: 30,
                                        fontSize: '0.74rem',
                                        fontWeight: 900,
                                      }}
                                    />
                                  )}

                                  {projectedLinePlan.totalGapMinutes > 0 && (
                                    <Chip
                                      size="small"
                                      label={`Huecos ${formatDurationMinutes(projectedLinePlan.totalGapMinutes)}`}
                                      variant="outlined"
                                      sx={{
                                        height: 30,
                                        color: '#fde68a',
                                        borderColor: 'rgba(251,191,36,0.34)',
                                        bgcolor: 'rgba(251,191,36,0.08)',
                                        fontSize: '0.74rem',
                                        fontWeight: 900,
                                      }}
                                    />
                                  )}

                                  {projectedLinePlan.conflictsCount > 0 && (
                                    <Chip
                                      size="small"
                                      label={`${projectedLinePlan.conflictsCount} conflicto${
                                        projectedLinePlan.conflictsCount === 1 ? '' : 's'
                                      }`}
                                      color="error"
                                      variant="outlined"
                                      sx={{
                                        height: 30,
                                        fontSize: '0.74rem',
                                        fontWeight: 900,
                                      }}
                                    />
                                  )}
                                </Stack>
                                </Stack>
                              </Stack>

                              <Stack
                                spacing={1}
                                sx={{
                                  flex: 1,
                                  minHeight: 0,
                                  overflowY: 'auto',
                                  pr: 0.5,
                                  pb: 0.5,
                                }}
                              >
                                {projectedContinuationSegments.length === 0 &&
                                linePlannedGroups.length === 0 ? (
                                  <Box
                                    sx={{
                                      borderRadius: 2.5,
                                      border: '1px dashed rgba(148,163,184,0.18)',
                                      bgcolor: 'rgba(2,6,23,0.24)',
                                      px: 1.25,
                                      py: 2.25,
                                      textAlign: 'center',
                                    }}
                                  >
                                    <Typography sx={{ color: '#64748b', fontSize: '0.84rem' }}>
                                      {draggedPendingGroupKey
                                        ? 'Suelta para asignar el conjunto a esta línea.'
                                        : draggedPlannedGroupKey
                                          ? 'Suelta para mover el conjunto a esta línea.'
                                          : 'Suelta aquí un conjunto para planificarlo en esta línea.'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <>
                                    {projectedContinuationSegments.map((segment, segmentIndex) => {
                                      const minutesBeforeSegment = projectedContinuationSegments
                                        .slice(0, segmentIndex)
                                        .reduce(
                                          (sum, previousSegment) =>
                                            sum + previousSegment.segmentMinutes,
                                          0,
                                        );
                                      const visualStartTime = addMinutesToTime(
                                        lineStartTime,
                                        minutesBeforeSegment,
                                      );
                                      const visualEndTime = addMinutesToTime(
                                        visualStartTime,
                                        segment.segmentMinutes,
                                      );

                                      return (
                                        <ProjectedContinuationCard
                                          key={`projected-continuation-${line.id}-${plannedDate}-${segment.groupKey}-${segmentIndex}`}
                                          segment={segment}
                                          visualStartTime={visualStartTime}
                                          visualEndTime={visualEndTime}
                                          onOpenOriginalDay={() => {
                                            setPlannedDate(segment.originalDateIso);
                                            setPlanningViewMode('day');
                                          }}
                                        />
                                      );
                                    })}

                                    {linePlannedGroups.map((group, groupIndex) => {
                                      const fallbackMinutesBeforeGroup =
                                        projectedContinuationMinutes +
                                        linePlannedGroups
                                          .slice(0, groupIndex)
                                          .reduce(
                                            (sum, previousGroup) =>
                                              sum + previousGroup.totalDurationMinutes,
                                            0,
                                          );
                                      const fallbackVisualStartTime = addMinutesToTime(
                                        lineStartTime,
                                        fallbackMinutesBeforeGroup,
                                      );
                                      const projectedGroupSegment =
                                        projectedLinePlan.segments.find(
                                          (segment) =>
                                            segment.type === 'production' &&
                                            segment.groupKey === group.key,
                                        );
                                      const visualStartTime =
                                        projectedGroupSegment?.startTime ??
                                        fallbackVisualStartTime;
                                      const visualEndTime =
                                        projectedGroupSegment?.endTime ??
                                        addMinutesToTime(
                                          visualStartTime,
                                          group.totalDurationMinutes,
                                        );

                                      const isDraggingAnotherPlannedGroup =
                                        Boolean(draggedPlannedGroupKey) &&
                                        draggedPlannedGroupKey !== group.key &&
                                        !isPlanningWriteInProgress;
                                      const showDropBefore =
                                        plannedGroupDropPreview?.lineId === line.id &&
                                        plannedGroupDropPreview.targetGroupKey === group.key &&
                                        plannedGroupDropPreview.position === 'before';
                                      const showDropAfter =
                                        plannedGroupDropPreview?.lineId === line.id &&
                                        plannedGroupDropPreview.targetGroupKey === group.key &&
                                        plannedGroupDropPreview.position === 'after';
                                      const isLastGroup =
                                        groupIndex === linePlannedGroups.length - 1;

                                      return (
                                        <Stack key={group.key} spacing={1}>
                                          <PlannedGroupDropPlaceholder
                                            isDragging={isDraggingAnotherPlannedGroup}
                                            isActive={showDropBefore}
                                            onDragOver={(event) =>
                                              handlePlannedGroupDropZoneDragOver(
                                                event,
                                                line,
                                                group,
                                                'before',
                                              )
                                            }
                                            onDrop={(event) =>
                                              handlePlannedGroupCardDrop(
                                                event,
                                                line,
                                                group,
                                                'before',
                                              )
                                            }
                                          />

                                          <PlannedOrderGroupCard
                                            group={group}
                                            visualStartTime={visualStartTime}
                                            visualEndTime={visualEndTime}
                                            onDragStart={setDraggedPlannedGroupKey}
                                            onDragEnd={() => {
                                              setDraggedPlannedGroupKey(null);
                                              setPlannedGroupDropPreview(null);
                                            }}
                                            onDragOver={(event) =>
                                              handlePlannedGroupCardDragOver(event, line, group)
                                            }
                                            onDrop={(event) =>
                                              handlePlannedGroupCardDrop(event, line, group)
                                            }
                                            onMoveUp={() =>
                                              handleMovePlannedGroupWithinLine(line, group.key, 'up')
                                            }
                                            onMoveDown={() =>
                                              handleMovePlannedGroupWithinLine(line, group.key, 'down')
                                            }
                                            canMoveUp={groupIndex > 0}
                                            canMoveDown={groupIndex < linePlannedGroups.length - 1}
                                            isBeingDragged={draggedPlannedGroupKey === group.key}
                                          />

                                          {isLastGroup && (
                                            <PlannedGroupDropPlaceholder
                                              isDragging={isDraggingAnotherPlannedGroup}
                                              isActive={showDropAfter}
                                              onDragOver={(event) =>
                                                handlePlannedGroupDropZoneDragOver(
                                                  event,
                                                  line,
                                                  group,
                                                  'after',
                                                )
                                              }
                                              onDrop={(event) =>
                                                handlePlannedGroupCardDrop(
                                                  event,
                                                  line,
                                                  group,
                                                  'after',
                                                )
                                              }
                                            />
                                          )}
                                        </Stack>
                                      );
                                    })}
                                  </>
                                )}
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
          </Paper>
        )}

        {pendingPanelOpen && (
        <Paper
          elevation={0}
          onDragOver={(event) => {
            if (!isPlanningWriteInProgress && draggedPlannedGroupKey) {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
            }
          }}
          onDrop={(event) => {
            event.preventDefault();

            if (isPlanningWriteInProgress) {
              setDraggedPlannedGroupKey(null);
              return;
            }

            const droppedGroupKey =
              event.dataTransfer.getData('text/plain') || draggedPlannedGroupKey;

            handleDropPlannedGroupOnPendingPanel(droppedGroupKey);
          }}
          sx={{
            borderRadius: 4,
            border: draggedPlannedGroupKey
              ? '1px solid rgba(56,189,248,0.42)'
              : '1px solid rgba(148,163,184,0.18)',
            bgcolor: draggedPlannedGroupKey
              ? 'rgba(8,47,73,0.3)'
              : 'rgba(15,23,42,0.66)',
            p: { xs: 2, md: 2.25 },
            position: { md: 'sticky' },
            top: { md: 24 },
            maxHeight: { md: 'calc(100vh - 48px)' },
            overflow: 'auto',
            transition: 'border-color 0.18s ease, background 0.18s ease',
          }}
        >
          <Stack spacing={1.5}>
            <Stack spacing={0.35}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Inventory2RoundedIcon sx={{ color: '#38bdf8', fontSize: '1.05rem' }} />
                <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                  Conjuntos pendientes
                </Typography>
              </Stack>

              <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
                {draggedPlannedGroupKey
                  ? 'Suelta aquí un conjunto planificado para devolverlo a pendientes.'
                  : 'Las OFs se agrupan por referencia para planificar por familias de producción sin perder la trazabilidad individual de cada orden.'}
              </Typography>
            </Stack>

            <TextField
              size="small"
              fullWidth
              label="Buscar referencia u OF"
              value={pendingGroupSearch}
              onChange={(event) => setPendingGroupSearch(event.target.value)}
              placeholder="Ej. 003-0006-055 u OF2H/0008387"
            />

            {pendingOrderGroups.length === 0 ? (
              <Alert severity="info" variant="outlined">
                No hay conjuntos pendientes para mostrar.
              </Alert>
            ) : filteredPendingOrderGroups.length === 0 ? (
              <Alert severity="info" variant="outlined">
                No hay conjuntos que coincidan con la búsqueda.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {visiblePendingOrderGroups.map((group) => (
                  <PendingOrderGroupCard
                    key={group.key}
                    group={group}
                    onDragStart={setDraggedPendingGroupKey}
                    onDragEnd={() => setDraggedPendingGroupKey(null)}
                  />
                ))}

                {filteredPendingOrderGroups.length > visiblePendingOrderGroups.length && (
                  <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                    Mostrando {visiblePendingOrderGroups.length} de{' '}
                    {filteredPendingOrderGroups.length} conjuntos encontrados. Usa la búsqueda para
                    acotar por referencia u OF.
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </Paper>
        )}
      </Box>
    <Dialog
      open={Boolean(weeklyPendingDropRequest)}
      onClose={handleCloseWeeklyPendingDropDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: '#020617',
        },
      }}
    >
      <DialogTitle sx={{ color: '#f8fafc', fontWeight: 950 }}>
        Planificar pedido en línea
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography sx={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
            Línea:{' '}
            <strong>{weeklyPendingDropRequest ? buildLineLabel(weeklyPendingDropRequest.line) : '—'}</strong>
          </Typography>

          <Typography sx={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
            Fecha de referencia:{' '}
            <strong>
              {weeklyPendingDropRequest
                ? formatShortWeekDay(weeklyPendingDropRequest.dateIso)
                : '—'}
            </strong>
          </Typography>

          {weeklyPendingDropRequest?.lineHasQueue ? (
            <Alert severity="info" variant="outlined">
              Esta línea ya tiene cola. Puedes añadir el pedido al final de la cola actual o
              programarlo con un inicio concreto.
            </Alert>
          ) : (
            <Alert severity="warning" variant="outlined">
              Esta línea no tiene cola todavía. El primer pedido debe crear un inicio concreto.
            </Alert>
          )}

          <TextField
            label="Hora de inicio"
            type="time"
            value={weeklyLockedStartTime}
            onChange={(event) => setWeeklyLockedStartTime(event.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleCloseWeeklyPendingDropDialog}>
          Cancelar
        </Button>

        {weeklyPendingDropRequest?.lineHasQueue && (
          <Button
            variant="outlined"
            onClick={() => handleConfirmWeeklyPendingDrop('append_to_queue')}
            disabled={assignPendingGroupToLineMutation.isPending}
          >
            Añadir a la cola en esta posición
          </Button>
        )}

        <Button
          variant="contained"
          onClick={() => handleConfirmWeeklyPendingDrop('locked_start')}
          disabled={assignPendingGroupToLineMutation.isPending}
        >
          Programar inicio concreto
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={Boolean(pendingDurationAssignmentRequest)}
      onClose={handleClosePendingDurationAssignmentDialog}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: '#020617',
        },
      }}
    >
      <DialogTitle sx={{ color: '#f8fafc', fontWeight: 950 }}>
        Indicar duración
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning" variant="outlined">
            Esta referencia no tiene duración informada. Para evitar una planificación
            de duración cero, indica la duración total antes de colocarla.
          </Alert>

          <Stack spacing={0.5}>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
              {pendingDurationAssignmentRequest?.group.referenceLabel ?? 'Referencia'}
            </Typography>

            <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
              {pendingDurationAssignmentRequest
                ? `${pendingDurationAssignmentRequest.group.orders.length} ${
                    pendingDurationAssignmentRequest.group.orders.length === 1
                      ? 'OF'
                      : 'OFs'
                  } · ${formatQuantity(
                    pendingDurationAssignmentRequest.group.totalQuantity,
                    pendingDurationAssignmentRequest.group.quantityUnit,
                  )}`
                : '—'}
            </Typography>
          </Stack>

          <TextField
            label="Duración total en minutos"
            type="number"
            value={pendingDurationMinutesInput}
            onChange={(event) => setPendingDurationMinutesInput(event.target.value)}
            size="small"
            fullWidth
            autoFocus
            inputProps={{
              min: getMinimumPendingGroupManualDurationMinutes(
                pendingDurationAssignmentRequest?.group,
              ),
              step: 1,
            }}
            helperText={`Mínimo ${getMinimumPendingGroupManualDurationMinutes(
              pendingDurationAssignmentRequest?.group,
            )} min. Si hay varias OFs, se repartirá proporcionalmente por cantidad.`}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClosePendingDurationAssignmentDialog}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirmPendingDurationAssignment}
          disabled={
            assignPendingGroupToLineMutation.isPending ||
            !Number.isFinite(Number(pendingDurationMinutesInput || 0)) ||
            Math.round(Number(pendingDurationMinutesInput || 0)) <
              getMinimumPendingGroupManualDurationMinutes(
                pendingDurationAssignmentRequest?.group,
              )
          }
        >
          Planificar con duración
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={lineTrackingDialogOpen}
      onClose={() => setLineTrackingDialogOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: '#020617',
        },
      }}
    >
      <DialogTitle sx={{ color: '#f8fafc', fontWeight: 950 }}>
        Seguimiento de línea
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack spacing={0.35}>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  fontSize: '1rem',
                }}
              >
                {selectedLine
                  ? buildLineLabel(selectedLine)
                  : 'Línea sin seleccionar'}
              </Typography>

              <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
                Referencias planificadas en la semana visible para esta línea.
                Utiliza esta vista para corregir previsiones horarias y operar
                sobre la planificación semanal.
              </Typography>
            </Stack>

            <Button
              size="small"
              variant="outlined"
              onClick={async () => {
                await planningWeekContextQuery.refetch();
                setLineTrackingReference(getWeekTrackingReference(weekStartDate));
              }}
              disabled={planningWeekContextQuery.isFetching}
              sx={{
                alignSelf: { xs: 'flex-start', md: 'center' },
                minWidth: 0,
                px: 1,
                fontSize: '0.72rem',
                fontWeight: 900,
              }}
            >
              Actualizar semana
            </Button>
          </Stack>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Proyección desde ${formatShortWeekDay(
                lineTrackingReference.dateIso,
              )} ${lineTrackingReference.time}`}
              variant="outlined"
              sx={{
                color: '#bae6fd',
                borderColor: 'rgba(56,189,248,0.28)',
                bgcolor: 'rgba(14,165,233,0.08)',
              }}
            />

            <Chip
              size="small"
              label={`Semana ${formatTrackingDayLabel(weekDates[0])} – ${formatTrackingDayLabel(
                weekDates[6],
              )}`}
              variant="outlined"
              sx={{
                color: '#bae6fd',
                borderColor: 'rgba(56,189,248,0.22)',
                bgcolor: 'rgba(14,165,233,0.06)',
              }}
            />

            <Chip
              size="small"
              label={`${selectedLineVisibleTrackingCards.length} referencias/conjuntos en semana`}
              variant="outlined"
              sx={{
                color: '#cbd5e1',
                borderColor: 'rgba(148,163,184,0.18)',
              }}
            />

            <Chip
              size="small"
              label={`${formatDurationMinutes(
                selectedLineVisibleTrackingTotalMinutes,
              )} de producción en semana`}
              variant="outlined"
              sx={{
                color: '#cbd5e1',
                borderColor: 'rgba(148,163,184,0.18)',
              }}
            />

            {selectedLineVisibleTrackingConflictsCount > 0 && (
              <Chip
                size="small"
                label={`${selectedLineVisibleTrackingConflictsCount} conflicto${
                  selectedLineVisibleTrackingConflictsCount === 1 ? '' : 's'
                }`}
                color="error"
                variant="outlined"
              />
            )}
          </Stack>

          {planningWeekContextQuery.isLoading ? (
            <LinearProgress />
          ) : planningWeekContextQuery.isError ? (
            <Alert severity="error" variant="outlined">
              No se pudo cargar la planificación semanal de la línea seleccionada.
            </Alert>
          ) : !selectedLine ? (
            <Alert severity="info" variant="outlined">
              No hay una línea seleccionada.
            </Alert>
          ) : selectedLineTrackingCards.length === 0 ? (
            <Alert severity="info" variant="outlined">
              Esta línea no tiene referencias planificadas en la semana visible.
            </Alert>
          ) : selectedLineVisibleTrackingCards.length === 0 ? (
            <Alert severity="info" variant="outlined">
              No hay referencias de esta línea dentro de la semana visible.
            </Alert>
          ) : (
            <Stack spacing={0.85}>
              {selectedLineTrackingDayGroups.map((dayGroup, dayIndex) => (
                <Accordion
                  key={`tracking-day-${dayGroup.dateIso}`}
                  defaultExpanded={dayIndex === 0}
                  disableGutters
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(56,189,248,0.18)',
                    bgcolor: 'rgba(15,23,42,0.38)',
                    color: '#e5e7eb',
                    overflow: 'hidden',
                    '&:before': {
                      display: 'none',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreRoundedIcon sx={{ color: '#38bdf8' }} />
                    }
                    sx={{
                      minHeight: 44,
                      px: 1.2,
                      '& .MuiAccordionSummary-content': {
                        my: 0.75,
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 950,
                          fontSize: '0.86rem',
                        }}
                      >
                        {formatTrackingDayLabel(dayGroup.dateIso)}
                      </Typography>

                      <Chip
                        size="small"
                        label={`${dayGroup.cards.length} referencia${
                          dayGroup.cards.length === 1 ? '' : 's'
                        }`}
                        variant="outlined"
                        sx={{
                          height: 21,
                          color: '#bae6fd',
                          borderColor: 'rgba(56,189,248,0.24)',
                          bgcolor: 'rgba(14,165,233,0.08)',
                          fontSize: '0.66rem',
                          fontWeight: 850,
                        }}
                      />

                      <Chip
                        size="small"
                        label={formatDurationMinutes(dayGroup.totalMinutes)}
                        variant="outlined"
                        sx={{
                          height: 21,
                          color: '#cbd5e1',
                          borderColor: 'rgba(148,163,184,0.18)',
                          fontSize: '0.66rem',
                        }}
                      />

                      {dayGroup.conflictsCount > 0 && (
                        <Chip
                          size="small"
                          label={`${dayGroup.conflictsCount} conflicto${
                            dayGroup.conflictsCount === 1 ? '' : 's'
                          }`}
                          color="error"
                          variant="outlined"
                          sx={{
                            height: 21,
                            fontSize: '0.66rem',
                            fontWeight: 900,
                          }}
                        />
                      )}
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                    <Stack spacing={0.75}>
                      {dayGroup.cards.map(
                        ({ group, segment, isExpiredAdjustment }) => {
                          const visibleIndex = selectedLineVisibleTrackingCards.findIndex(
                            (item) => item.group.key === group.key,
                          );

                          return (
                            <TrackingPlannedGroupCard
                              key={group.key}
                              positionLabel={
                                visibleIndex === 0
                                  ? 'Primera en semana'
                                  : visibleIndex === 1
                                    ? 'Siguiente en semana'
                                    : `Después · posición ${visibleIndex + 1}`
                              }
                              group={group}
                              segment={segment}
                              isExpiredAdjustment={isExpiredAdjustment}
                              isSaving={
                                updatePlannedGroupScheduleAdjustmentMutation.isPending
                              }
                              isUnplanning={
                                unplanPlannedGroupMutation.isPending
                              }
                              isUnplanningItem={
                                unplanTrackingPlanItemMutation.isPending
                              }
                              isUpdatingItemDuration={
                                updateTrackingPlanItemDurationMutation.isPending
                              }
                              isMovingToLockedStart={
                                moveTrackingGroupToLockedStartMutation.isPending
                              }
                              isResolvingLockedStartConflict={
                                resolveLockedStartConflictMutation.isPending
                              }
                              canMoveToLockedStart={Boolean(
                                resolveTrackingGroupLockedStartSource(group),
                              )}
                              canPrioritizeOwnLockedStart={Boolean(
                                resolveOwnLockedStartConflictBlockingGroup(group),
                              )}
                              moveToLockedStartLabel={
                                resolveTrackingGroupLockedStartSource(group)
                                  ? 'Mantener este inicio'
                                  : null
                              }
                              prioritizeOwnLockedStartLabel={
                                resolveOwnLockedStartConflictBlockingGroup(group)
                                  ? 'Priorizar inicio'
                                  : null
                              }
                              onSaveAdjustment={
                                handleSaveInlinePlannedGroupScheduleAdjustment
                              }
                              onClearAdjustment={
                                handleClearInlinePlannedGroupScheduleAdjustment
                              }
                              onUnplan={(plannedGroup) => {
                                setTrackingConfirmDialogRequest({
                                  title: 'Devolver referencia a pendientes',
                                  description: `Se devolverá ${plannedGroup.referenceLabel} al panel de pendientes. Las OFs de esta referencia dejarán de estar planificadas en esta línea.`,
                                  confirmLabel: 'Devolver a pendientes',
                                  confirmColor: 'warning',
                                  onConfirm: () => {
                                    unplanPlannedGroupMutation.mutate(plannedGroup);
                                  },
                                });
                              }}
                              onUnplanItem={(plannedGroup, planItem) => {
                                const orderNumber =
                                  planItem.manufacturing_order_number ||
                                  'OF sin número';

                                setTrackingConfirmDialogRequest({
                                  title: 'Devolver OF a pendientes',
                                  description: `Se devolverá la ${orderNumber} de la referencia ${plannedGroup.referenceLabel} al panel de pendientes. El resto de OFs de esta referencia continuará planificado.`,
                                  confirmLabel: 'Devolver OF',
                                  confirmColor: 'warning',
                                  onConfirm: () => {
                                    unplanTrackingPlanItemMutation.mutate(planItem);
                                  },
                                });
                              }}
                              onSaveItemDuration={(
                                _plannedGroup,
                                planItem,
                                durationMinutes,
                              ) => {
                                updateTrackingPlanItemDurationMutation.mutate({
                                  item: planItem,
                                  durationMinutes,
                                });
                              }}
                              onMoveToLockedStart={
                                handleMoveTrackingGroupToLockedStart
                              }
                              onPrioritizeOwnLockedStart={
                                handlePrioritizeOwnLockedStart
                              }
                            />
                          );
                        },
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={() => setLineTrackingDialogOpen(false)}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={Boolean(trackingConfirmDialogRequest)}
      onClose={() => {
        if (
          unplanPlannedGroupMutation.isPending ||
          unplanTrackingPlanItemMutation.isPending ||
          resolveLockedStartConflictMutation.isPending
        ) {
          return;
        }

        setTrackingConfirmDialogRequest(null);
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: '#1f2433',
          color: '#e5e7eb',
          minWidth: { xs: 'calc(100vw - 32px)', sm: 460 },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 950 }}>
        {trackingConfirmDialogRequest?.title ?? 'Confirmar acción'}
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ color: '#cbd5e1', fontSize: '0.92rem', lineHeight: 1.55 }}>
          {trackingConfirmDialogRequest?.description}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={() => setTrackingConfirmDialogRequest(null)}
          disabled={
            unplanPlannedGroupMutation.isPending ||
            unplanTrackingPlanItemMutation.isPending ||
            resolveLockedStartConflictMutation.isPending
          }
          sx={{ fontWeight: 850 }}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          color={trackingConfirmDialogRequest?.confirmColor ?? 'primary'}
          disabled={
            unplanPlannedGroupMutation.isPending ||
            unplanTrackingPlanItemMutation.isPending ||
            resolveLockedStartConflictMutation.isPending
          }
          onClick={() => {
            const request = trackingConfirmDialogRequest;
            setTrackingConfirmDialogRequest(null);
            request?.onConfirm();
          }}
          sx={{ fontWeight: 900 }}
        >
          {trackingConfirmDialogRequest?.confirmLabel ?? 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>


    <Dialog
      open={Boolean(plannedGroupScheduleAdjustmentRequest)}
      onClose={handleClosePlannedGroupScheduleAdjustmentDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: '#020617',
        },
      }}
    >
      <DialogTitle sx={{ color: '#f8fafc', fontWeight: 950 }}>
        Ajustar previsión horaria
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography sx={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
            Línea:{' '}
            <strong>
              {plannedGroupScheduleAdjustmentRequest
                ? buildLineLabel(plannedGroupScheduleAdjustmentRequest.line)
                : '—'}
            </strong>
          </Typography>

          <Typography sx={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
            Referencia:{' '}
            <strong>
              {plannedGroupScheduleAdjustmentRequest?.group.referenceLabel ?? '—'}
            </strong>
          </Typography>

          <Alert severity="info" variant="outlined">
            El ajuste conserva el plan original y recalcula la proyección de los
            bloques posteriores. Utilízalo para registrar un desfase operativo
            observado durante la jornada.
          </Alert>

          <TextField
            label="Fecha de inicio revisada"
            type="date"
            value={plannedGroupLockedStartDate}
            onChange={(event) => setPlannedGroupLockedStartDate(event.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Hora de inicio revisada"
            type="time"
            value={plannedGroupLockedStartTime}
            onChange={(event) => setPlannedGroupLockedStartTime(event.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Motivo o nota del desfase"
            value={plannedGroupLockedStartNote}
            onChange={(event) => setPlannedGroupLockedStartNote(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: 1000 }}
            helperText="Opcional. Máximo 1.000 caracteres."
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: 'wrap' }}>
        <Button
          onClick={handleClosePlannedGroupScheduleAdjustmentDialog}
          disabled={updatePlannedGroupScheduleAdjustmentMutation.isPending}
        >
          Cancelar
        </Button>

        {plannedGroupScheduleAdjustmentRequest?.group.items.some(
          (item) => item.is_start_locked,
        ) && (
          <Button
            variant="outlined"
            color="warning"
            onClick={handleClearPlannedGroupScheduleAdjustment}
            disabled={updatePlannedGroupScheduleAdjustmentMutation.isPending}
          >
            Retirar ajuste
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleSavePlannedGroupScheduleAdjustment}
          disabled={updatePlannedGroupScheduleAdjustmentMutation.isPending}
        >
          Guardar previsión revisada
        </Button>
      </DialogActions>
    </Dialog>
  </Stack>
  );
}
